/**
 * Character builder parts — body shapes, hat shapes, face presets.
 *
 * Each shape is hand-rolled SVG path on a 100x100 canvas. Bodies fill
 * roughly the bottom-center; hats sit above ~y=20; eyes/mouth around
 * y=45-65. Player picks one body, one hat (or "none"), one face, plus
 * a body hue and an accent hue.
 *
 * Phase E1 — added BORDERS and BACKGROUNDS registries (empty initially;
 * E3 fills them). Each entry has an `id` and a `render` function that
 * returns declarative SVG children + an optional CSS className for
 * animations. IconAvatar consumes the registries to draw the optional
 * border + background layers.
 */

import type { ReactNode } from 'react';

/**
 * Phase D — Rich-render API for Tier 4 cosmetic upgrades.
 *
 * Tier 4 bodies / hats / faces author a `richSvg(opts) => ReactNode`
 * fragment that fully replaces the primitive `<path>+decoration` render.
 * Items WITHOUT `richSvg` continue to render via the legacy single-path
 * pipeline unchanged.
 *
 * The renderer hands in the per-avatar palette-resolved colors so themed
 * richSvg fragments can tint to match the wearer (palette-prismatic,
 * accent-hue-derived shop items, etc.). Crimson-Solstice items with
 * FIXED palettes simply ignore the `primaryHue`/`accentHue` and emit
 * their own hard-coded fills.
 *
 * `uid` is a unique-per-render prefix the caller hands in. Every
 * `<defs>` id inside a richSvg fragment MUST be prefixed with `uid` so
 * 12 avatars on the post-round screen wearing the same item don't
 * ID-collide (the second avatar would otherwise reference the first
 * avatar's gradient / filter defs).
 */
export interface RichRenderOpts {
  primaryHue: number;
  accentHue: number;
  bodyHi: string;
  bodyShadow: string;
  accentFill: string;
  featureFill: string;
  bgFill: string;
  bgFillDark: string;
  /** Stable unique id to use as prefix for any <defs>/gradient ids the
   *  richSvg defines. The renderer hands this in so multiple avatars on
   *  the same page can render the same item without id collision. */
  uid: string;
}

export interface BodyShape {
  id: string;
  label: string;
  body: string;        // path filling the body silhouette
  /** Optional decorative SVG path drawn ON TOP of the body fill (after
   *  stroke). Painted with `decorationFill` if provided, else accent.
   *  Phase E3 — used by Solstice Bearer's pulsing ember. */
  decorationPath?: string;
  /** Optional CSS className applied to the wrapping <g> around the
   *  decoration path so it can animate via the cosmetic-animations
   *  section in global.css. */
  decorationClass?: string;
  /** Hard-coded fill for the decoration (overrides accent). Used by
   *  Crimson Solstice items that must render in the fixed palette
   *  regardless of player hue. */
  decorationFill?: string;
  /** Phase D — Tier 4 rich render. When present, fully replaces the
   *  primitive body+decoration render. The `body` path string above is
   *  kept as a FALLBACK only (some downstream renderers like the Pixi
   *  in-game sprite use the primitive path). */
  richSvg?: (opts: RichRenderOpts) => ReactNode;
}

export interface HatShape {
  id: string;
  label: string;
  path: string;
  accent?: string;     // optional second path for inner detail
  /** Phase E3 — optional CSS className on the accent <path> wrapper so
   *  it can animate (e.g. Crown of Coals' hue-shifting gems). */
  accentClass?: string;
  /** Phase E3 — fixed fill for accent path (overrides body-accent
   *  derivation). Used by Crimson Solstice items. */
  accentFill?: string;
  /** Phase E3 — fixed fill for the main hat path. Crimson Solstice
   *  items use this so they render in palette regardless of hue. */
  baseFill?: string;
  /** Phase D — Tier 4 rich render. When present, fully replaces the
   *  primitive path+accent render. */
  richSvg?: (opts: RichRenderOpts) => ReactNode;
}

export interface FaceShape {
  id: string;
  label: string;
  eyes: Array<{ x: number; y: number; r: number }>;
  mouth?: string;       // path for mouth
  blush?: [number, number, number]; // [leftX, y, rightX] cheek blush positions
  /** Phase E3 — optional fixed fill for eyes (overrides feature color).
   *  Crimson Solstice's Searing Mark uses crimson-glowing eyes. */
  eyeFill?: string;
  /** Phase E3 — optional CSS className on the eyes <g> for animation. */
  eyeClass?: string;
  /** Phase E3 — optional decorative path drawn over the face (e.g. the
   *  vertical gold scar on Searing Mark). */
  markPath?: string;
  markFill?: string;
  /** Phase D — Tier 4 rich render. When present, fully replaces the
   *  primitive eyes+mouth+mark render. */
  richSvg?: (opts: RichRenderOpts) => ReactNode;
}

export interface CharacterIconExt {
  set: string;
  id: number;
  hue: number;
  body: number;
  hat: number;
  face: number;
  accentHue?: number;
  paletteId?: string;
  /** Phase E1 — avatar-frame cosmetic id; resolves in BORDERS. */
  border?: string;
  /** Phase E1 — avatar-backdrop cosmetic id; resolves in BACKGROUNDS. */
  background?: string;
  /** Phase F5 — face-overlay mask cosmetic id; resolves in MASKS.
   *  Renders ON TOP of face features but UNDER the hat. */
  mask?: string;
}

export const BODY_SHAPES: BodyShape[] = [
  // 0 round blob — flagship default body.
  //
  // Phase A Tier 1 — restrained richSvg upgrade: drop-shadow ellipse,
  // 3-stop linearGradient body fill, ink outline, glassy top highlight
  // arc, subtle belly tint for grounding. ~30 lines (well under Tier 4
  // budget). Tinted via player's palette/hue via bodyHi+bodyShadow.
  { id: 'blob', label: 'Blob',
    body: 'M50 22 C 26 22, 16 46, 22 66 C 26 84, 42 92, 50 92 C 58 92, 74 84, 78 66 C 84 46, 74 22, 50 22 Z',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      const blobPath = 'M50 22 C 26 22, 16 46, 22 66 C 26 84, 42 92, 50 92 C 58 92, 74 84, 78 66 C 84 46, 74 22, 50 22 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-grad`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={bodyHi} />
              <stop offset="45%" stopColor={bodyHi} stopOpacity="0.85" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
          </defs>
          {/* Drop shadow ellipse on the ground plane */}
          <ellipse cx="50" cy="93" rx="20" ry="3.2" fill="#000" opacity="0.24" />
          {/* Body with 3-stop gradient + thin ink outline */}
          <path d={blobPath} fill={`url(#${uid}-grad)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Belly tint — grounds the silhouette */}
          <ellipse cx="50" cy="78" rx="22" ry="9" fill={bodyShadow} opacity="0.18" />
          {/* Glassy top highlight */}
          <path d="M34 28 Q 42 22 56 26" stroke="#fff" strokeWidth="2" opacity="0.18" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 1 tall — vertical capsule default body.
  //
  // Phase A Tier 1 — restrained richSvg upgrade: drop-shadow ellipse,
  // 3-stop linearGradient, ink outline, thin vertical highlight stripe
  // + shadow stripe to suggest cylindrical volume.
  { id: 'tall', label: 'Tall',
    body: 'M50 16 C 32 16, 28 32, 30 50 L 30 78 C 30 88, 70 88, 70 78 L 70 50 C 72 32, 68 16, 50 16 Z',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      const tallPath = 'M50 16 C 32 16, 28 32, 30 50 L 30 78 C 30 88, 70 88, 70 78 L 70 50 C 72 32, 68 16, 50 16 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-grad`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={bodyHi} />
              <stop offset="50%" stopColor={bodyHi} stopOpacity="0.8" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
          </defs>
          {/* Drop shadow ellipse */}
          <ellipse cx="50" cy="91" rx="20" ry="3" fill="#000" opacity="0.24" />
          {/* Body with 3-stop gradient + ink outline */}
          <path d={tallPath} fill={`url(#${uid}-grad)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Vertical highlight stripe — implies cylindrical volume */}
          <rect x="34" y="22" width="3" height="60" fill={bodyHi} opacity="0.22" rx="1.5" />
          {/* Vertical shadow stripe on the far side */}
          <rect x="63" y="22" width="3" height="60" fill={bodyShadow} opacity="0.28" rx="1.5" />
        </>
      );
    },
  },
  // 2 cat-like (with ears)
  //
  // Phase B Tier 2 — drop-shadow + 3-stop body gradient + ink outline +
  // ONE accent layer: inner-ear pink triangles + tabby stripe accents +
  // chest fur tuft + whisker hint stitches. Static (no animation).
  { id: 'cat', label: 'Cat',
    body: 'M30 28 L 34 14 L 42 26 C 47 24, 53 24, 58 26 L 66 14 L 70 28 C 80 36, 82 64, 70 80 C 60 92, 40 92, 30 80 C 18 64, 20 36, 30 28 Z',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const catPath = 'M30 28 L 34 14 L 42 26 C 47 24, 53 24, 58 26 L 66 14 L 70 28 C 80 36, 82 64, 70 80 C 60 92, 40 92, 30 80 C 18 64, 20 36, 30 28 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-fur`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={bodyHi} />
              <stop offset="55%" stopColor={bodyHi} stopOpacity="0.82" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
          </defs>
          {/* Drop shadow ellipse */}
          <ellipse cx="50" cy="92" rx="20" ry="3" fill="#000" opacity="0.24" />
          {/* Body with 3-stop fur gradient + thin ink outline */}
          <path d={catPath} fill={`url(#${uid}-fur)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Accent: tabby stripes across the back (semitransparent darker tone) */}
          <path d="M36 38 Q 50 42 64 38" stroke={bodyShadow} strokeWidth="1.2" fill="none" opacity="0.32" strokeLinecap="round" />
          <path d="M32 50 Q 50 54 68 50" stroke={bodyShadow} strokeWidth="1.4" fill="none" opacity="0.35" strokeLinecap="round" />
          <path d="M34 62 Q 50 66 66 62" stroke={bodyShadow} strokeWidth="1.2" fill="none" opacity="0.3" strokeLinecap="round" />
          {/* Inner-ear pink triangles */}
          <path d="M33 18 L 38 22 L 35 24 Z" fill={accentFill} opacity="0.8" stroke="#1a1410" strokeWidth="0.3" strokeOpacity="0.5" />
          <path d="M67 18 L 62 22 L 65 24 Z" fill={accentFill} opacity="0.8" stroke="#1a1410" strokeWidth="0.3" strokeOpacity="0.5" />
          {/* Chest fur-tuft highlight */}
          <path d="M44 60 Q 50 64 56 60 Q 50 68 44 60 Z" fill={bodyHi} opacity="0.45" />
          {/* Belly tint patch grounds the silhouette */}
          <ellipse cx="50" cy="80" rx="18" ry="8" fill={bodyHi} opacity="0.22" />
          {/* Whisker hint stitches (2 per cheek) */}
          <path d="M30 58 L 34 58.6" stroke="#1a1410" strokeWidth="0.4" opacity="0.55" strokeLinecap="round" />
          <path d="M30 62 L 34 61.4" stroke="#1a1410" strokeWidth="0.4" opacity="0.55" strokeLinecap="round" />
          <path d="M70 58 L 66 58.6" stroke="#1a1410" strokeWidth="0.4" opacity="0.55" strokeLinecap="round" />
          <path d="M70 62 L 66 61.4" stroke="#1a1410" strokeWidth="0.4" opacity="0.55" strokeLinecap="round" />
          {/* Top highlight arc */}
          <path d="M38 32 Q 50 28 62 32" stroke="#fff" strokeWidth="1.4" opacity="0.18" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 3 bear-ear
  //
  // Phase B Tier 2 — drop-shadow + 3-stop fur gradient + ink outline +
  // accent layer: inner-ear lighter fur + muzzle patch + belly patch.
  { id: 'bear', label: 'Bear',
    body: 'M28 32 C 18 24, 24 14, 32 16 C 36 18, 38 22, 38 26 C 44 22, 56 22, 62 26 C 62 22, 64 18, 68 16 C 76 14, 82 24, 72 32 C 84 44, 84 76, 70 84 C 58 92, 42 92, 30 84 C 16 76, 16 44, 28 32 Z',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      const bearPath = 'M28 32 C 18 24, 24 14, 32 16 C 36 18, 38 22, 38 26 C 44 22, 56 22, 62 26 C 62 22, 64 18, 68 16 C 76 14, 82 24, 72 32 C 84 44, 84 76, 70 84 C 58 92, 42 92, 30 84 C 16 76, 16 44, 28 32 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-fur`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={bodyHi} />
              <stop offset="55%" stopColor={bodyHi} stopOpacity="0.78" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="92" rx="22" ry="3" fill="#000" opacity="0.24" />
          {/* Body with 3-stop fur gradient + ink outline */}
          <path d={bearPath} fill={`url(#${uid}-fur)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Accent: inner-ear lighter fur ellipses */}
          <ellipse cx="30" cy="22" rx="3.4" ry="3" fill={bodyHi} opacity="0.55" />
          <ellipse cx="70" cy="22" rx="3.4" ry="3" fill={bodyHi} opacity="0.55" />
          {/* Muzzle patch */}
          <ellipse cx="50" cy="62" rx="14" ry="8" fill={bodyHi} opacity="0.35" />
          {/* Nose-bridge accent */}
          <ellipse cx="50" cy="55" rx="6" ry="3" fill={bodyShadow} opacity="0.22" />
          {/* Belly patch */}
          <ellipse cx="50" cy="78" rx="18" ry="10" fill={bodyHi} opacity="0.3" />
          {/* Soft top highlight */}
          <path d="M36 30 Q 50 26 64 30" stroke="#fff" strokeWidth="1.5" opacity="0.16" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 4 tall round
  //
  // Phase B Tier 2 — drop-shadow + 3-stop pumpkin gradient + ink outline +
  // accent layer: 4 curved pumpkin ribs + green stem cap with highlight.
  { id: 'pumpkin', label: 'Pumpkin',
    body: 'M50 18 C 22 22, 18 50, 24 70 C 28 86, 42 92, 50 92 C 58 92, 72 86, 76 70 C 82 50, 78 22, 50 18 Z M 50 18 L 50 8',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      const pumpkinPath = 'M50 18 C 22 22, 18 50, 24 70 C 28 86, 42 92, 50 92 C 58 92, 72 86, 76 70 C 82 50, 78 22, 50 18 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-rind`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={bodyHi} />
              <stop offset="55%" stopColor={bodyHi} stopOpacity="0.85" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="93" rx="24" ry="3.2" fill="#000" opacity="0.26" />
          {/* Body with 3-stop rind gradient */}
          <path d={pumpkinPath} fill={`url(#${uid}-rind)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Accent: 4 curved pumpkin ribs */}
          <path d="M30 28 Q 30 56 32 88" stroke={bodyShadow} strokeWidth="1.2" opacity="0.42" fill="none" strokeLinecap="round" />
          <path d="M42 22 Q 40 50 42 90" stroke={bodyShadow} strokeWidth="1.0" opacity="0.34" fill="none" strokeLinecap="round" />
          <path d="M58 22 Q 60 50 58 90" stroke={bodyShadow} strokeWidth="1.0" opacity="0.34" fill="none" strokeLinecap="round" />
          <path d="M70 28 Q 70 56 68 88" stroke={bodyShadow} strokeWidth="1.2" opacity="0.42" fill="none" strokeLinecap="round" />
          {/* Soft rib highlights */}
          <path d="M36 30 Q 36 56 38 86" stroke={bodyHi} strokeWidth="0.6" opacity="0.35" fill="none" strokeLinecap="round" />
          <path d="M64 30 Q 64 56 62 86" stroke={bodyHi} strokeWidth="0.6" opacity="0.35" fill="none" strokeLinecap="round" />
          {/* Stem cap (green) */}
          <rect x="46" y="6" width="8" height="12" fill="#3a5028" stroke="#1a1410" strokeWidth="0.5" rx="1" />
          {/* Stem highlight stripe */}
          <rect x="47.2" y="7" width="1.6" height="10" fill="#5a7838" rx="0.6" />
          {/* Top body highlight arc */}
          <path d="M34 28 Q 50 22 66 28" stroke="#fff" strokeWidth="1.4" opacity="0.18" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 5 ghost flowy
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop translucent body gradient,
  // inner mist radial overlay, wavy fringe shadow at the bottom, glow
  // halo stroke, slow float animation. ONE subtle animation track
  // (.body-ghost-float on the whole group).
  { id: 'ghost', label: 'Ghost',
    body: 'M50 18 C 28 18, 22 36, 22 56 L 22 86 C 22 90, 28 92, 30 88 L 36 84 C 38 90, 44 90, 46 84 L 50 86 C 52 90, 58 90, 60 84 L 66 88 C 68 92, 72 90, 72 86 L 72 56 C 72 36, 72 18, 50 18 Z',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      const ghostPath = 'M50 18 C 28 18, 22 36, 22 56 L 22 86 C 22 90, 28 92, 30 88 L 36 84 C 38 90, 44 90, 46 84 L 50 86 C 52 90, 58 90, 60 84 L 66 88 C 68 92, 72 90, 72 86 L 72 56 C 72 36, 72 18, 50 18 Z';
      const fringePath = 'M22 86 C 22 90, 28 92, 30 88 L 36 84 C 38 90, 44 90, 46 84 L 50 86 C 52 90, 58 90, 60 84 L 66 88 C 68 92, 72 90, 72 86';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-veil`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bodyHi} stopOpacity="0.92" />
              <stop offset="40%"  stopColor={bodyHi} stopOpacity="0.78" />
              <stop offset="75%"  stopColor={bodyShadow} stopOpacity="0.65" />
              <stop offset="100%" stopColor={bodyShadow} stopOpacity="0.55" />
            </linearGradient>
            <radialGradient id={`${uid}-mist`} cx="50%" cy="55%" r="42%">
              <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.55" />
              <stop offset="60%"  stopColor="#ffffff" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Drop shadow on ground */}
          <ellipse cx="50" cy="94" rx="22" ry="2.4" fill="#000" opacity="0.22" />
          <g className="body-ghost-float">
            {/* Outer glow halo stroke — ethereal aura */}
            <path d={ghostPath} fill="none" stroke={bodyHi} strokeWidth="2" opacity="0.18" />
            {/* Wavy fringe shadow — repeat the bottom fringe 2px below for depth */}
            <path d={fringePath} fill="none" stroke={bodyShadow} strokeWidth="1.4" opacity="0.3" transform="translate(0 2)" />
            {/* Body with 4-stop translucent gradient + ink outline */}
            <path d={ghostPath} fill={`url(#${uid}-veil)`} stroke="#1a1410" strokeWidth="0.55" strokeOpacity="0.7" />
            {/* Inner radial mist overlay */}
            <ellipse cx="50" cy="55" rx="18" ry="22" fill={`url(#${uid}-mist)`} />
            {/* Subtle vertical highlight stripe */}
            <path d="M40 26 Q 38 50 40 78" stroke="#fff" strokeWidth="1.4" opacity="0.16" fill="none" strokeLinecap="round" />
            {/* Tiny shoulder shadow on the far side */}
            <path d="M62 28 Q 64 52 62 76" stroke={bodyShadow} strokeWidth="1.2" opacity="0.25" fill="none" strokeLinecap="round" />
          </g>
        </>
      );
    },
  },
  // 6 robot block
  //
  // Phase B Tier 2 — drop-shadow + 3-stop metallic gradient + ink outline
  // + ONE accent layer: chest panel + 4 corner rivets + static status LED
  // + antenna stub. Static (no animation per Tier 2 budget).
  { id: 'robot', label: 'Robot',
    body: 'M28 28 L 72 28 L 76 80 C 76 88, 70 92, 64 92 L 36 92 C 30 92, 24 88, 24 80 Z',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const robotPath = 'M28 28 L 72 28 L 76 80 C 76 88, 70 92, 64 92 L 36 92 C 30 92, 24 88, 24 80 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-metal`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bodyHi} />
              <stop offset="50%"  stopColor={bodyHi} stopOpacity="0.78" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="93" rx="24" ry="3" fill="#000" opacity="0.26" />
          {/* Body with 3-stop metallic gradient + ink outline */}
          <path d={robotPath} fill={`url(#${uid}-metal)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Antenna stub */}
          <line x1="50" y1="28" x2="50" y2="22" stroke="#1a1410" strokeWidth="1.1" strokeLinecap="round" />
          <circle cx="50" cy="20.4" r="1.6" fill={bodyHi} stroke="#1a1410" strokeWidth="0.4" />
          {/* Accent: chest panel */}
          <rect x="34" y="42" width="32" height="26" fill={bodyShadow} opacity="0.55" rx="2" stroke="#1a1410" strokeWidth="0.4" />
          {/* 4 corner rivets */}
          <circle cx="37" cy="45" r="1.2" fill="#1a1410" />
          <circle cx="63" cy="45" r="1.2" fill="#1a1410" />
          <circle cx="37" cy="65" r="1.2" fill="#1a1410" />
          <circle cx="63" cy="65" r="1.2" fill="#1a1410" />
          {/* Status LED in panel center */}
          <circle cx="50" cy="55" r="2" fill={accentFill} stroke="#1a1410" strokeWidth="0.3" />
          <circle cx="49.4" cy="54.4" r="0.7" fill="#fff4c2" opacity="0.85" />
          {/* Vertical highlight band — implies cylinder volume */}
          <rect x="30" y="32" width="2.6" height="56" fill={bodyHi} opacity="0.18" rx="1.2" />
          {/* Vertical shadow band */}
          <rect x="68" y="32" width="2.6" height="56" fill={bodyShadow} opacity="0.28" rx="1.2" />
          {/* Top-edge highlight */}
          <path d="M30 30 L 70 30" stroke="#fff" strokeWidth="0.7" opacity="0.2" strokeLinecap="round" />
        </>
      );
    },
  },
  // 7 mushroom
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop cap gradient (red→cream),
  // cap spots (4 white-ish circles), cap underside shadow band, stem
  // gradient + 4 thin gill strokes, magical glow at base. ONE subtle
  // animation: glow pulse via .body-mushroom-glow on the base halo.
  { id: 'mushroom', label: 'Mushroom',
    body: 'M50 18 C 22 18, 18 50, 24 56 L 76 56 C 82 50, 78 18, 50 18 Z M 38 56 L 38 88 C 38 92, 62 92, 62 88 L 62 56 Z',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const capPath = 'M50 18 C 22 18, 18 50, 24 56 L 76 56 C 82 50, 78 18, 50 18 Z';
      const stemPath = 'M 38 56 L 38 88 C 38 92, 62 92, 62 88 L 62 56 Z';
      return (
        <>
          <defs>
            <radialGradient id={`${uid}-cap`} cx="50%" cy="40%" r="65%">
              <stop offset="0%"   stopColor="#fff8d8" />
              <stop offset="30%"  stopColor={bodyHi} />
              <stop offset="65%"  stopColor={bodyShadow} stopOpacity="0.85" />
              <stop offset="100%" stopColor={bodyShadow} />
            </radialGradient>
            <linearGradient id={`${uid}-stem`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#f5e6c4" />
              <stop offset="55%"  stopColor="#dec59a" />
              <stop offset="100%" stopColor="#a08458" />
            </linearGradient>
            <radialGradient id={`${uid}-baseGlow`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor={accentFill} stopOpacity="0.55" />
              <stop offset="100%" stopColor={accentFill} stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="93" rx="20" ry="2.6" fill="#000" opacity="0.26" />
          {/* Magical glow at base — pulses */}
          <ellipse cx="50" cy="86" rx="18" ry="5" fill={`url(#${uid}-baseGlow)`} className="body-mushroom-glow" />
          {/* Stem with gradient + ink outline */}
          <path d={stemPath} fill={`url(#${uid}-stem)`} stroke="#1a1410" strokeWidth="0.55" strokeOpacity="0.85" />
          {/* Stem gills — 4 thin vertical strokes */}
          <line x1="42" y1="60" x2="42" y2="84" stroke={bodyShadow} strokeWidth="0.4" opacity="0.45" />
          <line x1="46" y1="60" x2="46" y2="86" stroke={bodyShadow} strokeWidth="0.4" opacity="0.45" />
          <line x1="54" y1="60" x2="54" y2="86" stroke={bodyShadow} strokeWidth="0.4" opacity="0.45" />
          <line x1="58" y1="60" x2="58" y2="84" stroke={bodyShadow} strokeWidth="0.4" opacity="0.45" />
          {/* Stem highlight */}
          <rect x="40.4" y="58" width="1.4" height="28" fill="#fff" opacity="0.18" />
          {/* Cap with 4-stop gradient + ink outline */}
          <path d={capPath} fill={`url(#${uid}-cap)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Cap underside dark band where cap meets stem */}
          <path d="M24 54 Q 50 60 76 54" stroke={bodyShadow} strokeWidth="1.4" fill="none" opacity="0.6" />
          {/* Cap spots — 4 white-cream circles */}
          <circle cx="34" cy="36" r="3.4" fill="#fff8d8" stroke="#1a1410" strokeWidth="0.4" strokeOpacity="0.65" />
          <circle cx="58" cy="28" r="3" fill="#fff8d8" stroke="#1a1410" strokeWidth="0.4" strokeOpacity="0.65" />
          <circle cx="68" cy="42" r="2.4" fill="#fff8d8" stroke="#1a1410" strokeWidth="0.3" strokeOpacity="0.55" />
          <circle cx="46" cy="48" r="2" fill="#fff8d8" stroke="#1a1410" strokeWidth="0.3" strokeOpacity="0.55" />
          {/* Spot inner shadow tints */}
          <circle cx="34.6" cy="36.6" r="1.2" fill={bodyShadow} opacity="0.18" />
          <circle cx="58.6" cy="28.6" r="1" fill={bodyShadow} opacity="0.18" />
          {/* Cap top highlight arc */}
          <path d="M30 24 Q 50 18 70 24" stroke="#fff" strokeWidth="1.6" opacity="0.32" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 8 solstice-bearer-body — Crimson Solstice premium signature (E3,
  // tier 20). Flame-shaped silhouette painted in the fixed Solstice
  // palette (#4a0a0a → #ff4040). A pulsing orange ember sits at the
  // heart, animated via .solstice-ember (cf. global.css cosmetic
  // animations). decorationFill #ffaa3d = flame-orange.
  //
  // Phase D Tier 4 — richSvg flagship treatment: 4-stop fire gradient,
  // heat-haze backplate, inner flame core layer, outer glow halo,
  // 3 staggered rising sparks, fractalNoise coal texture, drop-shadow
  // filter. All palette-locked (Crimson Solstice ignores player hue).
  { id: 'solstice-bearer-body', label: 'Solstice Bearer',
    body: 'M50 8 C 44 22, 30 30, 32 48 C 26 50, 24 60, 30 70 C 26 78, 32 92, 50 92 C 68 92, 74 78, 70 70 C 76 60, 74 50, 68 48 C 70 30, 56 22, 50 8 Z',
    decorationPath: 'M50 56 m -7 0 a 7 9 0 1 0 14 0 a 7 9 0 1 0 -14 0',
    decorationClass: 'solstice-ember',
    decorationFill: '#ffaa3d',
    richSvg: ({ uid }) => {
      const flamePath = 'M50 8 C 44 22, 30 30, 32 48 C 26 50, 24 60, 30 70 C 26 78, 32 92, 50 92 C 68 92, 74 78, 70 70 C 76 60, 74 50, 68 48 C 70 30, 56 22, 50 8 Z';
      const coreFlame = 'M50 22 C 46 30, 38 38, 40 52 C 36 56, 36 62, 40 68 C 38 76, 44 84, 50 84 C 56 84, 62 76, 60 68 C 64 62, 64 56, 60 52 C 62 38, 54 30, 50 22 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-body`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#ff9038" />
              <stop offset="35%"  stopColor="#ff4040" />
              <stop offset="70%"  stopColor="#a02020" />
              <stop offset="100%" stopColor="#280404" />
            </linearGradient>
            <radialGradient id={`${uid}-haze`} cx="50%" cy="55%" r="55%">
              <stop offset="0%"   stopColor="#ff7820" stopOpacity="0.45" />
              <stop offset="60%"  stopColor="#a02410" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#ff7820" stopOpacity="0" />
            </radialGradient>
            <radialGradient id={`${uid}-emberHalo`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#ffe6a8" stopOpacity="0.85" />
              <stop offset="60%"  stopColor="#ff7820" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#ff7820" stopOpacity="0" />
            </radialGradient>
            <filter id={`${uid}-drop`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="1.4" />
              <feOffset dx="0" dy="2" result="offset" />
              <feComponentTransfer><feFuncA type="linear" slope="0.5" /></feComponentTransfer>
              <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id={`${uid}-tex`} x="0%" y="0%" width="100%" height="100%">
              <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="2" result="noise" />
              <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0.6  0 0 0 0 0.2  0 0 0 0 0.05  0 0 0 0.45 0" />
              <feComposite in2="SourceGraphic" operator="in" />
            </filter>
          </defs>
          {/* Heat-haze rectangular backplate (palette-locked) */}
          <g className="solstice-haze">
            <ellipse cx="50" cy="56" rx="38" ry="42" fill={`url(#${uid}-haze)`} />
          </g>
          {/* Outer glow halo */}
          <circle cx="50" cy="56" r="40" fill="none" stroke="#ff7820" strokeWidth="1.4" opacity="0.4" className="solstice-glow" />
          {/* Body with 4-stop fire gradient + drop-shadow filter */}
          <g filter={`url(#${uid}-drop)`}>
            <path d={flamePath} fill={`url(#${uid}-body)`} stroke="#280404" strokeWidth="0.6" />
            {/* Coal-grain fractalNoise texture overlay */}
            <path d={flamePath} filter={`url(#${uid}-tex)`} opacity="0.5" />
          </g>
          {/* Inner flame core — scale-pulses on .solstice-core */}
          <g className="solstice-core" style={{ transformOrigin: '50px 60px' }}>
            <path d={coreFlame} fill="#ffaa3d" opacity="0.55" />
          </g>
          {/* Ember halo + ember at the heart */}
          <g className="solstice-ember">
            <circle cx="50" cy="56" r="11" fill={`url(#${uid}-emberHalo)`} />
            <path d="M50 56 m -7 0 a 7 9 0 1 0 14 0 a 7 9 0 1 0 -14 0" fill="#ffaa3d" />
          </g>
          {/* Three rising sparks, staggered A/B/C */}
          <g className="solstice-sparks">
            <circle cx="40" cy="74" r="0.9" fill="#ffe6a8" className="solstice-spark solstice-spark-A" />
            <circle cx="58" cy="78" r="0.8" fill="#ffe6a8" className="solstice-spark solstice-spark-B" />
            <circle cx="50" cy="80" r="0.9" fill="#ffe6a8" className="solstice-spark solstice-spark-C" />
          </g>
        </>
      );
    },
  },
  // ── Phase E2 new bodies (indices 9-18) ──────────────────────────────
  // Each body has at least 2 layers: silhouette (body) + decoration
  // path. Decorations tint via accentFill unless overridden with a
  // fixed decorationFill (used for items where the decoration semantics
  // demand a specific color, e.g. flame must read as fire). Animations
  // live in global.css under the "Cosmetic animations" section,
  // applied via the wrapping <g>'s decorationClass.

  // 9 fox — angular silhouette with two pointed ears and a tail
  // arcing to the side. Decoration: chest blaze + tail-tip highlight.
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop orange-fur gradient,
  // inner-ear pink triangles, belly/chest pale patch, tail-tip glow,
  // eye-mark fur lines, drop shadow. Preserves existing .body-fox class
  // on the chest+tail decoration so the soft-pulse animation still plays.
  { id: 'fox', label: 'Fox',
    body: 'M30 30 L 24 12 L 34 26 C 40 22, 60 22, 66 26 L 76 12 L 70 30 C 78 38, 80 64, 74 80 C 70 90, 60 92, 50 88 C 40 92, 30 90, 26 80 C 20 64, 22 38, 30 30 Z M 72 76 C 84 76, 90 60, 86 50 C 86 64, 80 70, 72 72 Z',
    decorationPath: 'M46 50 L 50 78 L 54 50 Z M 80 56 L 86 52 L 84 60 Z',
    decorationClass: 'body-fox',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const foxPath = 'M30 30 L 24 12 L 34 26 C 40 22, 60 22, 66 26 L 76 12 L 70 30 C 78 38, 80 64, 74 80 C 70 90, 60 92, 50 88 C 40 92, 30 90, 26 80 C 20 64, 22 38, 30 30 Z M 72 76 C 84 76, 90 60, 86 50 C 86 64, 80 70, 72 72 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-fur`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bodyHi} />
              <stop offset="35%"  stopColor={bodyHi} stopOpacity="0.88" />
              <stop offset="70%"  stopColor={bodyShadow} stopOpacity="0.78" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
            <radialGradient id={`${uid}-tailGlow`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor={bodyHi} stopOpacity="0.85" />
              <stop offset="100%" stopColor={bodyHi} stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="92" rx="22" ry="3" fill="#000" opacity="0.26" />
          {/* Body with 4-stop fur gradient + ink outline */}
          <path d={foxPath} fill={`url(#${uid}-fur)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Belly / chest pale patch */}
          <path d="M40 60 Q 50 56 60 60 L 58 86 Q 50 88 42 86 Z" fill={bodyHi} opacity="0.55" />
          {/* Belly secondary highlight */}
          <ellipse cx="50" cy="78" rx="10" ry="6" fill="#fff" opacity="0.18" />
          {/* Inner-ear pink triangles */}
          <path d="M27 18 L 32 26 L 29 24 Z" fill={accentFill} opacity="0.85" stroke="#1a1410" strokeWidth="0.3" strokeOpacity="0.6" />
          <path d="M73 18 L 68 26 L 71 24 Z" fill={accentFill} opacity="0.85" stroke="#1a1410" strokeWidth="0.3" strokeOpacity="0.6" />
          {/* Eye-mark fur lines (short strokes from eye-corners outward) */}
          <path d="M36 46 L 32 44" stroke="#1a1410" strokeWidth="0.5" strokeLinecap="round" opacity="0.65" />
          <path d="M64 46 L 68 44" stroke="#1a1410" strokeWidth="0.5" strokeLinecap="round" opacity="0.65" />
          {/* Chest blaze + tail-tip highlight — keep existing decoration class */}
          <g className="body-fox">
            <path d="M46 50 L 50 78 L 54 50 Z" fill={accentFill} />
            <circle cx="84" cy="56" r="2.4" fill={`url(#${uid}-tailGlow)`} />
            <path d="M80 56 L 86 52 L 84 60 Z" fill={bodyHi} />
          </g>
          {/* Snout shadow */}
          <ellipse cx="50" cy="60" rx="5" ry="3" fill={bodyShadow} opacity="0.22" />
          {/* Top-of-head highlight arc */}
          <path d="M34 28 Q 50 22 66 28" stroke="#fff" strokeWidth="1.4" opacity="0.2" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 10 wolf — broader silhouette with thick mane tufts. Two angular
  // ears at the top, a heavy neck spreading to a wide chest.
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop slate-grey fur gradient,
  // inner-ear lighter fur, muzzle patch, belly patch, mane shadow
  // (decoration path repeated 1px below for depth), drop shadow. Keeps
  // existing .body-wolf class on the mane decoration so it still pulses.
  { id: 'wolf', label: 'Wolf',
    body: 'M50 24 C 32 24, 24 36, 24 50 C 18 54, 18 62, 22 64 C 22 78, 32 90, 50 90 C 68 90, 78 78, 78 64 C 82 62, 82 54, 76 50 C 76 36, 68 24, 50 24 Z M 30 36 L 24 22 L 30 30 Z M 70 36 L 76 22 L 70 30 Z',
    decorationPath: 'M34 56 L 30 72 L 38 64 L 42 78 L 46 64 L 50 80 L 54 64 L 58 78 L 62 64 L 70 72 L 66 56 Z',
    decorationClass: 'body-wolf',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const wolfPath = 'M50 24 C 32 24, 24 36, 24 50 C 18 54, 18 62, 22 64 C 22 78, 32 90, 50 90 C 68 90, 78 78, 78 64 C 82 62, 82 54, 76 50 C 76 36, 68 24, 50 24 Z M 30 36 L 24 22 L 30 30 Z M 70 36 L 76 22 L 70 30 Z';
      const manePath = 'M34 56 L 30 72 L 38 64 L 42 78 L 46 64 L 50 80 L 54 64 L 58 78 L 62 64 L 70 72 L 66 56 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-fur`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bodyHi} />
              <stop offset="40%"  stopColor={bodyHi} stopOpacity="0.78" />
              <stop offset="80%"  stopColor={bodyShadow} stopOpacity="0.88" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="92" rx="24" ry="3" fill="#000" opacity="0.26" />
          {/* Body with 4-stop slate-grey fur gradient + ink outline */}
          <path d={wolfPath} fill={`url(#${uid}-fur)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Inner-ear lighter fur tufts */}
          <path d="M28 32 L 26 24 L 29 28 Z" fill={bodyHi} opacity="0.55" />
          <path d="M72 32 L 74 24 L 71 28 Z" fill={bodyHi} opacity="0.55" />
          <path d="M27 22 L 25 26 L 28 26 Z" fill={accentFill} opacity="0.55" />
          <path d="M73 22 L 75 26 L 72 26 Z" fill={accentFill} opacity="0.55" />
          {/* Muzzle patch */}
          <ellipse cx="50" cy="56" rx="11" ry="6" fill={bodyHi} opacity="0.32" />
          {/* Nose bridge accent */}
          <ellipse cx="50" cy="50" rx="5" ry="2.4" fill={bodyShadow} opacity="0.28" />
          {/* Belly patch */}
          <ellipse cx="50" cy="80" rx="16" ry="8" fill={bodyHi} opacity="0.28" />
          {/* Mane shadow — repeat 1px below for depth */}
          <path d={manePath} fill={bodyShadow} opacity="0.45" transform="translate(0 1.2)" />
          {/* Existing mane decoration — preserves .body-wolf class for pulse animation */}
          <g className="body-wolf">
            <path d={manePath} fill={accentFill} />
          </g>
          {/* Mane center highlight */}
          <path d="M44 64 L 50 76 L 56 64" stroke={bodyHi} strokeWidth="0.7" fill="none" opacity="0.4" strokeLinecap="round" />
          {/* Eye-glow hints (small bright pupils at eye centers) */}
          <circle cx="40" cy="50" r="0.5" fill="#fff4c2" opacity="0.6" />
          <circle cx="60" cy="50" r="0.5" fill="#fff4c2" opacity="0.6" />
          {/* Top-of-head highlight arc */}
          <path d="M32 30 Q 50 24 68 30" stroke="#fff" strokeWidth="1.3" opacity="0.18" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 11 raven — winged perched bird. Ovoid body with two outstretched
  // wing tufts. Decoration: feather streaks across the wings.
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop near-black gradient
  // (#0a0810→#7a5cb8), wing-feather barbs (3 curves per wing),
  // iridescent violet-blue highlight arc, beak highlight, drop shadow.
  // Preserves existing .body-raven class on the feather-streaks
  // decoration so it still pulses softly.
  { id: 'raven', label: 'Raven',
    body: 'M50 28 C 40 28, 34 38, 34 54 C 34 76, 42 90, 50 90 C 58 90, 66 76, 66 54 C 66 38, 60 28, 50 28 Z M 34 50 L 14 44 L 22 54 L 12 60 L 24 64 L 16 74 L 30 70 Z M 66 50 L 86 44 L 78 54 L 88 60 L 76 64 L 84 74 L 70 70 Z M 50 28 L 46 20 L 50 24 L 54 20 Z',
    decorationPath: 'M22 52 L 18 56 L 18 54 Z M 24 60 L 20 64 L 20 62 Z M 26 66 L 22 70 L 22 68 Z M 78 52 L 82 56 L 82 54 Z M 76 60 L 80 64 L 80 62 Z M 74 66 L 78 70 L 78 68 Z',
    decorationClass: 'body-raven',
    richSvg: ({ uid }) => {
      const ravenPath = 'M50 28 C 40 28, 34 38, 34 54 C 34 76, 42 90, 50 90 C 58 90, 66 76, 66 54 C 66 38, 60 28, 50 28 Z M 34 50 L 14 44 L 22 54 L 12 60 L 24 64 L 16 74 L 30 70 Z M 66 50 L 86 44 L 78 54 L 88 60 L 76 64 L 84 74 L 70 70 Z M 50 28 L 46 20 L 50 24 L 54 20 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-feather`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#3a3a55" />
              <stop offset="35%"  stopColor="#1a1825" />
              <stop offset="75%"  stopColor="#0a0810" />
              <stop offset="100%" stopColor="#04020a" />
            </linearGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="92" rx="20" ry="2.6" fill="#000" opacity="0.3" />
          {/* Body with 4-stop near-black gradient + ink outline */}
          <path d={ravenPath} fill={`url(#${uid}-feather)`} stroke="#04020a" strokeWidth="0.6" />
          {/* Iridescent violet-blue highlight arc on body */}
          <path d="M40 36 Q 50 32 60 36" stroke="#7a5cb8" strokeWidth="1.2" opacity="0.55" fill="none" strokeLinecap="round" />
          <path d="M42 44 Q 50 42 58 44" stroke="#5a4a98" strokeWidth="0.8" opacity="0.45" fill="none" strokeLinecap="round" />
          {/* Wing-feather barbs (3 layered curves per wing) */}
          <path d="M30 52 Q 22 54 16 50" stroke="#3a3a55" strokeWidth="0.6" fill="none" opacity="0.65" strokeLinecap="round" />
          <path d="M28 60 Q 20 62 14 58" stroke="#3a3a55" strokeWidth="0.6" fill="none" opacity="0.65" strokeLinecap="round" />
          <path d="M28 68 Q 22 70 18 66" stroke="#3a3a55" strokeWidth="0.55" fill="none" opacity="0.6" strokeLinecap="round" />
          <path d="M70 52 Q 78 54 84 50" stroke="#3a3a55" strokeWidth="0.6" fill="none" opacity="0.65" strokeLinecap="round" />
          <path d="M72 60 Q 80 62 86 58" stroke="#3a3a55" strokeWidth="0.6" fill="none" opacity="0.65" strokeLinecap="round" />
          <path d="M72 68 Q 78 70 82 66" stroke="#3a3a55" strokeWidth="0.55" fill="none" opacity="0.6" strokeLinecap="round" />
          {/* Existing feather-streak decoration — preserves .body-raven pulse */}
          <g className="body-raven">
            <path d="M22 52 L 18 56 L 18 54 Z M 24 60 L 20 64 L 20 62 Z M 26 66 L 22 70 L 22 68 Z M 78 52 L 82 56 L 82 54 Z M 76 60 L 80 64 L 80 62 Z M 74 66 L 78 70 L 78 68 Z"
                  fill="#7a5cb8" opacity="0.7" />
          </g>
          {/* Beak: subtle highlight wedge at tip */}
          <path d="M48 22 L 50 16 L 52 22 Z" fill="#a07028" stroke="#1a1410" strokeWidth="0.3" />
          <path d="M49.4 19 L 50 16.6 L 50.6 19 Z" fill="#fff4c2" opacity="0.6" />
          {/* Body sheen highlight */}
          <path d="M44 36 Q 42 56 44 78" stroke="#fff" strokeWidth="0.8" opacity="0.16" fill="none" strokeLinecap="round" />
          {/* Tiny eye highlights (raven beady eyes) */}
          <circle cx="42" cy="42" r="0.7" fill="#fff4c2" opacity="0.75" />
          <circle cx="58" cy="42" r="0.7" fill="#fff4c2" opacity="0.75" />
        </>
      );
    },
  },
  // 12 owl — round body with two large pale eye-discs integrated into
  // the silhouette. Two small ear tufts at the top. The decoration
  // paints the eye discs in a near-white tone (decorationFill) so the
  // owl reads as eyes-on-the-body rather than relying on the face
  // layer.
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop brown/cream body gradient,
  // 6 V-chevron chest feather pattern, eye-disc inner bright cores,
  // ear-tuft accent stripes, accent beak, drop shadow. Preserves
  // existing .body-owl class on the eye-disc decoration so the soft
  // blink-pulse animation still plays.
  { id: 'owl', label: 'Owl',
    body: 'M50 22 C 32 22, 24 38, 24 56 C 24 78, 36 92, 50 92 C 64 92, 76 78, 76 56 C 76 38, 68 22, 50 22 Z M 30 30 L 26 18 L 32 26 Z M 70 30 L 74 18 L 68 26 Z',
    decorationPath: 'M40 50 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0 M 60 50 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0 M 47 56 L 50 62 L 53 56 Z',
    decorationFill: '#f3e9d2',
    decorationClass: 'body-owl',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const owlPath = 'M50 22 C 32 22, 24 38, 24 56 C 24 78, 36 92, 50 92 C 64 92, 76 78, 76 56 C 76 38, 68 22, 50 22 Z M 30 30 L 26 18 L 32 26 Z M 70 30 L 74 18 L 68 26 Z';
      return (
        <>
          <defs>
            <radialGradient id={`${uid}-plume`} cx="50%" cy="35%" r="65%">
              <stop offset="0%"   stopColor="#fff8d8" />
              <stop offset="30%"  stopColor={bodyHi} />
              <stop offset="70%"  stopColor={bodyShadow} stopOpacity="0.85" />
              <stop offset="100%" stopColor={bodyShadow} />
            </radialGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="93" rx="22" ry="2.6" fill="#000" opacity="0.26" />
          {/* Body + ear tufts with 4-stop plumage gradient + ink outline */}
          <path d={owlPath} fill={`url(#${uid}-plume)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Ear-tuft accent stripes */}
          <path d="M28 22 L 30 28" stroke={bodyShadow} strokeWidth="0.5" opacity="0.6" strokeLinecap="round" />
          <path d="M72 22 L 70 28" stroke={bodyShadow} strokeWidth="0.5" opacity="0.6" strokeLinecap="round" />
          {/* Feather chest pattern — 6 V-chevrons (widths decreasing bottom-up) */}
          <path d="M40 84 L 50 78 L 60 84" stroke={bodyShadow} strokeWidth="0.6" fill="none" opacity="0.5" strokeLinecap="round" />
          <path d="M42 78 L 50 72 L 58 78" stroke={bodyShadow} strokeWidth="0.6" fill="none" opacity="0.5" strokeLinecap="round" />
          <path d="M43 72 L 50 66 L 57 72" stroke={bodyShadow} strokeWidth="0.55" fill="none" opacity="0.48" strokeLinecap="round" />
          <path d="M44 66 L 50 62 L 56 66" stroke={bodyShadow} strokeWidth="0.5" fill="none" opacity="0.45" strokeLinecap="round" />
          {/* Belly tint to ground chest pattern */}
          <ellipse cx="50" cy="76" rx="14" ry="9" fill={bodyHi} opacity="0.2" />
          {/* Eye-disc decoration — preserves .body-owl class */}
          <g className="body-owl">
            <circle cx="40" cy="50" r="8" fill="#f3e9d2" stroke="#1a1410" strokeWidth="0.4" />
            <circle cx="60" cy="50" r="8" fill="#f3e9d2" stroke="#1a1410" strokeWidth="0.4" />
            {/* Inner bright eye-core highlights */}
            <circle cx="40" cy="50" r="3.6" fill="#fff8e8" />
            <circle cx="60" cy="50" r="3.6" fill="#fff8e8" />
            {/* Tiny pupils */}
            <circle cx="40" cy="50" r="1.2" fill="#1a1410" />
            <circle cx="60" cy="50" r="1.2" fill="#1a1410" />
            {/* Pupil glints */}
            <circle cx="39.5" cy="49.5" r="0.4" fill="#fff" />
            <circle cx="59.5" cy="49.5" r="0.4" fill="#fff" />
          </g>
          {/* Eye-disc rim shadow on the inner side */}
          <path d="M40 50 m -8 0 a 8 8 0 1 0 16 0" fill="none" stroke={bodyShadow} strokeWidth="0.4" opacity="0.4" />
          <path d="M60 50 m -8 0 a 8 8 0 1 0 16 0" fill="none" stroke={bodyShadow} strokeWidth="0.4" opacity="0.4" />
          {/* Beak (small triangle below the eye discs) */}
          <path d="M47 56 L 50 62 L 53 56 Z" fill={accentFill} stroke="#1a1410" strokeWidth="0.4" />
          <path d="M49 57 L 50 60 L 51 57 Z" fill="#fff4c2" opacity="0.5" />
          {/* Top-of-head highlight arc */}
          <path d="M34 30 Q 50 24 66 30" stroke="#fff" strokeWidth="1.3" opacity="0.18" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 13 jellyfish — translucent bell with trailing tendril fringe.
  // Decoration: tendril paths drawn as thin tapered fills hanging
  // from the bell. CSS-animated float (translateY ±2px on the body).
  //
  // Phase D Tier 4 — richSvg: translucent bell with inner radial
  // highlight, volumetric layered re-strokes, swaying tendrils (skewX
  // ±3° on staggered phases), 5 bioluminescent dots pulsing, outer
  // pale-cyan halo, feTurbulence jelly-membrane texture, drop-shadow
  // filter, extended float (±3px + scale 0.98↔1.02).
  { id: 'jellyfish', label: 'Jellyfish',
    body: 'M50 24 C 28 24, 20 44, 22 60 C 22 64, 28 64, 30 60 C 32 56, 36 64, 38 60 C 40 56, 44 64, 46 60 C 48 56, 52 64, 54 60 C 56 56, 60 64, 62 60 C 64 56, 68 64, 70 60 C 72 64, 78 64, 78 60 C 80 44, 72 24, 50 24 Z',
    decorationPath: 'M28 60 L 30 62 L 26 88 L 24 86 Z M 38 60 L 40 62 L 38 92 L 36 90 Z M 50 60 L 52 62 L 50 92 L 48 92 Z M 62 60 L 64 62 L 64 90 L 60 92 Z M 72 60 L 74 62 L 76 86 L 72 88 Z',
    decorationClass: 'body-jellyfish',
    richSvg: ({ uid, bodyHi, accentFill, primaryHue }) => {
      const bellPath = 'M50 24 C 28 24, 20 44, 22 60 C 22 64, 28 64, 30 60 C 32 56, 36 64, 38 60 C 40 56, 44 64, 46 60 C 48 56, 52 64, 54 60 C 56 56, 60 64, 62 60 C 64 56, 68 64, 70 60 C 72 64, 78 64, 78 60 C 80 44, 72 24, 50 24 Z';
      // Palette-tinted: jellyfish picks up the player's primary hue for
      // the bell base, but uses pale-cyan accents for bioluminescence.
      const bellBase = `hsl(${primaryHue} 60% 60%)`;
      const bellShadow = `hsl(${primaryHue} 70% 30%)`;
      return (
        <>
          <defs>
            <radialGradient id={`${uid}-bell`} cx="50%" cy="40%" r="60%">
              <stop offset="0%"   stopColor={bodyHi} stopOpacity="0.9" />
              <stop offset="50%"  stopColor={bellBase}       stopOpacity="0.75" />
              <stop offset="100%" stopColor={bellShadow}     stopOpacity="0.7" />
            </radialGradient>
            <radialGradient id={`${uid}-halo`} cx="50%" cy="42%" r="65%">
              <stop offset="0%"   stopColor="#a0eaf2" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#a0eaf2" stopOpacity="0" />
            </radialGradient>
            <filter id={`${uid}-drop`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="1.2" />
              <feOffset dx="0" dy="1.6" result="offset" />
              <feComponentTransfer><feFuncA type="linear" slope="0.45" /></feComponentTransfer>
              <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id={`${uid}-membrane`}>
              <feTurbulence type="fractalNoise" baseFrequency="0.06" numOctaves="2" result="n" />
              <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.6  0 0 0 0 0.9  0 0 0 0 1  0 0 0 0.18 0" />
              <feComposite in2="SourceGraphic" operator="in" />
            </filter>
          </defs>
          {/* Outer pale halo */}
          <g className="jelly-halo">
            <ellipse cx="50" cy="44" rx="34" ry="22" fill={`url(#${uid}-halo)`} />
          </g>
          {/* Bell, drop-shadowed, with layered volume re-strokes */}
          <g filter={`url(#${uid}-drop)`}>
            <path d={bellPath} fill={`url(#${uid}-bell)`} opacity="0.7" stroke={bellShadow} strokeWidth="0.5" />
            {/* Layered inner volume — scaled 0.95× and 0.88× */}
            <g transform="translate(50 44) scale(0.95) translate(-50 -44)">
              <path d={bellPath} fill={bodyHi} opacity="0.35" />
            </g>
            <g transform="translate(50 42) scale(0.78) translate(-50 -42)">
              <path d={bellPath} fill={bodyHi} opacity="0.5" />
            </g>
            {/* Membrane texture overlay */}
            <path d={bellPath} filter={`url(#${uid}-membrane)`} opacity="0.6" />
          </g>
          {/* Bioluminescent dots inside the bell, staggered pulses */}
          <g className="jelly-motes">
            <circle cx="38" cy="40" r="0.8" fill="#a0eaf2" className="jelly-mote jelly-mote-A" />
            <circle cx="48" cy="34" r="0.9" fill="#cff4fa" className="jelly-mote jelly-mote-B" />
            <circle cx="56" cy="42" r="0.8" fill="#a0eaf2" className="jelly-mote jelly-mote-C" />
            <circle cx="62" cy="36" r="0.7" fill="#cff4fa" className="jelly-mote jelly-mote-D" />
            <circle cx="44" cy="48" r="0.7" fill="#a0eaf2" className="jelly-mote jelly-mote-E" />
          </g>
          {/* Tendrils — 5 swaying paths with skewX stagger */}
          <g className="jelly-tendrils">
            <path d="M28 60 L 30 62 L 26 88 L 24 86 Z" fill={accentFill} className="jelly-tendril jelly-tendril-A" />
            <path d="M38 60 L 40 62 L 38 92 L 36 90 Z" fill={accentFill} className="jelly-tendril jelly-tendril-B" />
            <path d="M50 60 L 52 62 L 50 92 L 48 92 Z" fill={accentFill} className="jelly-tendril jelly-tendril-C" />
            <path d="M62 60 L 64 62 L 64 90 L 60 92 Z" fill={accentFill} className="jelly-tendril jelly-tendril-D" />
            <path d="M72 60 L 74 62 L 76 86 L 72 88 Z" fill={accentFill} className="jelly-tendril jelly-tendril-E" />
          </g>
        </>
      );
    },
  },
  // 14 living-candle — wax cylinder body with a flickering flame on
  // top. Decoration: orange flame painted in fixed flame color
  // (#ffaa3d) so it reads as fire regardless of body hue. The flame
  // pulses via .body-living-candle (opacity wave).
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop wax gradient, 2 wax drip
  // teardrops, flame-core layering (inner bright + outer flame),
  // halo glow circle, wick char-stub. Preserves existing
  // .body-living-candle class on the flame group so it still pulses.
  { id: 'living-candle', label: 'Living Candle',
    body: 'M38 30 C 38 26, 62 26, 62 30 L 62 88 C 62 92, 38 92, 38 88 Z M 42 30 C 42 26, 58 22, 58 30 Z',
    decorationPath: 'M50 12 C 44 18, 56 22, 50 30 C 50 22, 44 18, 50 12 Z M 48 30 L 52 30 L 52 36 L 48 36 Z',
    decorationFill: '#ffaa3d',
    decorationClass: 'body-living-candle',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      const waxPath = 'M38 30 C 38 26, 62 26, 62 30 L 62 88 C 62 92, 38 92, 38 88 Z M 42 30 C 42 26, 58 22, 58 30 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-wax`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor={bodyShadow} />
              <stop offset="30%"  stopColor={bodyHi} />
              <stop offset="60%"  stopColor={bodyHi} stopOpacity="0.92" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
            <radialGradient id={`${uid}-flameHalo`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#ffd27a" stopOpacity="0.75" />
              <stop offset="60%"  stopColor="#ffaa3d" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#ffaa3d" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="93" rx="14" ry="2.4" fill="#000" opacity="0.28" />
          {/* Wax cylinder with 4-stop gradient + ink outline */}
          <path d={waxPath} fill={`url(#${uid}-wax)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Wax drip teardrops */}
          <path d="M40 44 Q 36 50 38 56 Q 41 52 40 44 Z" fill={bodyHi} stroke="#1a1410" strokeWidth="0.35" strokeOpacity="0.65" />
          <path d="M60 58 Q 64 62 62 70 Q 59 64 60 58 Z" fill={bodyHi} stroke="#1a1410" strokeWidth="0.35" strokeOpacity="0.6" />
          {/* Wax surface vertical highlight stripe */}
          <rect x="42" y="34" width="1.6" height="50" fill="#fff" opacity="0.28" rx="0.6" />
          {/* Wax shadow stripe on far side */}
          <rect x="56.4" y="34" width="1.6" height="50" fill={bodyShadow} opacity="0.42" rx="0.6" />
          {/* Top wax rim — molten pool shadow */}
          <ellipse cx="50" cy="30" rx="10" ry="2" fill={bodyShadow} opacity="0.5" />
          {/* Wick char-stub */}
          <line x1="50" y1="30" x2="50" y2="24" stroke="#1a1410" strokeWidth="0.8" strokeLinecap="round" />
          {/* Halo glow circle around flame */}
          <circle cx="50" cy="18" r="10" fill={`url(#${uid}-flameHalo)`} />
          {/* Flame layered — preserves existing .body-living-candle class */}
          <g className="body-living-candle">
            {/* Outer larger flame at 0.45 opacity */}
            <path d="M50 8 C 42 16, 58 22, 50 32 C 50 22, 44 16, 50 8 Z" fill="#ff7820" opacity="0.45" />
            {/* Main orange flame */}
            <path d="M50 12 C 44 18, 56 22, 50 30 C 50 22, 44 18, 50 12 Z" fill="#ffaa3d" />
            {/* Inner bright flame core */}
            <path d="M50 16 C 47 20, 53 22, 50 28 C 50 22, 47 20, 50 16 Z" fill="#fff4c2" opacity="0.85" />
          </g>
        </>
      );
    },
  },
  // 15 lantern — paper lantern with a handle hoop on top. Decoration:
  // warm amber glow square in the lantern's center + the top brace.
  // Animated glow (drop-shadow pulse via .body-lantern).
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop metal-frame gradient,
  // semi-transparent glass pane with radial warm glow, 4 corner
  // rivets, dark stroke metal braces, small flame inside glass,
  // hanging-chain hint above hoop. Preserves existing .body-lantern
  // class on the glow rect so its warm-pulse animation still plays.
  { id: 'lantern', label: 'Lantern',
    body: 'M30 38 C 30 30, 70 30, 70 38 L 72 70 C 72 80, 60 88, 50 88 C 40 88, 28 80, 28 70 Z M 34 30 L 50 14 L 66 30 Z M 50 14 L 50 6',
    decorationPath: 'M40 48 L 60 48 L 60 70 L 40 70 Z M 38 38 L 62 38 L 62 42 L 38 42 Z',
    decorationFill: '#ffd27a',
    decorationClass: 'body-lantern',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      const lanternBody = 'M30 38 C 30 30, 70 30, 70 38 L 72 70 C 72 80, 60 88, 50 88 C 40 88, 28 80, 28 70 Z M 34 30 L 50 14 L 66 30 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-metal`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bodyHi} />
              <stop offset="35%"  stopColor={bodyHi} stopOpacity="0.78" />
              <stop offset="70%"  stopColor={bodyShadow} stopOpacity="0.85" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
            <radialGradient id={`${uid}-glow`} cx="50%" cy="55%" r="55%">
              <stop offset="0%"   stopColor="#fff4c2" />
              <stop offset="40%"  stopColor="#ffd27a" />
              <stop offset="80%"  stopColor="#ff9038" stopOpacity="0.65" />
              <stop offset="100%" stopColor="#a04020" stopOpacity="0.3" />
            </radialGradient>
            <radialGradient id={`${uid}-lightCone`} cx="50%" cy="0%" r="60%">
              <stop offset="0%"   stopColor="#ffd27a" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#ffd27a" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Faint light cone on the ground beneath lantern */}
          <path d="M30 92 L 70 92 L 60 96 L 40 96 Z" fill={`url(#${uid}-lightCone)`} opacity="0.5" />
          {/* Drop shadow */}
          <ellipse cx="50" cy="93" rx="22" ry="2.4" fill="#000" opacity="0.26" />
          {/* Hanging chain hint above hoop */}
          <line x1="50" y1="14" x2="50" y2="4" stroke="#1a1410" strokeWidth="1" strokeDasharray="1.4 1" opacity="0.85" />
          {/* Top hoop */}
          <ellipse cx="50" cy="13" rx="3.4" ry="1.4" fill="none" stroke="#1a1410" strokeWidth="0.7" />
          {/* Lantern body with 4-stop metal gradient + ink outline */}
          <path d={lanternBody} fill={`url(#${uid}-metal)`} stroke="#1a1410" strokeWidth="0.65" strokeOpacity="0.9" />
          {/* Glass pane with radial warm glow — preserves .body-lantern class */}
          <g className="body-lantern">
            <rect x="36" y="42" width="28" height="36" fill={`url(#${uid}-glow)`} stroke="#1a1410" strokeWidth="0.5" rx="1" />
          </g>
          {/* Top metal brace */}
          <rect x="36" y="36" width="28" height="4" fill={bodyShadow} stroke="#1a1410" strokeWidth="0.4" />
          <rect x="36.4" y="36.4" width="27.2" height="0.8" fill="#fff" opacity="0.35" />
          {/* Bottom metal brace */}
          <rect x="36" y="78" width="28" height="3.2" fill={bodyShadow} stroke="#1a1410" strokeWidth="0.4" />
          {/* 4 corner rivets */}
          <circle cx="38" cy="44" r="0.9" fill="#1a1410" />
          <circle cx="62" cy="44" r="0.9" fill="#1a1410" />
          <circle cx="38" cy="76" r="0.9" fill="#1a1410" />
          <circle cx="62" cy="76" r="0.9" fill="#1a1410" />
          {/* Flame inside glass */}
          <path d="M50 56 C 46 60, 54 64, 50 72 C 50 64, 46 60, 50 56 Z" fill="#ff7820" opacity="0.85" />
          <path d="M50 60 C 48 62, 52 64, 50 70 C 50 64, 48 62, 50 60 Z" fill="#fff4c2" opacity="0.85" />
          {/* Vertical glass pane shading */}
          <rect x="38" y="44" width="2" height="32" fill="#fff" opacity="0.18" />
          <rect x="60" y="44" width="2" height="32" fill={bodyShadow} opacity="0.32" />
          {/* Top crown highlight stroke */}
          <path d="M36 30 L 50 16 L 64 30" stroke="#fff" strokeWidth="0.6" opacity="0.3" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 16 broom — witch's broom: cone-bristle bottom with a long handle
  // extending upward. Decoration: binding twine wrap + sweeping
  // bristle stripes across the bristle cone.
  //
  // Phase B Tier 2 — drop-shadow + 3-stop bristle/handle gradients +
  // ink outline + accent layer: handle wood-grain + extra middle bristle
  // highlight + twine cross-hatch + handle-tip cap.
  { id: 'broom', label: 'Broom',
    body: 'M48 12 L 52 12 L 52 56 L 64 56 L 76 92 L 24 92 L 36 56 L 48 56 Z',
    decorationPath: 'M34 60 L 66 60 L 64 64 L 36 64 Z M 32 92 L 38 64 L 36 64 Z M 48 92 L 50 64 L 52 92 Z M 68 92 L 64 64 L 62 64 Z',
    decorationClass: 'body-broom',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const broomPath = 'M48 12 L 52 12 L 52 56 L 64 56 L 76 92 L 24 92 L 36 56 L 48 56 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-broom`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={bodyHi} />
              <stop offset="55%" stopColor={bodyHi} stopOpacity="0.78" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="92" rx="28" ry="3" fill="#000" opacity="0.26" />
          {/* Body with 3-stop gradient + ink outline */}
          <path d={broomPath} fill={`url(#${uid}-broom)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Accent: handle wood-grain curves */}
          <path d="M49 16 Q 49.4 32 49 54" stroke={bodyShadow} strokeWidth="0.45" fill="none" opacity="0.5" />
          <path d="M51 16 Q 50.6 32 51 54" stroke={bodyShadow} strokeWidth="0.45" fill="none" opacity="0.5" />
          {/* Handle highlight stripe */}
          <rect x="48.4" y="14" width="0.6" height="42" fill={bodyHi} opacity="0.5" />
          {/* Bristle sweep stripes */}
          <path d="M34 68 L 32 92" stroke={bodyShadow} strokeWidth="1.2" opacity="0.4" strokeLinecap="round" />
          <path d="M44 68 L 42 92" stroke={bodyShadow} strokeWidth="1.0" opacity="0.36" strokeLinecap="round" />
          <path d="M50 68 L 50 92" stroke={bodyHi} strokeWidth="1.1" opacity="0.45" strokeLinecap="round" />
          <path d="M56 68 L 58 92" stroke={bodyShadow} strokeWidth="1.0" opacity="0.36" strokeLinecap="round" />
          <path d="M66 68 L 68 92" stroke={bodyShadow} strokeWidth="1.2" opacity="0.4" strokeLinecap="round" />
          {/* Twine binding wrap (rect) */}
          <rect x="34" y="58" width="32" height="6" fill={accentFill} stroke="#1a1410" strokeWidth="0.4" rx="1" />
          {/* Twine cross-hatch detail */}
          <path d="M34 58 L 66 64" stroke="#1a1410" strokeWidth="0.4" opacity="0.55" />
          <path d="M34 64 L 66 58" stroke="#1a1410" strokeWidth="0.4" opacity="0.55" />
          {/* Handle-tip cap (accent) */}
          <rect x="47" y="10" width="6" height="4" fill={accentFill} stroke="#1a1410" strokeWidth="0.4" rx="1.2" />
          {/* Tip cap highlight */}
          <rect x="47.6" y="10.5" width="2" height="0.8" fill="#fff" opacity="0.45" rx="0.4" />
        </>
      );
    },
  },
  // 17 cauldron — squat round pot with rim handles + bubbling liquid.
  // Decoration: bubble circles rising from the cauldron's open top.
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop iron pot gradient, rim
  // band with 3 rivets, glowing brew at the rim with under-lit radial
  // gradient, 3 layered bubbles (existing positions preserved + brew
  // halo), 2 steam wisps. Preserves existing .body-cauldron class on
  // the bubble group so its float animation still plays.
  { id: 'cauldron', label: 'Cauldron',
    body: 'M22 52 C 22 46, 78 46, 78 52 L 76 84 C 76 90, 24 90, 24 84 Z M 18 50 L 22 46 L 22 54 Z M 78 50 L 82 50 L 78 54 Z M 26 48 L 74 48 L 74 56 L 26 56 Z',
    decorationPath: 'M34 44 m -3 0 a 3 3 0 1 0 6 0 a 3 3 0 1 0 -6 0 M 50 38 m -4 0 a 4 4 0 1 0 8 0 a 4 4 0 1 0 -8 0 M 64 42 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0',
    decorationClass: 'body-cauldron',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const potPath = 'M22 52 C 22 46, 78 46, 78 52 L 76 84 C 76 90, 24 90, 24 84 Z';
      const handleL = 'M18 50 L 22 46 L 22 54 Z';
      const handleR = 'M78 50 L 82 50 L 78 54 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-iron`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bodyHi} />
              <stop offset="25%"  stopColor={bodyShadow} stopOpacity="0.92" />
              <stop offset="55%"  stopColor={bodyHi} stopOpacity="0.7" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
            <radialGradient id={`${uid}-brewGlow`} cx="50%" cy="50%" r="55%">
              <stop offset="0%"   stopColor={accentFill} stopOpacity="0.9" />
              <stop offset="60%"  stopColor={accentFill} stopOpacity="0.45" />
              <stop offset="100%" stopColor={accentFill} stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="91" rx="28" ry="3" fill="#000" opacity="0.3" />
          {/* Pot body with 4-stop iron gradient + ink outline */}
          <path d={potPath} fill={`url(#${uid}-iron)`} stroke="#1a1410" strokeWidth="0.7" strokeOpacity="0.9" />
          {/* Rim handles */}
          <path d={handleL} fill={bodyShadow} stroke="#1a1410" strokeWidth="0.5" />
          <path d={handleR} fill={bodyShadow} stroke="#1a1410" strokeWidth="0.5" />
          {/* Rim band with rivets */}
          <rect x="26" y="48" width="48" height="8" fill={bodyShadow} opacity="0.65" stroke="#1a1410" strokeWidth="0.4" />
          <rect x="26.4" y="48.4" width="47.2" height="1.4" fill="#fff" opacity="0.25" />
          {/* 3 rivets on the rim band */}
          <circle cx="34" cy="52" r="1.1" fill="#1a1410" />
          <circle cx="50" cy="52" r="1.1" fill="#1a1410" />
          <circle cx="66" cy="52" r="1.1" fill="#1a1410" />
          {/* Highlights on rivets */}
          <circle cx="33.6" cy="51.6" r="0.4" fill="#fff" opacity="0.7" />
          <circle cx="49.6" cy="51.6" r="0.4" fill="#fff" opacity="0.7" />
          <circle cx="65.6" cy="51.6" r="0.4" fill="#fff" opacity="0.7" />
          {/* Brew at the rim — glowing ellipse with under-lit radial */}
          <ellipse cx="50" cy="48" rx="22" ry="2.6" fill={`url(#${uid}-brewGlow)`} />
          <ellipse cx="50" cy="48" rx="22" ry="2.4" fill={accentFill} opacity="0.85" stroke="#1a1410" strokeWidth="0.35" />
          <ellipse cx="50" cy="47.6" rx="18" ry="0.8" fill="#fff" opacity="0.4" />
          {/* Steam wisps rising from pot */}
          <path d="M36 44 Q 34 38, 38 34 Q 36 30, 40 26"
                stroke="#fff4c2" strokeWidth="0.7" fill="none" opacity="0.45" strokeLinecap="round" className="cauldron-steam" />
          <path d="M58 44 Q 62 38, 60 34 Q 64 30, 62 24"
                stroke="#fff4c2" strokeWidth="0.7" fill="none" opacity="0.4" strokeLinecap="round" className="cauldron-steam" />
          {/* Pot vertical highlight + shadow stripes */}
          <rect x="28" y="56" width="2" height="28" fill="#fff" opacity="0.16" rx="0.8" />
          <rect x="70" y="56" width="2" height="28" fill={bodyShadow} opacity="0.34" rx="0.8" />
          {/* Bubbles — preserves existing .body-cauldron float animation class */}
          <g className="body-cauldron">
            <circle cx="34" cy="44" r="3" fill={accentFill} opacity="0.85" stroke="#1a1410" strokeWidth="0.3" />
            <circle cx="33.4" cy="43.4" r="1" fill="#fff" opacity="0.55" />
            <circle cx="50" cy="38" r="4" fill={accentFill} opacity="0.85" stroke="#1a1410" strokeWidth="0.3" />
            <circle cx="49.2" cy="37" r="1.3" fill="#fff" opacity="0.6" />
            <circle cx="64" cy="42" r="2" fill={accentFill} opacity="0.85" stroke="#1a1410" strokeWidth="0.3" />
            <circle cx="63.6" cy="41.4" r="0.7" fill="#fff" opacity="0.55" />
          </g>
        </>
      );
    },
  },
  // 18 scroll — rolled parchment with rolled top + bottom and a
  // ribbon tie running across. Decoration: ink-line text + ribbon.
  //
  // Phase B Tier 2 — drop-shadow + 3-stop parchment gradient + ink outline
  // + accent layer: rolled-edge shadow bands + ribbon band + wax seal +
  // calligraphic flourish line.
  { id: 'scroll', label: 'Scroll',
    body: 'M32 22 C 32 18, 68 18, 68 22 L 68 26 C 68 28, 64 30, 60 28 L 60 78 C 64 76, 68 78, 68 82 L 68 86 C 68 90, 32 90, 32 86 L 32 82 C 32 78, 36 76, 40 78 L 40 28 C 36 30, 32 28, 32 26 Z',
    decorationPath: 'M44 48 L 56 48 L 56 52 L 44 52 Z M 44 60 L 56 60 L 56 64 L 44 64 Z M 38 36 L 62 36 L 62 40 L 38 40 Z',
    decorationClass: 'body-scroll',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const scrollPath = 'M32 22 C 32 18, 68 18, 68 22 L 68 26 C 68 28, 64 30, 60 28 L 60 78 C 64 76, 68 78, 68 82 L 68 86 C 68 90, 32 90, 32 86 L 32 82 C 32 78, 36 76, 40 78 L 40 28 C 36 30, 32 28, 32 26 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-parch`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bodyHi} />
              <stop offset="50%"  stopColor={bodyHi} stopOpacity="0.92" />
              <stop offset="100%" stopColor={bodyShadow} stopOpacity="0.85" />
            </linearGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="91" rx="24" ry="2.6" fill="#000" opacity="0.22" />
          {/* Body parchment with 3-stop gradient + ink outline */}
          <path d={scrollPath} fill={`url(#${uid}-parch)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Accent: rolled-edge shadow bands (top + bottom roll curls) */}
          <path d="M40 26 Q 50 24 60 26" stroke={bodyShadow} strokeWidth="0.6" fill="none" opacity="0.5" strokeLinecap="round" />
          <path d="M40 80 Q 50 82 60 80" stroke={bodyShadow} strokeWidth="0.6" fill="none" opacity="0.5" strokeLinecap="round" />
          <rect x="34" y="22" width="32" height="2" fill={bodyShadow} opacity="0.28" />
          <rect x="34" y="84" width="32" height="2" fill={bodyShadow} opacity="0.28" />
          {/* Ink-line "text" rows */}
          <rect x="42" y="36" width="16" height="1.4" fill="#1a1410" opacity="0.5" rx="0.3" />
          <rect x="42" y="40" width="14" height="1.2" fill="#1a1410" opacity="0.4" rx="0.3" />
          <rect x="42" y="62" width="16" height="1.4" fill="#1a1410" opacity="0.5" rx="0.3" />
          <rect x="42" y="66" width="13" height="1.2" fill="#1a1410" opacity="0.4" rx="0.3" />
          {/* Calligraphic flourish */}
          <path d="M42 70 Q 46 74 50 70 Q 54 66 58 70" stroke="#1a1410" strokeWidth="0.5" fill="none" opacity="0.5" strokeLinecap="round" />
          {/* Ribbon band across the middle */}
          <rect x="40" y="50" width="20" height="6" fill={accentFill} stroke="#1a1410" strokeWidth="0.4" />
          {/* Ribbon highlight stripe */}
          <rect x="40" y="50.6" width="20" height="1.2" fill="#fff" opacity="0.3" />
          {/* Wax seal */}
          <circle cx="50" cy="53" r="3" fill="#a02020" stroke="#1a1410" strokeWidth="0.5" />
          <circle cx="49.4" cy="52.4" r="0.9" fill="#ff4040" opacity="0.7" />
          <path d="M48.6 53 L 50 54.6 L 51.4 53 L 50 51.4 Z" stroke="#5a0808" strokeWidth="0.3" fill="none" />
        </>
      );
    },
  },
];

export const HAT_SHAPES: HatShape[] = [
  // 0 = none
  { id: 'none', label: 'None', path: '' },
  // 1 wizard
  //
  // Phase B Tier 2 — drop-shadow + 3-stop cone gradient + ink outline +
  // accent layer: hatband + 3 tiny gold star embellishments + tip curl.
  { id: 'wizard', label: 'Wizard',
    path: 'M50 4 L 26 32 L 74 32 Z',
    accent: 'M44 22 L 50 16 L 56 22 Z',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const wizardPath = 'M50 4 L 26 32 L 74 32 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-cone`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={bodyShadow} />
              <stop offset="50%" stopColor={accentFill} stopOpacity="0.85" />
              <stop offset="100%" stopColor={bodyHi} />
            </linearGradient>
          </defs>
          {/* Drop shadow under brim */}
          <ellipse cx="50" cy="33" rx="26" ry="2.4" fill="#000" opacity="0.3" />
          {/* Cone with 3-stop gradient + ink outline */}
          <path d={wizardPath} fill={`url(#${uid}-cone)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Tip curl (small bend at the top) */}
          <path d="M50 4 Q 53 6 52 10" stroke="#1a1410" strokeWidth="0.7" fill="none" strokeLinecap="round" opacity="0.85" />
          {/* Hatband */}
          <rect x="26" y="28" width="48" height="4" fill={bodyShadow} stroke="#1a1410" strokeWidth="0.4" />
          {/* Hatband highlight stripe */}
          <rect x="26" y="28.4" width="48" height="0.9" fill={bodyHi} opacity="0.45" />
          {/* Star embellishments — 3 tiny 4-point stars */}
          <path d="M44 16 L 44.5 17.4 L 46 18 L 44.5 18.6 L 44 20 L 43.5 18.6 L 42 18 L 43.5 17.4 Z" fill="#ffd27a" opacity="0.9" />
          <path d="M38 24 L 38.5 25.2 L 39.8 25.7 L 38.5 26.2 L 38 27.4 L 37.5 26.2 L 36.2 25.7 L 37.5 25.2 Z" fill="#ffd27a" opacity="0.85" />
          <path d="M58 22 L 58.5 23.3 L 60 23.8 L 58.5 24.3 L 58 25.6 L 57.5 24.3 L 56 23.8 L 57.5 23.3 Z" fill="#ffd27a" opacity="0.9" />
          {/* Brim highlight (top of band) */}
          <path d="M26 28 L 74 28" stroke="#fff" strokeWidth="0.5" opacity="0.32" />
          {/* Left side highlight line */}
          <path d="M38 26 L 48 8" stroke="#fff" strokeWidth="0.8" opacity="0.18" strokeLinecap="round" />
        </>
      );
    },
  },
  // 2 crown
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop gold gradient, 3 gemstones
  // at the zigzag peaks (diamond shapes with inner glint), engraved
  // cross-hatch band detail, gem-shimmer animation via .hat-crown-gem-A/B/C
  // on staggered phases.
  { id: 'crown', label: 'Crown',
    path: 'M28 28 L 32 12 L 40 22 L 50 8 L 60 22 L 68 12 L 72 28 Z',
    accent: 'M30 24 L 70 24 L 70 28 L 30 28 Z',
    richSvg: ({ uid, accentFill }) => {
      const crownPath = 'M28 28 L 32 12 L 40 22 L 50 8 L 60 22 L 68 12 L 72 28 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-gold`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#fff8d8" />
              <stop offset="30%"  stopColor="#f5d68a" />
              <stop offset="65%"  stopColor="#b8902a" />
              <stop offset="100%" stopColor="#6c5012" />
            </linearGradient>
          </defs>
          {/* Drop shadow under crown */}
          <ellipse cx="50" cy="30" rx="24" ry="2.2" fill="#000" opacity="0.32" />
          {/* Crown body with 4-stop gold gradient + ink outline */}
          <path d={crownPath} fill={`url(#${uid}-gold)`} stroke="#1a1410" strokeWidth="0.65" />
          {/* Engraved cross-hatch band — 2 diagonal strokes for engraved look */}
          <rect x="30" y="24" width="40" height="4" fill="#5a4218" opacity="0.4" stroke="#1a1410" strokeWidth="0.4" />
          <path d="M30 24 L 70 28" stroke="#5a4218" strokeWidth="0.4" opacity="0.7" />
          <path d="M30 28 L 70 24" stroke="#5a4218" strokeWidth="0.4" opacity="0.7" />
          <rect x="30" y="24.6" width="40" height="0.8" fill="#fff" opacity="0.3" />
          {/* 3 gemstones at zigzag peaks — staggered shimmer animation */}
          <g>
            <g className="hat-crown-gem-A">
              <path d="M32 12 L 36 16 L 32 20 L 28 16 Z" fill={accentFill} stroke="#5a0808" strokeWidth="0.4" />
              <path d="M32 14 L 34 16 L 32 18 L 30 16 Z" fill="#fff4c2" opacity="0.55" />
              <circle cx="31" cy="15" r="0.45" fill="#fff" opacity="0.85" />
            </g>
            <g className="hat-crown-gem-B">
              <path d="M50 8 L 54 12 L 50 16 L 46 12 Z" fill={accentFill} stroke="#5a0808" strokeWidth="0.4" />
              <path d="M50 10 L 52 12 L 50 14 L 48 12 Z" fill="#fff4c2" opacity="0.55" />
              <circle cx="49" cy="11" r="0.5" fill="#fff" opacity="0.85" />
            </g>
            <g className="hat-crown-gem-C">
              <path d="M68 12 L 72 16 L 68 20 L 64 16 Z" fill={accentFill} stroke="#5a0808" strokeWidth="0.4" />
              <path d="M68 14 L 70 16 L 68 18 L 66 16 Z" fill="#fff4c2" opacity="0.55" />
              <circle cx="67" cy="15" r="0.45" fill="#fff" opacity="0.85" />
            </g>
          </g>
          {/* Top-edge gold highlight on each tip */}
          <path d="M32 12 L 50 8 L 68 12" stroke="#fff8d8" strokeWidth="0.4" fill="none" opacity="0.5" />
          {/* Inner gold lining beneath band */}
          <path d="M30 22 L 70 22" stroke="#f5d680" strokeWidth="0.4" opacity="0.55" />
        </>
      );
    },
  },
  // 3 top hat
  //
  // Phase B Tier 2 — drop-shadow + 3-stop hat gradient + ink outline +
  // accent layer: colored hatband + gold buckle + brim highlight.
  { id: 'top', label: 'Top hat',
    path: 'M30 28 L 70 28 L 70 24 L 64 24 L 64 6 L 36 6 L 36 24 L 30 24 Z',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const topPath = 'M30 28 L 70 28 L 70 24 L 64 24 L 64 6 L 36 6 L 36 24 L 30 24 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-felt`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor={bodyShadow} />
              <stop offset="50%"  stopColor={bodyHi} />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
          </defs>
          {/* Drop shadow under brim */}
          <ellipse cx="50" cy="29" rx="22" ry="2.2" fill="#000" opacity="0.3" />
          {/* Hat with 3-stop gradient + ink outline */}
          <path d={topPath} fill={`url(#${uid}-felt)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Colored hatband at base of cylinder */}
          <rect x="36" y="18" width="28" height="6" fill={accentFill} stroke="#1a1410" strokeWidth="0.4" />
          {/* Hatband highlight stripe */}
          <rect x="36" y="18.5" width="28" height="0.9" fill="#fff" opacity="0.35" />
          {/* Gold buckle */}
          <rect x="46" y="19.4" width="8" height="3.2" fill="#f5d68a" stroke="#1a1410" strokeWidth="0.4" rx="0.4" />
          <rect x="48" y="20.4" width="4" height="1.2" fill="#1a1410" opacity="0.55" />
          {/* Brim highlight (top edge of brim) */}
          <path d="M30 24.4 L 70 24.4" stroke="#fff" strokeWidth="0.5" opacity="0.32" />
          {/* Top-edge highlight on cylinder */}
          <path d="M37 7.2 L 63 7.2" stroke="#fff" strokeWidth="0.6" opacity="0.28" />
          {/* Vertical right-side shadow */}
          <rect x="60" y="7" width="2.2" height="16" fill={bodyShadow} opacity="0.4" />
        </>
      );
    },
  },
  // 4 hood
  //
  // Phase B Tier 2 — drop-shadow + 3-stop hood gradient (lit-from-below
  // feel) + ink outline + accent layer: drape fold lines + inner shadow
  // at face opening + stitched seam dashes.
  { id: 'hood', label: 'Hood',
    path: 'M22 32 C 22 12, 78 12, 78 32 L 76 36 C 60 30, 40 30, 24 36 Z',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      const hoodPath = 'M22 32 C 22 12, 78 12, 78 32 L 76 36 C 60 30, 40 30, 24 36 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-hood`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={bodyShadow} />
              <stop offset="60%" stopColor={bodyShadow} stopOpacity="0.78" />
              <stop offset="100%" stopColor={bodyHi} />
            </linearGradient>
          </defs>
          {/* Drop shadow under hood opening */}
          <ellipse cx="50" cy="36" rx="26" ry="2" fill="#000" opacity="0.28" />
          {/* Hood with 3-stop gradient + ink outline */}
          <path d={hoodPath} fill={`url(#${uid}-hood)`} stroke="#1a1410" strokeWidth="0.6" strokeOpacity="0.85" />
          {/* Drape fold lines from peak down each side */}
          <path d="M50 12 Q 42 24 30 34" stroke={bodyShadow} strokeWidth="0.7" fill="none" opacity="0.55" strokeLinecap="round" />
          <path d="M50 12 Q 58 24 70 34" stroke={bodyShadow} strokeWidth="0.7" fill="none" opacity="0.55" strokeLinecap="round" />
          <path d="M50 12 L 50 32" stroke={bodyShadow} strokeWidth="0.6" fill="none" opacity="0.45" strokeLinecap="round" />
          {/* Inner shadow at face opening (darker band on lower hood edge) */}
          <path d="M24 34 Q 50 30 76 34" stroke={bodyShadow} strokeWidth="2.2" fill="none" opacity="0.55" strokeLinecap="round" />
          {/* Stitched seam — dashed stroke along one fold line */}
          <path d="M50 14 Q 56 24 66 32" stroke="#1a1410" strokeWidth="0.4" fill="none" strokeDasharray="1 1.4" opacity="0.7" />
          {/* Top highlight arc */}
          <path d="M28 22 Q 50 12 72 22" stroke="#fff" strokeWidth="1.4" opacity="0.18" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 5 antlers
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop bone gradient, velvet base
  // fuzz patches, brighter tip highlights, 2-3 thin ridge grooves
  // along each prong, drop shadow. Static (no animation — antler is
  // a stately element, but a SUBTLE pulse on tip glints).
  { id: 'antlers', label: 'Antlers',
    path: 'M30 22 L 22 6 L 28 14 L 32 4 L 36 18 Z M 64 18 L 68 4 L 72 14 L 78 6 L 70 22 Z',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      const antlerLeft = 'M30 22 L 22 6 L 28 14 L 32 4 L 36 18 Z';
      const antlerRight = 'M64 18 L 68 4 L 72 14 L 78 6 L 70 22 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-bone`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#fff4d8" />
              <stop offset="35%"  stopColor="#e8d8a8" />
              <stop offset="75%"  stopColor="#a8895a" />
              <stop offset="100%" stopColor="#5a3a18" />
            </linearGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="24" rx="24" ry="1.4" fill="#000" opacity="0.28" />
          {/* Velvet base fuzz patches (where antlers attach) */}
          <ellipse cx="32" cy="22" rx="4" ry="1.4" fill={bodyHi} opacity="0.45" />
          <ellipse cx="68" cy="22" rx="4" ry="1.4" fill={bodyHi} opacity="0.45" />
          {/* Antlers with 4-stop bone gradient + ink outline */}
          <path d={antlerLeft} fill={`url(#${uid}-bone)`} stroke="#1a1410" strokeWidth="0.55" />
          <path d={antlerRight} fill={`url(#${uid}-bone)`} stroke="#1a1410" strokeWidth="0.55" />
          {/* Ridge-groove texture along each prong (left antler) */}
          <path d="M30 20 L 26 10" stroke={bodyShadow} strokeWidth="0.35" opacity="0.55" />
          <path d="M32 18 L 31 6" stroke={bodyShadow} strokeWidth="0.35" opacity="0.55" />
          <path d="M34 18 L 35 8" stroke={bodyShadow} strokeWidth="0.35" opacity="0.5" />
          {/* Right antler ridges */}
          <path d="M70 20 L 74 10" stroke={bodyShadow} strokeWidth="0.35" opacity="0.55" />
          <path d="M68 18 L 69 6" stroke={bodyShadow} strokeWidth="0.35" opacity="0.55" />
          <path d="M66 18 L 65 8" stroke={bodyShadow} strokeWidth="0.35" opacity="0.5" />
          {/* Tip highlights — small glints with subtle pulse */}
          <g className="hat-antlers-tips">
            <circle cx="22" cy="6" r="0.7" fill="#fff8e8" className="antler-tip antler-tip-A" />
            <circle cx="32" cy="4" r="0.7" fill="#fff8e8" className="antler-tip antler-tip-B" />
            <circle cx="78" cy="6" r="0.7" fill="#fff8e8" className="antler-tip antler-tip-A" />
            <circle cx="68" cy="4" r="0.7" fill="#fff8e8" className="antler-tip antler-tip-B" />
          </g>
          {/* Tip cap shading */}
          <circle cx="22" cy="6" r="1.2" fill={bodyHi} opacity="0.45" />
          <circle cx="32" cy="4" r="1.2" fill={bodyHi} opacity="0.45" />
          <circle cx="78" cy="6" r="1.2" fill={bodyHi} opacity="0.45" />
          <circle cx="68" cy="4" r="1.2" fill={bodyHi} opacity="0.45" />
          {/* Highlight stroke along outer edge */}
          <path d="M30 22 L 22 6" stroke="#fff" strokeWidth="0.35" opacity="0.4" />
          <path d="M70 22 L 78 6" stroke="#fff" strokeWidth="0.35" opacity="0.4" />
        </>
      );
    },
  },
  // 6 flower
  //
  // Phase B Tier 2 — drop-shadow + 5 overlapping petal paths each with
  // radial gradient + gold center disc + small leaf bump on the stem.
  { id: 'flower', label: 'Flower',
    path: 'M50 6 m -8 0 a 8 8 0 1 0 16 0 a 8 8 0 1 0 -16 0 M50 16 L50 22',
    accent: 'M50 8 a 4 4 0 1 0 0 0.001',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      // 5 petals around (50, 8) — rendered as small ovals rotated 72° apart
      const petals = [0, 72, 144, 216, 288].map((angle, i) => (
        <ellipse
          key={`pet-${i}`}
          cx="50" cy="2" rx="4.5" ry="3"
          fill={`url(#${uid}-petal)`}
          stroke="#1a1410" strokeWidth="0.3" strokeOpacity="0.7"
          transform={`rotate(${angle} 50 8)`}
        />
      ));
      return (
        <>
          <defs>
            <radialGradient id={`${uid}-petal`} cx="50%" cy="50%" r="60%">
              <stop offset="0%"   stopColor={bodyHi} />
              <stop offset="55%"  stopColor={accentFill} />
              <stop offset="100%" stopColor={bodyShadow} />
            </radialGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="24" rx="9" ry="1.4" fill="#000" opacity="0.22" />
          {/* Stem */}
          <line x1="50" y1="14" x2="50" y2="22" stroke="#3a5028" strokeWidth="1.4" strokeLinecap="round" />
          {/* Stem-side leaf bump */}
          <path d="M50 18 Q 56 16 56 20 Q 53 21 50 19 Z" fill="#5a7838" stroke="#1a1410" strokeWidth="0.35" />
          {/* 5 overlapping petals */}
          {petals}
          {/* Center disc (gold) */}
          <circle cx="50" cy="8" r="2.4" fill="#f5d68a" stroke="#5a4218" strokeWidth="0.4" />
          {/* Center highlight */}
          <circle cx="49.4" cy="7.4" r="0.6" fill="#fff8d8" opacity="0.85" />
          {/* Pollen dots */}
          <circle cx="50.6" cy="8.4" r="0.3" fill="#5a4218" opacity="0.7" />
          <circle cx="49.4" cy="8.6" r="0.3" fill="#5a4218" opacity="0.7" />
          <circle cx="50.2" cy="7.2" r="0.25" fill="#5a4218" opacity="0.6" />
        </>
      );
    },
  },
  // 7 candle
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop cream wax gradient, wax
  // drip teardrop, 3-layer flame (outer orange + mid amber + inner
  // bright), wick char stub, halo glow circle behind flame. ONE
  // subtle pulse animation on the halo via .hat-candle-glow.
  { id: 'candle', label: 'Candle',
    path: 'M46 24 L 54 24 L 54 14 L 46 14 Z',
    accent: 'M50 14 C 46 8, 54 4, 50 -2 C 50 4, 46 10, 50 14 Z',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-wax`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor={bodyShadow} />
              <stop offset="35%"  stopColor={bodyHi} />
              <stop offset="65%"  stopColor={bodyHi} stopOpacity="0.92" />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
            <radialGradient id={`${uid}-flameHalo`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#ffd27a" stopOpacity="0.85" />
              <stop offset="55%"  stopColor="#ffaa3d" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#ffaa3d" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="25" rx="6" ry="1.2" fill="#000" opacity="0.28" />
          {/* Wax stub */}
          <rect x="46" y="14" width="8" height="10" fill={`url(#${uid}-wax)`} stroke="#1a1410" strokeWidth="0.55" />
          {/* Wax drip on the side */}
          <path d="M46 18 Q 44 21 45 24 Q 46.4 22 46 18 Z" fill={bodyHi} stroke="#1a1410" strokeWidth="0.3" strokeOpacity="0.55" />
          {/* Top wax rim shadow */}
          <rect x="46" y="14" width="8" height="0.8" fill={bodyShadow} opacity="0.55" />
          {/* Wick char-stub */}
          <line x1="50" y1="14" x2="50" y2="11" stroke="#1a1410" strokeWidth="0.65" strokeLinecap="round" />
          {/* Halo glow circle behind flame */}
          <circle cx="50" cy="6" r="8" fill={`url(#${uid}-flameHalo)`} className="hat-candle-glow" />
          {/* Outer flame at 0.5 opacity */}
          <path d="M50 14 C 44 6, 56 2, 50 -6 C 50 2, 44 6, 50 14 Z" fill="#ff7820" opacity="0.5" />
          {/* Main amber flame */}
          <path d="M50 14 C 46 8, 54 4, 50 -2 C 50 4, 46 10, 50 14 Z" fill="#ffaa3d" stroke="#ff7020" strokeWidth="0.3" />
          {/* Inner bright flame core */}
          <path d="M50 10 C 48 6, 52 4, 50 0 C 50 4, 48 7, 50 10 Z" fill="#fff4c2" opacity="0.85" />
          {/* Wax body highlight stripe */}
          <rect x="47" y="14.4" width="1" height="9" fill="#fff" opacity="0.45" />
        </>
      );
    },
  },
  // 8 crown-of-coals — Crimson Solstice premium signature (E3, tier 40).
  // Low brass-gold circlet (#ddc090) with three glowing red gems
  // (#ff4040). The gems hue-shift via .solstice-gem.
  //
  // Phase D Tier 4 — richSvg: 4-stop brass-coal gradient, 3 gemstone
  // mountings with claw prongs, gem layering (outer crimson + inner
  // bright + glint), engraved coal-vein filigree, ember sparks rising
  // from each gem on staggered phases, halo glow behind the crown.
  { id: 'crown-of-coals', label: 'Crown of Coals',
    path: 'M28 30 L 30 18 L 38 26 L 50 14 L 62 26 L 70 18 L 72 30 Z',
    accent: 'M40 24 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0 M50 18 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0 M60 24 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0',
    accentClass: 'solstice-gem',
    accentFill: '#ff4040',
    baseFill: '#ddc090',
    richSvg: ({ uid }) => {
      const crownPath = 'M28 30 L 30 18 L 38 26 L 50 14 L 62 26 L 70 18 L 72 30 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-brass`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#f4d8a0" />
              <stop offset="35%"  stopColor="#ddc090" />
              <stop offset="70%"  stopColor="#a07028" />
              <stop offset="100%" stopColor="#5a3a18" />
            </linearGradient>
            <radialGradient id={`${uid}-halo`} cx="50%" cy="60%" r="55%">
              <stop offset="0%"   stopColor="#ff4040" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#ff4040" stopOpacity="0" />
            </radialGradient>
            <filter id={`${uid}-drop`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" />
              <feOffset dx="0" dy="1.2" result="offset" />
              <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
              <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>
          {/* Halo glow behind the crown */}
          <ellipse cx="50" cy="24" rx="30" ry="14" fill={`url(#${uid}-halo)`} className="solstice-halo" />
          {/* Crown body with 4-stop brass gradient + drop shadow */}
          <g filter={`url(#${uid}-drop)`}>
            <path d={crownPath} fill={`url(#${uid}-brass)`} stroke="#1a1410" strokeWidth="0.7" />
            {/* Engraved coal-vein filigree across the band */}
            <path d="M30 28 Q 38 30 46 28 Q 54 30 62 28 Q 70 30 72 28" stroke="#5a3a18" strokeWidth="0.4" fill="none" opacity="0.7" />
            <path d="M34 22 L 38 24 M 60 24 L 64 22" stroke="#5a3a18" strokeWidth="0.4" opacity="0.6" />
          </g>
          {/* Gem mountings (claw prongs) at each gem position */}
          <path d="M38 24 L 40 22 L 42 24 M 48 16 L 50 14 L 52 16 M 58 24 L 60 22 L 62 24" stroke="#a07028" strokeWidth="0.5" fill="none" />
          {/* Gems — outer crimson + inner bright + white glint */}
          <g className="solstice-gem">
            {/* Left gem */}
            <circle cx="40" cy="24" r="2" fill="#ff4040" stroke="#5a0808" strokeWidth="0.4" />
            <circle cx="40" cy="24" r="1.3" fill="#ff7820" opacity="0.7" />
            <circle cx="39.4" cy="23.4" r="0.5" fill="#fff4c2" />
            {/* Center gem */}
            <circle cx="50" cy="18" r="2" fill="#ff4040" stroke="#5a0808" strokeWidth="0.4" />
            <circle cx="50" cy="18" r="1.3" fill="#ff7820" opacity="0.7" />
            <circle cx="49.4" cy="17.4" r="0.5" fill="#fff4c2" />
            {/* Right gem */}
            <circle cx="60" cy="24" r="2" fill="#ff4040" stroke="#5a0808" strokeWidth="0.4" />
            <circle cx="60" cy="24" r="1.3" fill="#ff7820" opacity="0.7" />
            <circle cx="59.4" cy="23.4" r="0.5" fill="#fff4c2" />
          </g>
          {/* Floating coal embers above each gem, staggered A/B/C */}
          <g className="crown-embers">
            <circle cx="40" cy="18" r="0.6" fill="#ffe6a8" className="crown-ember crown-ember-A" />
            <circle cx="50" cy="12" r="0.7" fill="#ffe6a8" className="crown-ember crown-ember-B" />
            <circle cx="60" cy="18" r="0.6" fill="#ffe6a8" className="crown-ember crown-ember-C" />
          </g>
        </>
      );
    },
  },
  // ── Phase E2 new hats (indices 9-14) ────────────────────────────────

  // 9 tricorn — three-pointed pirate hat. Wide curved brim with three
  // upturned points. Accent: gold-trim band on the front.
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop dark navy felt gradient,
  // gold band with buckle + 2 rivets, small skull medallion on front,
  // feather plume rising from one side with sway animation, brim
  // shadow under each point. ONE subtle plume sway via .hat-tricorn-plume.
  { id: 'tricorn', label: 'Tricorn',
    path: 'M14 32 C 14 18, 28 8, 50 8 C 72 8, 86 18, 86 32 L 78 28 L 70 14 L 60 24 L 50 6 L 40 24 L 30 14 L 22 28 Z',
    accent: 'M22 26 L 78 26 L 78 30 L 22 30 Z',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const tricornPath = 'M14 32 C 14 18, 28 8, 50 8 C 72 8, 86 18, 86 32 L 78 28 L 70 14 L 60 24 L 50 6 L 40 24 L 30 14 L 22 28 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-felt`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bodyHi} stopOpacity="0.85" />
              <stop offset="35%"  stopColor={bodyShadow} stopOpacity="0.9" />
              <stop offset="70%"  stopColor={bodyShadow} />
              <stop offset="100%" stopColor="#0a0608" />
            </linearGradient>
          </defs>
          {/* Drop shadow under brim */}
          <ellipse cx="50" cy="33" rx="36" ry="2.2" fill="#000" opacity="0.32" />
          {/* Feather plume rising from one side — sways slowly */}
          <g className="hat-tricorn-plume" style={{ transformOrigin: '32px 12px' }}>
            <path d="M32 12 Q 24 6 20 -2 Q 28 4 32 12 Z" fill={accentFill} stroke="#1a1410" strokeWidth="0.3" />
            <path d="M30 8 L 24 2" stroke="#1a1410" strokeWidth="0.3" opacity="0.55" />
            <path d="M28 10 L 22 4" stroke="#1a1410" strokeWidth="0.3" opacity="0.5" />
            <path d="M30 4 Q 26 0 28 -4" stroke={bodyHi} strokeWidth="0.3" fill="none" opacity="0.4" />
          </g>
          {/* Tricorn body with 4-stop felt gradient + ink outline */}
          <path d={tricornPath} fill={`url(#${uid}-felt)`} stroke="#1a1410" strokeWidth="0.7" strokeOpacity="0.9" />
          {/* Brim curl shadows under each point */}
          <path d="M30 14 L 22 28" stroke={bodyShadow} strokeWidth="0.6" opacity="0.55" fill="none" />
          <path d="M50 6 L 40 24" stroke={bodyShadow} strokeWidth="0.55" opacity="0.45" fill="none" />
          <path d="M70 14 L 78 28" stroke={bodyShadow} strokeWidth="0.6" opacity="0.55" fill="none" />
          {/* Gold band */}
          <rect x="22" y="26" width="56" height="4" fill="#a08438" stroke="#1a1410" strokeWidth="0.4" />
          <rect x="22" y="26.4" width="56" height="0.8" fill="#f5d680" opacity="0.65" />
          {/* Belt buckle — gold rect on center */}
          <rect x="46" y="26" width="8" height="4" fill="#f5d680" stroke="#1a1410" strokeWidth="0.4" />
          <rect x="48" y="27" width="4" height="2" fill="#1a1410" opacity="0.55" />
          {/* 2 rivets on band */}
          <circle cx="32" cy="28" r="0.8" fill="#1a1410" />
          <circle cx="68" cy="28" r="0.8" fill="#1a1410" />
          <circle cx="31.6" cy="27.6" r="0.3" fill="#fff4c2" opacity="0.7" />
          <circle cx="67.6" cy="27.6" r="0.3" fill="#fff4c2" opacity="0.7" />
          {/* Small skull medallion */}
          <g transform="translate(50 20)">
            <circle cx="0" cy="0" r="2.4" fill="#e8d8a8" stroke="#1a1410" strokeWidth="0.4" />
            <circle cx="-0.7" cy="-0.5" r="0.4" fill="#1a1410" />
            <circle cx="0.7" cy="-0.5" r="0.4" fill="#1a1410" />
            <path d="M-0.6 0.6 L 0 1.2 L 0.6 0.6" stroke="#1a1410" strokeWidth="0.25" fill="none" />
          </g>
          {/* Top-edge highlight */}
          <path d="M22 28 Q 50 12 78 28" stroke="#fff" strokeWidth="0.5" opacity="0.18" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 10 witch — tall pointed cone with wide circular brim. Accent: a
  // hat-band with buckle suggestion at the base of the cone.
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop deep purple cone gradient,
  // hat-band with moon emblem inside the buckle, cone tip curl,
  // brim shadow band, 4 tiny constellation stars on the cone with
  // staggered pulse animation via .hat-witch-star-A/B/C/D.
  { id: 'witch', label: 'Witch',
    path: 'M50 0 L 38 28 C 36 32, 64 32, 62 28 Z M 14 32 C 14 28, 86 28, 86 32 C 86 36, 14 36, 14 32 Z',
    accent: 'M40 28 L 60 28 L 60 32 L 40 32 Z M 48 30 L 52 30 L 52 32 L 48 32 Z',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const conePath = 'M50 0 L 38 28 C 36 32, 64 32, 62 28 Z';
      const brimPath = 'M 14 32 C 14 28, 86 28, 86 32 C 86 36, 14 36, 14 32 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-cone`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={bodyHi} stopOpacity="0.7" />
              <stop offset="35%"  stopColor={bodyShadow} stopOpacity="0.9" />
              <stop offset="70%"  stopColor={bodyShadow} />
              <stop offset="100%" stopColor="#0a0410" />
            </linearGradient>
          </defs>
          {/* Drop shadow under brim */}
          <ellipse cx="50" cy="36" rx="36" ry="2" fill="#000" opacity="0.32" />
          {/* Cone with 4-stop deep purple gradient + ink outline */}
          <path d={conePath} fill={`url(#${uid}-cone)`} stroke="#1a1410" strokeWidth="0.65" />
          {/* Cone tip curl (small bend at the top — reads as witch not wizard) */}
          <path d="M50 0 Q 54 -2 56 4" stroke="#1a1410" strokeWidth="0.8" fill="none" strokeLinecap="round" />
          {/* Cone tip cap (rounded) */}
          <circle cx="55" cy="3.5" r="1.4" fill={bodyShadow} stroke="#1a1410" strokeWidth="0.4" />
          {/* Brim */}
          <path d={brimPath} fill={`url(#${uid}-cone)`} stroke="#1a1410" strokeWidth="0.65" />
          {/* Brim shadow stripe underneath */}
          <path d="M14 33.4 Q 50 38 86 33.4" stroke={bodyShadow} strokeWidth="1.2" fill="none" opacity="0.55" />
          {/* Constellation stars on cone — staggered pulses */}
          <g>
            <circle cx="46" cy="10" r="0.6" fill="#ffd27a" className="hat-witch-star hat-witch-star-A" />
            <circle cx="54" cy="14" r="0.55" fill="#ffd27a" className="hat-witch-star hat-witch-star-B" />
            <circle cx="44" cy="20" r="0.6" fill="#ffd27a" className="hat-witch-star hat-witch-star-C" />
            <circle cx="55" cy="22" r="0.5" fill="#ffd27a" className="hat-witch-star hat-witch-star-D" />
          </g>
          {/* Hat band */}
          <rect x="40" y="28" width="20" height="4" fill="#1a1410" stroke="#1a1410" strokeWidth="0.4" />
          {/* Band highlight stripe */}
          <rect x="40" y="28.4" width="20" height="0.7" fill={accentFill} opacity="0.55" />
          {/* Buckle */}
          <rect x="46" y="28.4" width="8" height="3.2" fill="#a08438" stroke="#1a1410" strokeWidth="0.35" />
          {/* Moon emblem inside the buckle */}
          <path d="M50 29.2 a 1 1 0 1 0 0 2 a 0.7 0.7 0 0 1 0 -2 Z" fill="#fff8d8" />
          {/* Cone-side highlight stripe */}
          <path d="M44 6 L 41 28" stroke="#fff" strokeWidth="0.6" opacity="0.16" fill="none" strokeLinecap="round" />
        </>
      );
    },
  },
  // 11 brass-goggles — circular brass-strapped goggles worn on the
  // forehead. Two lens circles connected by a strap; accent: rivets.
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop brass gradient on lens
  // rims, tinted glass with radial reflection crescent in each lens,
  // 4 rivets per lens rim quadrant, center bridge buckle, dashed
  // stitching on side straps. Static (no animation — engineering item).
  { id: 'brass-goggles', label: 'Brass Goggles',
    path: 'M36 22 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0 M 64 22 m -10 0 a 10 10 0 1 0 20 0 a 10 10 0 1 0 -20 0 M 46 20 L 54 20 L 54 24 L 46 24 Z M 14 22 L 26 22 L 26 24 L 14 24 Z M 74 22 L 86 22 L 86 24 L 74 24 Z',
    accent: 'M36 22 m -5 0 a 5 5 0 1 0 10 0 a 5 5 0 1 0 -10 0 M 64 22 m -5 0 a 5 5 0 1 0 10 0 a 5 5 0 1 0 -10 0',
    richSvg: ({ uid, accentFill }) => {
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-brass`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#fff8d8" />
              <stop offset="30%"  stopColor="#f5d680" />
              <stop offset="65%"  stopColor="#a08438" />
              <stop offset="100%" stopColor="#5a4218" />
            </linearGradient>
            <radialGradient id={`${uid}-glass`} cx="35%" cy="35%" r="65%">
              <stop offset="0%"   stopColor="#dcecf8" stopOpacity="0.92" />
              <stop offset="55%"  stopColor={accentFill} stopOpacity="0.55" />
              <stop offset="100%" stopColor="#0a1428" stopOpacity="0.85" />
            </radialGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="34" rx="36" ry="1.6" fill="#000" opacity="0.28" />
          {/* Side straps */}
          <rect x="14" y="22" width="12" height="2" fill="#5a4218" stroke="#1a1410" strokeWidth="0.3" />
          <rect x="74" y="22" width="12" height="2" fill="#5a4218" stroke="#1a1410" strokeWidth="0.3" />
          {/* Side strap stitching (dashed) */}
          <line x1="15" y1="23" x2="25" y2="23" stroke="#fff" strokeWidth="0.3" strokeDasharray="0.8 1.2" opacity="0.7" />
          <line x1="75" y1="23" x2="85" y2="23" stroke="#fff" strokeWidth="0.3" strokeDasharray="0.8 1.2" opacity="0.7" />
          {/* Center strap (bridge) */}
          <rect x="46" y="20" width="8" height="4" fill="#5a4218" stroke="#1a1410" strokeWidth="0.4" />
          <rect x="46" y="20.4" width="8" height="0.8" fill="#a08438" />
          {/* Center buckle */}
          <rect x="48.4" y="21" width="3.2" height="2" fill="#f5d680" stroke="#1a1410" strokeWidth="0.3" />
          {/* Left lens rim — brass with 4-stop gradient */}
          <circle cx="36" cy="22" r="10" fill="none" stroke={`url(#${uid}-brass)`} strokeWidth="2.4" />
          <circle cx="36" cy="22" r="10" fill="none" stroke="#1a1410" strokeWidth="0.5" />
          {/* Left lens glass */}
          <circle cx="36" cy="22" r="8" fill={`url(#${uid}-glass)`} />
          {/* Left lens reflection crescent (top-left) */}
          <path d="M30 18 Q 32 14 38 16" stroke="#fff" strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round" />
          {/* Right lens rim — same brass */}
          <circle cx="64" cy="22" r="10" fill="none" stroke={`url(#${uid}-brass)`} strokeWidth="2.4" />
          <circle cx="64" cy="22" r="10" fill="none" stroke="#1a1410" strokeWidth="0.5" />
          {/* Right lens glass */}
          <circle cx="64" cy="22" r="8" fill={`url(#${uid}-glass)`} />
          {/* Right lens reflection crescent */}
          <path d="M58 18 Q 60 14 66 16" stroke="#fff" strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round" />
          {/* 4 rivets per lens rim (at quadrants) */}
          <circle cx="36" cy="12" r="0.8" fill="#1a1410" />
          <circle cx="46" cy="22" r="0.8" fill="#1a1410" />
          <circle cx="36" cy="32" r="0.8" fill="#1a1410" />
          <circle cx="26" cy="22" r="0.8" fill="#1a1410" />
          <circle cx="64" cy="12" r="0.8" fill="#1a1410" />
          <circle cx="74" cy="22" r="0.8" fill="#1a1410" />
          <circle cx="64" cy="32" r="0.8" fill="#1a1410" />
          <circle cx="54" cy="22" r="0.8" fill="#1a1410" />
          {/* Pinprick highlights on rivets */}
          <circle cx="35.7" cy="11.7" r="0.3" fill="#fff4c2" opacity="0.75" />
          <circle cx="45.7" cy="21.7" r="0.3" fill="#fff4c2" opacity="0.75" />
          <circle cx="63.7" cy="11.7" r="0.3" fill="#fff4c2" opacity="0.75" />
          <circle cx="73.7" cy="21.7" r="0.3" fill="#fff4c2" opacity="0.75" />
        </>
      );
    },
  },
  // 12 antlers — branched deer antlers, more elaborate than HAT_SHAPES[5]'s
  // simple V-prongs. Each side has 3 forks at staggered heights.
  //
  // Phase C Tier 3 — richSvg upgrade: 4-stop bone gradient on antlers,
  // gold circlet band with 3 inset gem circles, velvet wrap fluff at
  // antler bases, antler-tip glints, 2 leaf accents tucked into the
  // band. ONE subtle pulse on the circlet gems via .hat-antler-gem-A/B/C.
  { id: 'antler-circlet', label: 'Antler Circlet',
    path: 'M30 28 L 22 12 L 26 18 L 16 6 L 22 16 L 14 14 L 26 24 L 18 22 L 32 30 Z M 70 28 L 78 12 L 74 18 L 84 6 L 78 16 L 86 14 L 74 24 L 82 22 L 68 30 Z M 30 28 L 70 28 L 70 30 L 30 30 Z',
    accent: 'M32 28 L 68 28 L 68 30 L 32 30 Z',
    richSvg: ({ uid, bodyHi, bodyShadow, accentFill }) => {
      const antlerL = 'M30 28 L 22 12 L 26 18 L 16 6 L 22 16 L 14 14 L 26 24 L 18 22 L 32 30 Z';
      const antlerR = 'M70 28 L 78 12 L 74 18 L 84 6 L 78 16 L 86 14 L 74 24 L 82 22 L 68 30 Z';
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-bone`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#fff8e0" />
              <stop offset="30%"  stopColor="#e8d8a8" />
              <stop offset="70%"  stopColor="#a88a5a" />
              <stop offset="100%" stopColor="#5a3a18" />
            </linearGradient>
            <linearGradient id={`${uid}-gold`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#fff8d8" />
              <stop offset="35%"  stopColor="#f5d680" />
              <stop offset="70%"  stopColor="#a08438" />
              <stop offset="100%" stopColor="#5a4218" />
            </linearGradient>
          </defs>
          {/* Drop shadow */}
          <ellipse cx="50" cy="32" rx="36" ry="1.8" fill="#000" opacity="0.3" />
          {/* Velvet wrap fluff at antler bases */}
          <ellipse cx="30" cy="28" rx="3.6" ry="1.4" fill={bodyHi} opacity="0.55" />
          <ellipse cx="70" cy="28" rx="3.6" ry="1.4" fill={bodyHi} opacity="0.55" />
          {/* Antlers with 4-stop bone gradient + ink outline */}
          <path d={antlerL} fill={`url(#${uid}-bone)`} stroke="#1a1410" strokeWidth="0.55" />
          <path d={antlerR} fill={`url(#${uid}-bone)`} stroke="#1a1410" strokeWidth="0.55" />
          {/* Bone-ridge texture along main shafts */}
          <path d="M26 26 L 22 14" stroke={bodyShadow} strokeWidth="0.3" opacity="0.55" />
          <path d="M22 18 L 18 10" stroke={bodyShadow} strokeWidth="0.3" opacity="0.5" />
          <path d="M74 26 L 78 14" stroke={bodyShadow} strokeWidth="0.3" opacity="0.55" />
          <path d="M78 18 L 82 10" stroke={bodyShadow} strokeWidth="0.3" opacity="0.5" />
          {/* Antler-tip glints */}
          <circle cx="16" cy="6" r="0.7" fill="#fff4c2" opacity="0.85" />
          <circle cx="14" cy="14" r="0.5" fill="#fff4c2" opacity="0.75" />
          <circle cx="84" cy="6" r="0.7" fill="#fff4c2" opacity="0.85" />
          <circle cx="86" cy="14" r="0.5" fill="#fff4c2" opacity="0.75" />
          {/* Gold circlet band */}
          <rect x="30" y="28" width="40" height="2.4" fill={`url(#${uid}-gold)`} stroke="#1a1410" strokeWidth="0.4" />
          {/* Gold filigree engravings */}
          <path d="M32 29.2 Q 40 30 50 29.2 Q 60 30 68 29.2" stroke="#5a4218" strokeWidth="0.3" fill="none" opacity="0.65" />
          {/* 3 inset gem circles — staggered pulse */}
          <g>
            <g className="hat-antler-gem hat-antler-gem-A">
              <circle cx="36" cy="29.2" r="1.4" fill={accentFill} stroke="#1a1410" strokeWidth="0.3" />
              <circle cx="35.6" cy="28.8" r="0.45" fill="#fff" opacity="0.85" />
            </g>
            <g className="hat-antler-gem hat-antler-gem-B">
              <circle cx="50" cy="29.2" r="1.6" fill={accentFill} stroke="#1a1410" strokeWidth="0.3" />
              <circle cx="49.6" cy="28.8" r="0.5" fill="#fff" opacity="0.9" />
            </g>
            <g className="hat-antler-gem hat-antler-gem-C">
              <circle cx="64" cy="29.2" r="1.4" fill={accentFill} stroke="#1a1410" strokeWidth="0.3" />
              <circle cx="63.6" cy="28.8" r="0.45" fill="#fff" opacity="0.85" />
            </g>
          </g>
          {/* Leaf accents tucked into the band */}
          <path d="M42 28 Q 38 26 40 24 Q 42 26 42 28 Z" fill="#5a7838" stroke="#1a1410" strokeWidth="0.3" />
          <path d="M58 28 Q 62 26 60 24 Q 58 26 58 28 Z" fill="#5a7838" stroke="#1a1410" strokeWidth="0.3" />
          {/* Leaf vein */}
          <path d="M40 25 L 42 27" stroke="#3a5028" strokeWidth="0.3" opacity="0.7" />
          <path d="M60 25 L 58 27" stroke="#3a5028" strokeWidth="0.3" opacity="0.7" />
          {/* Band top highlight */}
          <rect x="30" y="28.2" width="40" height="0.5" fill="#fff4c2" opacity="0.55" />
        </>
      );
    },
  },
  // 13 halo — glowing ring above the head. Accent ring is the bright
  // inner glow band; CSS animation .hat-halo pulses the outer glow.
  //
  // Phase D Tier 4 — richSvg: 5 concentric glow rings, soft feGaussianBlur
  // bloom on the outer halo, an inner shimmer arc rotating @ 8s, 5
  // orbiting light motes, 3 sparkle bursts scale-pulsing on staggered
  // phases.
  { id: 'halo', label: 'Halo',
    path: 'M50 16 m -18 0 a 18 6 0 1 0 36 0 a 18 6 0 1 0 -36 0 M 50 16 m -14 0 a 14 4 0 1 0 28 0 a 14 4 0 1 0 -28 0',
    accent: 'M50 16 m -10 0 a 10 3 0 1 0 20 0 a 10 3 0 1 0 -20 0',
    accentClass: 'hat-halo',
    accentFill: '#fff4c2',
    richSvg: ({ uid }) => {
      return (
        <>
          <defs>
            <filter id={`${uid}-bloom`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.2" />
            </filter>
            <radialGradient id={`${uid}-glow`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#fff4c2" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#ffaa3d" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Outer bloom ring (blurred) */}
          <g filter={`url(#${uid}-bloom)`} className="halo-bloom">
            <ellipse cx="50" cy="16" rx="22" ry="7" fill="none" stroke="#fff4c2" strokeWidth="1.5" opacity="0.4" />
          </g>
          {/* 5 concentric rings, brightness increasing inward */}
          <ellipse cx="50" cy="16" rx="20" ry="6.4" fill="none" stroke="#ffaa3d" strokeWidth="0.7" opacity="0.7" />
          <ellipse cx="50" cy="16" rx="17" ry="5.2" fill="none" stroke="#ffe6a8" strokeWidth="0.8" opacity="0.8" />
          <ellipse cx="50" cy="16" rx="14" ry="4.2" fill="none" stroke="#fff4c2" strokeWidth="0.9" opacity="0.9" />
          <ellipse cx="50" cy="16" rx="11" ry="3.2" fill={`url(#${uid}-glow)`} />
          {/* Bright pulsing inner band (existing .hat-halo class) */}
          <g className="hat-halo">
            <ellipse cx="50" cy="16" rx="10" ry="3" fill="#fff4c2" />
          </g>
          {/* Rotating shimmer arc — bright crescent inside the halo */}
          <g className="halo-shimmer" style={{ transformOrigin: '50px 16px' }}>
            <path d="M40 16 A 10 3 0 0 1 60 16" fill="none" stroke="#ffffff" strokeWidth="0.8" opacity="0.9" />
          </g>
          {/* 5 orbiting light motes (slow drift via .halo-mote) */}
          <g className="halo-motes" style={{ transformOrigin: '50px 16px' }}>
            <circle cx="32" cy="16" r="0.5" fill="#fff4c2" className="halo-mote halo-mote-A" />
            <circle cx="68" cy="16" r="0.5" fill="#fff4c2" className="halo-mote halo-mote-B" />
            <circle cx="50" cy="9"  r="0.5" fill="#fff4c2" className="halo-mote halo-mote-C" />
            <circle cx="42" cy="22" r="0.4" fill="#fff4c2" className="halo-mote halo-mote-D" />
            <circle cx="58" cy="22" r="0.4" fill="#fff4c2" className="halo-mote halo-mote-E" />
          </g>
          {/* 3 sparkle bursts — 4-point stars scale-pulsing */}
          <g className="halo-sparkles">
            <path d="M36 12 L 36.6 14 L 38 14.5 L 36.6 15 L 36 17 L 35.4 15 L 34 14.5 L 35.4 14 Z" fill="#fff4c2" className="halo-sparkle halo-sparkle-A" />
            <path d="M50 6 L 50.6 8 L 52 8.5 L 50.6 9 L 50 11 L 49.4 9 L 48 8.5 L 49.4 8 Z" fill="#fff4c2" className="halo-sparkle halo-sparkle-B" />
            <path d="M64 12 L 64.6 14 L 66 14.5 L 64.6 15 L 64 17 L 63.4 15 L 62 14.5 L 63.4 14 Z" fill="#fff4c2" className="halo-sparkle halo-sparkle-C" />
          </g>
        </>
      );
    },
  },
  // 14 candle-cap — a single tall candle worn as a hat. Accent: tiny
  // flame at the top. Distinct from BODY_SHAPES living-candle (which
  // is the entire avatar).
  //
  // Phase B Tier 2 — drop-shadow + 3-stop wax gradient + ink outline +
  // accent layer: wax drip teardrop + flame core highlight + soft halo
  // behind flame + wick char-stub. Existing .hat-candle-cap class is
  // preserved on the flame group so its glow animation still plays.
  { id: 'candle-cap', label: 'Candle Cap',
    path: 'M44 30 L 56 30 L 56 14 L 44 14 Z M 38 30 L 62 30 L 62 32 L 38 32 Z',
    accent: 'M50 14 C 46 8, 54 4, 50 -2 C 50 4, 46 10, 50 14 Z',
    accentClass: 'hat-candle-cap',
    accentFill: '#ffaa3d',
    richSvg: ({ uid, bodyHi, bodyShadow }) => {
      return (
        <>
          <defs>
            <linearGradient id={`${uid}-wax`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor={bodyShadow} />
              <stop offset="50%"  stopColor={bodyHi} />
              <stop offset="100%" stopColor={bodyShadow} />
            </linearGradient>
            <radialGradient id={`${uid}-flameHalo`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#ffd27a" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ffaa3d" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Drop shadow under brim */}
          <ellipse cx="50" cy="32.4" rx="14" ry="1.8" fill="#000" opacity="0.28" />
          {/* Brim/base */}
          <rect x="38" y="30" width="24" height="2.4" fill="#1a1410" opacity="0.85" stroke="#1a1410" strokeWidth="0.4" />
          {/* Candle wax cylinder with 3-stop gradient + ink outline */}
          <rect x="44" y="14" width="12" height="16" fill={`url(#${uid}-wax)`} stroke="#1a1410" strokeWidth="0.6" />
          {/* Wax drip on side */}
          <path d="M44 22 Q 42 24 43 27 Q 44.4 25 44 22 Z" fill={bodyHi} stroke="#1a1410" strokeWidth="0.35" />
          {/* Top wax rim shadow */}
          <rect x="44" y="14" width="12" height="1.2" fill={bodyShadow} opacity="0.5" />
          {/* Wick char-stub */}
          <line x1="50" y1="14" x2="50" y2="12" stroke="#1a1410" strokeWidth="0.7" strokeLinecap="round" />
          {/* Flame halo */}
          <circle cx="50" cy="6" r="6" fill={`url(#${uid}-flameHalo)`} />
          {/* Flame — preserves existing .hat-candle-cap class for animation */}
          <g className="hat-candle-cap">
            <path d="M50 14 C 46 8, 54 4, 50 -2 C 50 4, 46 10, 50 14 Z" fill="#ffaa3d" stroke="#1a1410" strokeWidth="0.35" />
            {/* Inner bright flame core */}
            <path d="M50 10 C 48 6, 52 4, 50 1 C 50 4, 48 7, 50 10 Z" fill="#fff4c2" opacity="0.85" />
          </g>
        </>
      );
    },
  },
];

export const FACE_SHAPES: FaceShape[] = [
  // 0 dot eyes + smile — default Tier 1.
  //
  // Phase A Tier 1 — restrained richSvg upgrade: glassy white highlight
  // inside each eye, mouth drop-shadow under-stroke, radial-gradient
  // blush dots. Eyes still use feature color so palettes carry through.
  { id: 'happy', label: 'Happy',
    eyes: [{ x: 40, y: 50, r: 2.4 }, { x: 60, y: 50, r: 2.4 }],
    mouth: 'M40 64 Q 50 72 60 64', blush: [33, 60, 67],
    richSvg: ({ uid, featureFill, accentFill, bodyShadow }) => (
      <>
        <defs>
          <radialGradient id={`${uid}-blush`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor={accentFill} stopOpacity="0.85" />
            <stop offset="100%" stopColor={accentFill} stopOpacity="0.25" />
          </radialGradient>
        </defs>
        {/* Mouth shadow — slight depth under the smile */}
        <path d="M40 64 Q 50 72 60 64" fill="none" stroke={bodyShadow} strokeWidth="1.8" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* Smile */}
        <path d="M40 64 Q 50 72 60 64" fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        {/* Eyes with glassy highlight */}
        <circle cx="40" cy="50" r="2.4" fill={featureFill} />
        <circle cx="60" cy="50" r="2.4" fill={featureFill} />
        <circle cx="39.3" cy="49.3" r="0.7" fill="#fff4c2" opacity="0.85" />
        <circle cx="59.3" cy="49.3" r="0.7" fill="#fff4c2" opacity="0.85" />
        {/* Cheek blush — radial-gradient */}
        <g opacity="0.55">
          <circle cx="33" cy="60" r="3" fill={`url(#${uid}-blush)`} />
          <circle cx="67" cy="60" r="3" fill={`url(#${uid}-blush)`} />
        </g>
      </>
    ),
  },
  // 1 sleepy eyes — default Tier 1.
  //
  // Phase A Tier 1 — restrained richSvg upgrade: small eyelash strokes
  // from each eye corner downward (suggests closed lids), mouth shadow.
  { id: 'sleepy', label: 'Sleepy',
    eyes: [{ x: 40, y: 52, r: 1.4 }, { x: 60, y: 52, r: 1.4 }],
    mouth: 'M44 66 Q 50 64 56 66',
    richSvg: ({ featureFill, bodyShadow }) => (
      <>
        {/* Mouth shadow */}
        <path d="M44 66 Q 50 64 56 66" fill="none" stroke={bodyShadow} strokeWidth="1.8" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* Mouth */}
        <path d="M44 66 Q 50 64 56 66" fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        {/* Eyes — small dots */}
        <circle cx="40" cy="52" r="1.4" fill={featureFill} />
        <circle cx="60" cy="52" r="1.4" fill={featureFill} />
        {/* Eyelash hint strokes — outer corners drooping */}
        <path d="M37 53 L 35 55" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.85" />
        <path d="M43 53 L 45 55" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.85" />
        <path d="M57 53 L 55 55" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.85" />
        <path d="M63 53 L 65 55" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.85" />
      </>
    ),
  },
  // 2 wide eyes surprised — default Tier 1.
  //
  // Phase A Tier 1 — restrained richSvg upgrade: glassy white highlight
  // in each eye + a thin outer rim to anchor the silhouette, mouth shadow.
  { id: 'wide', label: 'Wide',
    eyes: [{ x: 40, y: 50, r: 3.2 }, { x: 60, y: 50, r: 3.2 }],
    mouth: 'M46 66 Q 50 70 54 66',
    richSvg: ({ featureFill, bodyShadow }) => (
      <>
        {/* Mouth shadow */}
        <path d="M46 66 Q 50 70 54 66" fill="none" stroke={bodyShadow} strokeWidth="1.8" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* Mouth */}
        <path d="M46 66 Q 50 70 54 66" fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        {/* Eyes — large dots with rim + highlight */}
        <circle cx="40" cy="50" r="3.4" fill="none" stroke={featureFill} strokeWidth="0.4" opacity="0.5" />
        <circle cx="60" cy="50" r="3.4" fill="none" stroke={featureFill} strokeWidth="0.4" opacity="0.5" />
        <circle cx="40" cy="50" r="3.2" fill={featureFill} />
        <circle cx="60" cy="50" r="3.2" fill={featureFill} />
        <circle cx="38.9" cy="48.9" r="0.95" fill="#fff4c2" opacity="0.9" />
        <circle cx="58.9" cy="48.9" r="0.95" fill="#fff4c2" opacity="0.9" />
      </>
    ),
  },
  // 3 angry slits
  //
  // Phase B Tier 2 — furrowed brow strokes above each eye + brow crease
  // between + eye glassy highlights + tight mouth with shadow.
  { id: 'angry', label: 'Stern',
    eyes: [{ x: 40, y: 50, r: 1.8 }, { x: 60, y: 50, r: 1.8 }],
    mouth: 'M42 66 L 58 66',
    richSvg: ({ featureFill, bodyShadow }) => (
      <>
        {/* Furrowed brow strokes — angled downward toward center */}
        <path d="M34 44 L 42 47" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M66 44 L 58 47" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        {/* Brow crease between (small vertical line) */}
        <path d="M50 45 L 50 48" stroke={bodyShadow} strokeWidth="0.7" strokeLinecap="round" opacity="0.75" />
        {/* Mouth shadow */}
        <path d="M42 66 L 58 66" fill="none" stroke={bodyShadow} strokeWidth="2" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* Tight pursed mouth */}
        <path d="M42 66 L 58 66" fill="none" stroke={featureFill} strokeWidth="1.8" strokeLinecap="round" />
        {/* Eyes with glassy highlight */}
        <circle cx="40" cy="50" r="1.8" fill={featureFill} />
        <circle cx="60" cy="50" r="1.8" fill={featureFill} />
        <circle cx="39.4" cy="49.4" r="0.55" fill="#fff4c2" opacity="0.8" />
        <circle cx="59.4" cy="49.4" r="0.55" fill="#fff4c2" opacity="0.8" />
        {/* Subtle cheek shading */}
        <ellipse cx="32" cy="60" rx="2.4" ry="1.6" fill={bodyShadow} opacity="0.22" />
        <ellipse cx="68" cy="60" rx="2.4" ry="1.6" fill={bodyShadow} opacity="0.22" />
      </>
    ),
  },
  // 4 cute round
  //
  // Phase B Tier 2 — large eye highlights ("starry-eye" with extra small
  // glint) + radial-gradient blush dots + mouth shadow.
  { id: 'cute', label: 'Cute',
    eyes: [{ x: 40, y: 50, r: 2.8 }, { x: 60, y: 50, r: 2.8 }],
    mouth: 'M46 66 Q 50 70 54 66', blush: [32, 60, 68],
    richSvg: ({ uid, featureFill, accentFill, bodyShadow }) => (
      <>
        <defs>
          <radialGradient id={`${uid}-blush`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor={accentFill} stopOpacity="0.9" />
            <stop offset="100%" stopColor={accentFill} stopOpacity="0.25" />
          </radialGradient>
        </defs>
        {/* Mouth shadow */}
        <path d="M46 66 Q 50 70 54 66" fill="none" stroke={bodyShadow} strokeWidth="1.8" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* Mouth */}
        <path d="M46 66 Q 50 70 54 66" fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        {/* Eyes — large dots */}
        <circle cx="40" cy="50" r="2.8" fill={featureFill} />
        <circle cx="60" cy="50" r="2.8" fill={featureFill} />
        {/* Primary glassy highlights */}
        <circle cx="39.1" cy="49.1" r="0.95" fill="#fff4c2" opacity="0.95" />
        <circle cx="59.1" cy="49.1" r="0.95" fill="#fff4c2" opacity="0.95" />
        {/* Secondary starry-eye glints */}
        <circle cx="41" cy="50.6" r="0.5" fill="#ffffff" opacity="0.9" />
        <circle cx="61" cy="50.6" r="0.5" fill="#ffffff" opacity="0.9" />
        {/* Blush with radial gradient */}
        <g opacity="0.65">
          <circle cx="32" cy="60" r="3.4" fill={`url(#${uid}-blush)`} />
          <circle cx="68" cy="60" r="3.4" fill={`url(#${uid}-blush)`} />
        </g>
        {/* Tiny 3-point sparkle inside each blush */}
        <path d="M32 60 L 32.4 61 L 31.4 60.4 L 33 60 Z" fill="#fff4c2" opacity="0.8" />
        <path d="M68 60 L 68.4 61 L 67.4 60.4 L 69 60 Z" fill="#fff4c2" opacity="0.8" />
      </>
    ),
  },
  // 5 mischievous
  //
  // Phase B Tier 2 — half-lidded eyelid hints over each eye + mouth shadow
  // + tiny tooth nub at the corner of the mouth + eye glassy highlights.
  { id: 'mischief', label: 'Mischief',
    eyes: [{ x: 40, y: 50, r: 2 }, { x: 60, y: 50, r: 2 }],
    mouth: 'M40 64 Q 45 70 50 66 Q 55 62 60 68',
    richSvg: ({ featureFill, bodyShadow }) => (
      <>
        {/* Mouth shadow */}
        <path d="M40 64 Q 45 70 50 66 Q 55 62 60 68" fill="none" stroke={bodyShadow} strokeWidth="1.8" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* S-curve mischievous mouth */}
        <path d="M40 64 Q 45 70 50 66 Q 55 62 60 68" fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        {/* Tooth nub at one corner */}
        <path d="M58 67 L 59 68.4 L 60 67 Z" fill="#fff8d8" stroke={featureFill} strokeWidth="0.3" />
        {/* Eyes */}
        <circle cx="40" cy="50" r="2" fill={featureFill} />
        <circle cx="60" cy="50" r="2" fill={featureFill} />
        {/* Eye glassy highlights */}
        <circle cx="39.4" cy="49.4" r="0.55" fill="#fff4c2" opacity="0.85" />
        <circle cx="59.4" cy="49.4" r="0.55" fill="#fff4c2" opacity="0.85" />
        {/* Half-lidded eyelid hints (curve covering upper half of each eye) */}
        <path d="M37 48.4 Q 40 47 43 48.4" stroke={featureFill} strokeWidth="0.6" fill="none" strokeLinecap="round" opacity="0.85" />
        <path d="M57 48.4 Q 60 47 63 48.4" stroke={featureFill} strokeWidth="0.6" fill="none" strokeLinecap="round" opacity="0.85" />
        {/* Brow-tilt strokes (mischievous angle) */}
        <path d="M36 46 L 42 45" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.7" />
        <path d="M64 46 L 58 45" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.7" />
      </>
    ),
  },
  // 6 sad
  //
  // Phase B Tier 2 — eye glassy highlights + eyelid droop strokes +
  // a static teardrop at one eye corner + mouth shadow.
  { id: 'sad', label: 'Sad',
    eyes: [{ x: 40, y: 50, r: 2 }, { x: 60, y: 50, r: 2 }],
    mouth: 'M40 70 Q 50 62 60 70',
    richSvg: ({ featureFill, bodyShadow }) => (
      <>
        {/* Mouth shadow */}
        <path d="M40 70 Q 50 62 60 70" fill="none" stroke={bodyShadow} strokeWidth="1.8" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* Frown mouth */}
        <path d="M40 70 Q 50 62 60 70" fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        {/* Eyes */}
        <circle cx="40" cy="50" r="2" fill={featureFill} />
        <circle cx="60" cy="50" r="2" fill={featureFill} />
        {/* Eye glassy highlights */}
        <circle cx="39.4" cy="49.4" r="0.55" fill="#fff4c2" opacity="0.85" />
        <circle cx="59.4" cy="49.4" r="0.55" fill="#fff4c2" opacity="0.85" />
        {/* Drooping eyelid strokes (curve over upper portion) */}
        <path d="M37 48 Q 40 49.2 43 48" stroke={featureFill} strokeWidth="0.6" fill="none" strokeLinecap="round" opacity="0.85" />
        <path d="M57 48 Q 60 49.2 63 48" stroke={featureFill} strokeWidth="0.6" fill="none" strokeLinecap="round" opacity="0.85" />
        {/* Sloped brow strokes (sad-tilt: outer-high, inner-low) */}
        <path d="M34 45 L 42 47" stroke={featureFill} strokeWidth="0.6" strokeLinecap="round" opacity="0.7" />
        <path d="M66 45 L 58 47" stroke={featureFill} strokeWidth="0.6" strokeLinecap="round" opacity="0.7" />
        {/* Teardrop at one eye corner (static) */}
        <path d="M38 54 Q 36 58 38 62 Q 40 58 38 54 Z" fill="#9be0f0" stroke="#3a6090" strokeWidth="0.3" opacity="0.9" />
        {/* Tear highlight */}
        <circle cx="37.6" cy="57" r="0.5" fill="#fff" opacity="0.7" />
      </>
    ),
  },
  // 7 cyclops
  //
  // Phase C Tier 3 — richSvg upgrade: iris ring (outer dark + inner
  // accent), eye highlight glint, 5 eyelash strokes radiating outward,
  // small brow ridge arc above. Static (the eye is the focal point).
  { id: 'cyclops', label: 'Cyclops',
    eyes: [{ x: 50, y: 50, r: 4.4 }],
    mouth: 'M44 66 Q 50 70 56 66',
    richSvg: ({ featureFill, accentFill, bodyShadow }) => (
      <>
        {/* Brow ridge arc */}
        <path d="M42 42 Q 50 38 58 42" stroke={featureFill} strokeWidth="0.7" fill="none" strokeLinecap="round" opacity="0.85" />
        {/* Eye outer iris ring */}
        <circle cx="50" cy="50" r="4.4" fill={bodyShadow} stroke={featureFill} strokeWidth="0.5" opacity="0.95" />
        {/* Inner accent iris */}
        <circle cx="50" cy="50" r="3.2" fill={accentFill} opacity="0.85" />
        {/* Dark pupil */}
        <circle cx="50" cy="50" r="1.6" fill="#1a1410" />
        {/* Bright glint top-left */}
        <circle cx="49.2" cy="49.2" r="0.7" fill="#fff4c2" opacity="0.95" />
        {/* Secondary tiny glint */}
        <circle cx="51" cy="51" r="0.35" fill="#fff" opacity="0.85" />
        {/* 5 eyelash strokes radiating outward */}
        <path d="M45 44 L 44 41" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.85" />
        <path d="M48 44 L 47 40.5" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.85" />
        <path d="M50 44 L 50 40.4" stroke={featureFill} strokeWidth="0.55" strokeLinecap="round" opacity="0.9" />
        <path d="M52 44 L 53 40.5" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.85" />
        <path d="M55 44 L 56 41" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.85" />
        {/* Mouth shadow */}
        <path d="M44 66 Q 50 70 56 66" fill="none" stroke={bodyShadow} strokeWidth="1.8" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* Mouth */}
        <path d="M44 66 Q 50 70 56 66" fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        {/* Subtle bottom-lid hint */}
        <path d="M46 54 Q 50 55.4 54 54" stroke={featureFill} strokeWidth="0.4" fill="none" strokeLinecap="round" opacity="0.65" />
      </>
    ),
  },
  // 8 searing-mark — Crimson Solstice premium signature (E3, tier 60,
  // mythic). Crimson glowing eyes that subtly blink via .solstice-eye,
  // plus a vertical brass-gold scar (#ddc090) running across the face.
  //
  // Phase D Tier 4 — richSvg: glowing eye cores (crimson outer + bright
  // inner + white glint), feGaussianBlur outer eye halos pulsing, scar
  // layering (dark inset stroke + glowing crimson edge + 3 ember beads),
  // scar shimmer animation. Palette-locked Crimson Solstice fills.
  { id: 'searing-mark', label: 'Searing Mark',
    eyes: [{ x: 40, y: 50, r: 2.8 }, { x: 60, y: 50, r: 2.8 }],
    mouth: 'M42 66 L 58 66',
    eyeFill: '#ff4040',
    eyeClass: 'solstice-eye',
    markPath: 'M48 36 L 52 36 L 52 72 L 48 72 Z',
    markFill: '#ddc090',
    richSvg: ({ uid }) => {
      return (
        <>
          <defs>
            <filter id={`${uid}-blur`} x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="1.2" />
            </filter>
            <radialGradient id={`${uid}-eyeglow`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#ff4040" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#ff4040" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Outer eye halos — pulse via .searing-glow */}
          <g className="searing-glow">
            <circle cx="40" cy="50" r="5" fill={`url(#${uid}-eyeglow)`} />
            <circle cx="60" cy="50" r="5" fill={`url(#${uid}-eyeglow)`} />
          </g>
          {/* Eyes — outer crimson + inner bright core + glint */}
          <g className="solstice-eye">
            <circle cx="40" cy="50" r="2.8" fill="#ff4040" />
            <circle cx="40" cy="50" r="1.4" fill="#ffaa3d" />
            <circle cx="40" cy="50" r="0.7" fill="#fff4c2" />
            <circle cx="60" cy="50" r="2.8" fill="#ff4040" />
            <circle cx="60" cy="50" r="1.4" fill="#ffaa3d" />
            <circle cx="60" cy="50" r="0.7" fill="#fff4c2" />
          </g>
          {/* Mouth — flat line */}
          <path d="M42 66 L 58 66" fill="none" stroke="#280404" strokeWidth="1.6" strokeLinecap="round" />
          {/* Scar — layered brass-gold with crimson glow edge + ember beads */}
          <g className="searing-scar">
            {/* Outer gold scar */}
            <path d="M48 36 L 52 36 L 52 72 L 48 72 Z" fill="#ddc090" stroke="#1a1410" strokeWidth="0.3" />
            {/* Dark inset center stroke */}
            <line x1="50" y1="38" x2="50" y2="70" stroke="#5a3a18" strokeWidth="0.6" opacity="0.6" />
            {/* Glowing crimson hairline along one edge */}
            <line x1="48.5" y1="38" x2="48.5" y2="70" stroke="#ff4040" strokeWidth="0.5" opacity="0.9" />
            {/* 3 ember beads */}
            <circle cx="50" cy="44" r="0.5" fill="#ff7820" />
            <circle cx="50" cy="54" r="0.5" fill="#ff7820" />
            <circle cx="50" cy="64" r="0.5" fill="#ff7820" />
          </g>
        </>
      );
    },
  },
  // ── Phase E2 new faces (indices 9-12) ───────────────────────────────

  // 9 single-eye — a giant centered cyclops eye, more pronounced than
  // FACE_SHAPES[7]'s "Cyclops". Includes a small iris-mark beneath
  // (markPath drawn over the face).
  //
  // Phase C Tier 3 — richSvg upgrade: 2-band iris ring (outer dark +
  // inner accent), pupil with white glint, 5 vein lines radiating from
  // pupil to eye edge, bottom-lid hint, slow blink animation via
  // .face-single-eye-blink (rare opacity dip).
  { id: 'single-eye', label: 'Single Eye',
    eyes: [{ x: 50, y: 50, r: 6.4 }],
    mouth: 'M44 68 Q 50 72 56 68',
    markPath: 'M48 48 m -2 0 a 2 2 0 1 0 4 0 a 2 2 0 1 0 -4 0',
    markFill: '#1a1410',
    richSvg: ({ uid, featureFill, accentFill, bodyShadow }) => (
      <>
        <defs>
          <radialGradient id={`${uid}-iris`} cx="50%" cy="50%" r="55%">
            <stop offset="0%"   stopColor={accentFill} stopOpacity="0.95" />
            <stop offset="65%"  stopColor={accentFill} stopOpacity="0.6" />
            <stop offset="100%" stopColor={bodyShadow} stopOpacity="0.95" />
          </radialGradient>
        </defs>
        {/* Eye outer ring (sclera shadow) */}
        <circle cx="50" cy="50" r="6.6" fill={featureFill} stroke="#1a1410" strokeWidth="0.5" opacity="0.95" />
        {/* Slow-blink wrapper */}
        <g className="face-single-eye-blink">
          {/* Sclera (white-ish) */}
          <circle cx="50" cy="50" r="6.4" fill="#f5e8d0" />
          {/* Vein lines radiating from pupil to edge */}
          <path d="M48 50 L 44.6 50" stroke="#a02418" strokeWidth="0.25" opacity="0.45" />
          <path d="M52 50 L 55.4 50" stroke="#a02418" strokeWidth="0.25" opacity="0.45" />
          <path d="M48 48.5 L 45 46" stroke="#a02418" strokeWidth="0.22" opacity="0.4" />
          <path d="M52 48.5 L 55 46" stroke="#a02418" strokeWidth="0.22" opacity="0.4" />
          <path d="M50 52 L 51 55" stroke="#a02418" strokeWidth="0.22" opacity="0.4" />
          {/* Outer iris band */}
          <circle cx="50" cy="50" r="4" fill={`url(#${uid}-iris)`} stroke="#1a1410" strokeWidth="0.3" />
          {/* Inner iris ring */}
          <circle cx="50" cy="50" r="2.6" fill={accentFill} stroke="#1a1410" strokeWidth="0.3" opacity="0.85" />
          {/* Pupil */}
          <circle cx="50" cy="50" r="1.6" fill="#1a1410" />
          {/* Bright white glint */}
          <circle cx="49" cy="49" r="0.7" fill="#fff4c2" opacity="0.95" />
          {/* Tiny secondary glint */}
          <circle cx="51" cy="51" r="0.35" fill="#fff" opacity="0.85" />
        </g>
        {/* Bottom lid hint stroke */}
        <path d="M44 54 Q 50 56 56 54" stroke={featureFill} strokeWidth="0.55" fill="none" strokeLinecap="round" opacity="0.7" />
        {/* Brow shadow */}
        <path d="M42 42 Q 50 39 58 42" stroke={featureFill} strokeWidth="0.6" fill="none" strokeLinecap="round" opacity="0.75" />
        {/* Mouth shadow */}
        <path d="M44 68 Q 50 72 56 68" fill="none" stroke={bodyShadow} strokeWidth="1.8" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* Mouth */}
        <path d="M44 68 Q 50 72 56 68" fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
      </>
    ),
  },
  // 10 third-eye — three eyes in a triangle pattern. The middle/upper
  // eye is decorated as a "third eye" via markPath glow.
  //
  // Phase C Tier 3 — richSvg upgrade: 3 eyes with standard highlights,
  // third eye gets larger surrounding violet glow halo, 4 mystical
  // aura points at compass positions, thin connecting lines from third
  // eye to lower eyes ("linked sight"). Preserves existing
  // .face-third-eye class on the third-eye for pulse animation.
  { id: 'third-eye', label: 'Third Eye',
    eyes: [{ x: 38, y: 52, r: 2.2 }, { x: 62, y: 52, r: 2.2 }, { x: 50, y: 40, r: 2.6 }],
    mouth: 'M42 66 Q 50 70 58 66',
    markPath: 'M50 40 m -4 0 a 4 4 0 1 0 8 0 a 4 4 0 1 0 -8 0',
    markFill: '#a070ff',
    eyeClass: 'face-third-eye',
    richSvg: ({ uid, featureFill, bodyShadow }) => (
      <>
        <defs>
          <radialGradient id={`${uid}-thirdGlow`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#a070ff" stopOpacity="0.7" />
            <stop offset="60%"  stopColor="#a070ff" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#a070ff" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Mouth shadow */}
        <path d="M42 66 Q 50 70 58 66" fill="none" stroke={bodyShadow} strokeWidth="1.8" strokeLinecap="round" opacity="0.4" transform="translate(0 0.6)" />
        {/* Mouth */}
        <path d="M42 66 Q 50 70 58 66" fill="none" stroke={featureFill} strokeWidth="1.6" strokeLinecap="round" />
        {/* Connecting lines from third eye to lower eyes — linked-sight curves */}
        <path d="M48 42 Q 42 48 38 50" stroke="#a070ff" strokeWidth="0.4" fill="none" opacity="0.55" strokeLinecap="round" />
        <path d="M52 42 Q 58 48 62 50" stroke="#a070ff" strokeWidth="0.4" fill="none" opacity="0.55" strokeLinecap="round" />
        {/* Third eye glow halo */}
        <circle cx="50" cy="40" r="7" fill={`url(#${uid}-thirdGlow)`} className="face-third-eye" />
        {/* Standard eyes with highlights */}
        <circle cx="38" cy="52" r="2.2" fill={featureFill} />
        <circle cx="62" cy="52" r="2.2" fill={featureFill} />
        <circle cx="37.4" cy="51.4" r="0.65" fill="#fff4c2" opacity="0.9" />
        <circle cx="61.4" cy="51.4" r="0.65" fill="#fff4c2" opacity="0.9" />
        {/* Third eye base */}
        <circle cx="50" cy="40" r="2.8" fill="#a070ff" stroke="#1a1410" strokeWidth="0.4" />
        <circle cx="50" cy="40" r="1.6" fill="#1a1410" />
        <circle cx="49.4" cy="39.4" r="0.55" fill="#fff" opacity="0.95" />
        {/* 4 mystical aura sparkle points at compass positions */}
        <g>
          <circle cx="50" cy="32" r="0.5" fill="#dcc8ff" className="face-third-eye-aura face-third-eye-aura-A" />
          <circle cx="58" cy="40" r="0.5" fill="#dcc8ff" className="face-third-eye-aura face-third-eye-aura-B" />
          <circle cx="50" cy="48" r="0.5" fill="#dcc8ff" className="face-third-eye-aura face-third-eye-aura-C" />
          <circle cx="42" cy="40" r="0.5" fill="#dcc8ff" className="face-third-eye-aura face-third-eye-aura-D" />
        </g>
        {/* Subtle brow strokes over lower eyes */}
        <path d="M34 48 L 40 47" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.65" />
        <path d="M60 47 L 66 48" stroke={featureFill} strokeWidth="0.5" strokeLinecap="round" opacity="0.65" />
      </>
    ),
  },
  // 11 calligraphic — stylized brushstroke eyes (curved ink strokes
  // instead of round dots). Eyes array is empty; the markPath carries
  // the brushstroke glyphs so they render in feature color.
  //
  // Phase C Tier 3 — richSvg upgrade: each brush eye gets an
  // undershadow at 0.4 opacity for ink thickness, 2-3 tiny ink-drip
  // dots near each eye, calligraphic mouth flourish replaces the
  // simple smile. Static (calligraphy is a static art).
  { id: 'calligraphic', label: 'Calligraphic',
    eyes: [],
    mouth: 'M40 66 Q 50 72 60 66',
    markPath: 'M34 48 Q 38 44 44 48 Q 40 52 34 50 Z M 56 48 Q 60 44 66 48 Q 62 52 56 50 Z',
    markFill: '#1a1410',
    richSvg: ({ featureFill, bodyShadow }) => (
      <>
        {/* Mouth undershadow */}
        <path d="M38 66 Q 50 74 62 66 Q 56 68 50 70 Q 44 68 38 66 Z" fill={bodyShadow} opacity="0.32" transform="translate(0 0.8)" />
        {/* Flourished calligraphic mouth */}
        <path d="M38 66 Q 50 74 62 66 Q 56 68 50 70 Q 44 68 38 66 Z" fill={featureFill} opacity="0.95" stroke="#0a0608" strokeWidth="0.3" />
        {/* Mouth flourish tail (right side) */}
        <path d="M62 66 Q 66 64 68 62" stroke={featureFill} strokeWidth="0.9" fill="none" strokeLinecap="round" opacity="0.85" />
        {/* Left brushstroke eye undershadow */}
        <path d="M34 48 Q 38 44 44 48 Q 40 52 34 50 Z" fill={bodyShadow} opacity="0.4" transform="translate(0.5 0.6)" />
        {/* Left brushstroke eye */}
        <path d="M34 48 Q 38 44 44 48 Q 40 52 34 50 Z" fill={featureFill} stroke="#0a0608" strokeWidth="0.3" />
        {/* Brushstroke tail (left, outgoing) */}
        <path d="M33 49 Q 30 50 28 50.6" stroke={featureFill} strokeWidth="0.65" fill="none" strokeLinecap="round" opacity="0.9" />
        {/* Right brushstroke eye undershadow */}
        <path d="M56 48 Q 60 44 66 48 Q 62 52 56 50 Z" fill={bodyShadow} opacity="0.4" transform="translate(0.5 0.6)" />
        {/* Right brushstroke eye */}
        <path d="M56 48 Q 60 44 66 48 Q 62 52 56 50 Z" fill={featureFill} stroke="#0a0608" strokeWidth="0.3" />
        {/* Brushstroke tail (right, outgoing) */}
        <path d="M67 49 Q 70 50 72 50.6" stroke={featureFill} strokeWidth="0.65" fill="none" strokeLinecap="round" opacity="0.9" />
        {/* Ink-drip dots near each eye */}
        <circle cx="30" cy="44" r="0.5" fill={featureFill} opacity="0.85" />
        <circle cx="46" cy="44" r="0.35" fill={featureFill} opacity="0.7" />
        <circle cx="54" cy="44" r="0.35" fill={featureFill} opacity="0.7" />
        <circle cx="70" cy="44" r="0.5" fill={featureFill} opacity="0.85" />
        {/* Smaller drip dots */}
        <circle cx="33" cy="53" r="0.3" fill={featureFill} opacity="0.55" />
        <circle cx="67" cy="53" r="0.3" fill={featureFill} opacity="0.55" />
        {/* Subtle wash highlight inside each eye */}
        <path d="M37 47 Q 39 46 41 47" stroke="#fff" strokeWidth="0.35" fill="none" opacity="0.4" />
        <path d="M59 47 Q 61 46 63 47" stroke="#fff" strokeWidth="0.35" fill="none" opacity="0.4" />
      </>
    ),
  },
  // 12 ghost-veil — hollow eye sockets with a faint luminous glow
  // (eyeFill near-white, eyeClass animates a pulse). No mouth.
  //
  // Phase D Tier 4 — richSvg: layered eye sockets (outer dark hollow +
  // mid pale glow + inner pinprick pupil + outermost soft ethereal halo
  // via feGaussianBlur), tear streak dripping from one eye, 3 drifting
  // mist particles, aura pulse on the whole face group.
  { id: 'ghost-veil', label: 'Ghost Veil',
    eyes: [{ x: 40, y: 50, r: 3 }, { x: 60, y: 50, r: 3 }],
    eyeFill: '#e8eaff',
    eyeClass: 'face-ghost-veil',
    markPath: 'M40 50 m -1 0 a 1 1 0 1 0 2 0 a 1 1 0 1 0 -2 0 M 60 50 m -1 0 a 1 1 0 1 0 2 0 a 1 1 0 1 0 -2 0',
    markFill: '#1a1410',
    richSvg: ({ uid }) => {
      return (
        <>
          <defs>
            <filter id={`${uid}-blur`} x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="0.6" />
            </filter>
            <radialGradient id={`${uid}-ethereal`} cx="50%" cy="50%" r="50%">
              <stop offset="0%"   stopColor="#e8eaff" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#e8eaff" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g className="face-ghost-veil">
            {/* Outermost ethereal halo (per-eye) */}
            <circle cx="40" cy="50" r="6" fill={`url(#${uid}-ethereal)`} />
            <circle cx="60" cy="50" r="6" fill={`url(#${uid}-ethereal)`} />
            {/* Outer dark hollow socket */}
            <circle cx="40" cy="50" r="3.4" fill="#0a0814" />
            <circle cx="60" cy="50" r="3.4" fill="#0a0814" />
            {/* Mid pale glow inside socket — blurred */}
            <g filter={`url(#${uid}-blur)`}>
              <circle cx="40" cy="50" r="2.4" fill="#e8eaff" opacity="0.7" />
              <circle cx="60" cy="50" r="2.4" fill="#e8eaff" opacity="0.7" />
            </g>
            {/* Inner pinprick dark pupil */}
            <circle cx="40" cy="50" r="0.8" fill="#1a1410" />
            <circle cx="60" cy="50" r="0.8" fill="#1a1410" />
          </g>
          {/* Tear streak — pale ethereal line dripping from one eye */}
          <path d="M40 54 Q 39 60 40.5 66 Q 41.5 62 40 54 Z" fill="#e8eaff" opacity="0.55" className="ghost-tear" />
          {/* 3 drifting mist particles around the face */}
          <g className="ghost-mist">
            <circle cx="32" cy="44" r="0.5" fill="#e8eaff" opacity="0.6" className="mist-mote mist-mote-A" />
            <circle cx="68" cy="46" r="0.4" fill="#e8eaff" opacity="0.6" className="mist-mote mist-mote-B" />
            <circle cx="50" cy="38" r="0.5" fill="#e8eaff" opacity="0.6" className="mist-mote mist-mote-C" />
          </g>
        </>
      );
    },
  },
];

export const HUES = [0, 18, 25, 35, 50, 90, 110, 130, 140, 180, 210, 240, 250, 270, 290, 320, 330];
//                      ^new   ^new                ^new                ^new
// 110 / 240 / 320 are shop-exclusive hues (Mint / Indigo / Orchid)
// added in the customization revamp Phase 1. Without them the
// IconPicker doesn't render swatches for the items even when they're
// in the player's inventory.
//
// Phase E2 — added Sunset (18), Bone (35), Forest (130), Twilight (270)
// as four new accent hue swatches. Catalog entries (`accent-18`,
// `accent-35`, `accent-130`, `accent-270`) live in COSMETIC_CATALOG
// with mixed embers-shop / lantern-shop sources.

// ─────────────────────────────────────────────────────────────────────
// Phase E1 — Borders + Backgrounds (avatar-icon decoration)
// ─────────────────────────────────────────────────────────────────────
//
// IconAvatar renders these layers BACK→FRONT in this order:
//   background (rect/g filling the 100x100 frame, BEHIND body) →
//   body silhouette → face features → hat → border ring (stroked
//   circle/path drawn LAST so it always reads as a frame).
//
// Each registry is empty in E1 — E3 appends real entries. Both render
// functions receive the avatar's primary + accent hue so themed items
// can tint themselves to match the wearer (e.g. an "ember" border that
// picks up the player's body color). The optional `className` becomes
// the wrapping <g>'s className so CSS keyframes can animate the layer
// without a per-avatar requestAnimationFrame loop.

/** Render arguments shared by border + background + mask entries. */
export interface CosmeticLayerOpts {
  /** Avatar's primary body hue (0-359). Resolved from palette or fallback. */
  primaryHue: number;
  /** Avatar's accent hue (0-359). Resolved from palette or fallback. */
  accentHue: number;
  /** Phase D pass-2 — Stable unique id prefix the renderer hands in so
   *  multiple avatars wearing the same border/background/mask on the
   *  same page don't collide on <defs>/gradient/filter ids. Tier 4
   *  layers MUST namespace every defined id with this prefix.
   *  Optional for back-compat: older entries that don't define ids can
   *  ignore it. */
  uid?: string;
}

/** What a layer's render fn returns. `children` is the SVG fragment
 *  (one or many elements) appended inside a wrapping <g>; `className`
 *  is attached to that <g> so CSS animations can target the whole
 *  layer at once. */
export interface CosmeticLayerOutput {
  children: ReactNode;
  className?: string;
}

export interface BorderDef {
  id: string;
  /** Render the border. Returns SVG children to append into a wrapping
   *  <g>, plus optional className for CSS animation. The wrapping <g>
   *  is positioned ON TOP of all other layers (final overlay). */
  render(opts: CosmeticLayerOpts): CosmeticLayerOutput;
}

export interface BackgroundDef {
  id: string;
  /** Render the background. Returns SVG children to append into a
   *  wrapping <g>, plus optional className for CSS animation. The
   *  wrapping <g> is positioned BEHIND the body silhouette. */
  render(opts: CosmeticLayerOpts): CosmeticLayerOutput;
}

/** Phase F5 — mask layer. Renders ON TOP of the face features but
 *  UNDER the hat (z-order: background → body → face → MASK → hat →
 *  border). Each entry returns SVG children + an optional className
 *  for CSS animation. */
export interface MaskDef {
  id: string;
  /** Display label shown in the locker / shop tile. */
  label: string;
  render(opts: CosmeticLayerOpts): CosmeticLayerOutput;
}

// ─────────────────────────────────────────────────────────────────────
// Phase E3 — Border + Background entries
// ─────────────────────────────────────────────────────────────────────
//
// Each border renders into a wrapping <g> drawn AFTER all other layers
// (so the ring sits on top). Each background renders into a wrapping
// <g> drawn BEFORE the body silhouette. Both receive the player's
// resolved primaryHue + accentHue so themed items can tint themselves
// to match the wearer.
//
// Animation policy: CSS-only. Each registry entry sets a `className` on
// its wrapping <g>; matching keyframes live in `web/src/styles/global.css`
// under the `/* Cosmetic animations */` section. We never animate
// layout properties — only `transform`, `opacity`, `filter: hue-rotate`.

/** Border ring overlays. 5 lantern-shop entries + ember-ring (Crimson
 *  Solstice tier 30 premium). */
export const BORDERS: BorderDef[] = [
  // 1. leaf-ring — circle of stylized leaves around the avatar
  //    perimeter. Static. Rarity 2. lantern-shop 400.
  //
  // Phase B Tier 2 — 2-stop linearGradient per leaf (light → dark) + thin
  // center-vein stroke + ink edge. Static (no animation). Keep existing
  // className so any prior wiring still attaches.
  {
    id: 'leaf-ring',
    render: ({ accentHue, uid = 'lr' }) => {
      const leafHi = `hsl(${(accentHue + 100) % 360} 60% 56%)`;
      const leafMid = `hsl(${(accentHue + 100) % 360} 55% 38%)`;
      const leafEdge = `hsl(${(accentHue + 100) % 360} 60% 22%)`;
      const leaves = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        return (
          <g key={`leaf-${i}`} transform={`rotate(${angle} 50 50)`}>
            {/* Leaf body — gradient-filled tear-drop */}
            <path
              d="M50 3 C 47 6, 47 10, 50 13 C 53 10, 53 6, 50 3 Z"
              fill={`url(#${uid}-leaf)`}
              stroke={leafEdge}
              strokeWidth="0.5"
            />
            {/* Center vein */}
            <path d="M50 4 L 50 12.4" stroke={leafEdge} strokeWidth="0.3" opacity="0.85" />
            {/* Light side highlight */}
            <path d="M49 5 Q 48.4 8 49 11" stroke={leafHi} strokeWidth="0.3" fill="none" opacity="0.6" />
          </g>
        );
      });
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-leaf`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor={leafHi} />
                <stop offset="60%"  stopColor={leafMid} />
                <stop offset="100%" stopColor={leafEdge} />
              </linearGradient>
            </defs>
            {leaves}
          </>
        ),
        className: 'border-leaf-ring',
      };
    },
  },

  // 2. brass-frame — thick metallic ring with rivets.
  //    Slight gradient highlight. Rarity 2. lantern-shop 500.
  //
  // Phase B Tier 2 — 3-stop brass gradient on the band + inner brighter
  // polish ring + rivet highlights (top-left pinprick) + engraved hatch
  // marks between rivets. Static.
  {
    id: 'brass-frame',
    render: ({ uid = 'bf' }) => {
      // Rivets at 8 evenly spaced positions
      const rivetData = Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 360) / 8;
        const rad = (angle * Math.PI) / 180;
        return { cx: 50 + Math.cos(rad) * 47, cy: 50 + Math.sin(rad) * 47, angle };
      });
      // Hatch marks between rivets (at midpoints, 8 spots)
      const hatchMarks = Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 360) / 8 + 22.5;
        return (
          <g key={`hatch-${i}`} transform={`rotate(${angle} 50 50)`}>
            <line x1="49" y1="3" x2="51" y2="6" stroke="#5a4a20" strokeWidth="0.5" opacity="0.55" strokeLinecap="round" />
          </g>
        );
      });
      const rivets = rivetData.map((r, i) => (
        <g key={`rivet-${i}`}>
          <circle cx={r.cx} cy={r.cy} r={1.5} fill="#5a4a20" stroke="#1a1410" strokeWidth="0.3" />
          {/* Pinprick highlight top-left */}
          <circle cx={r.cx - 0.4} cy={r.cy - 0.4} r="0.45" fill="#fff4c2" opacity="0.75" />
        </g>
      ));
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-brass`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#f5d68a" />
                <stop offset="50%"  stopColor="#b8902a" />
                <stop offset="100%" stopColor="#6c5012" />
              </linearGradient>
            </defs>
            {/* Main brass band — 3-stop gradient */}
            <circle cx="50" cy="50" r="47" fill="none" stroke={`url(#${uid}-brass)`} strokeWidth="6" />
            {/* Engraved hatch marks */}
            {hatchMarks}
            {/* Inner polish-band highlight */}
            <circle cx="50" cy="50" r="44.4" fill="none" stroke="#f5d68a" strokeWidth="0.6" opacity="0.85" />
            {/* Outer dark trim */}
            <circle cx="50" cy="50" r="49.6" fill="none" stroke="#5a4a20" strokeWidth="0.4" opacity="0.7" />
            {/* Rivets with highlights */}
            {rivets}
          </>
        ),
        className: 'border-brass-frame',
      };
    },
  },

  // 3. candle-circle — 8 small candle flames evenly distributed.
  //    Each flame opacity wobble, staggered. Rarity 3. lantern-shop 800.
  //
  // Phase C Tier 3 — layered upgrade: 3-stop cream wax gradient on each
  // candle base, flame layering (outer orange + inner bright), per-candle
  // halo glow circle, wax drip on each base. Preserves existing .flame-A..E
  // stagger classes so the flicker animation still plays.
  {
    id: 'candle-circle',
    render: ({ uid = 'cc' }) => {
      const phases = ['A', 'B', 'C', 'D', 'E', 'A', 'B', 'C'];
      const flames = Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 360) / 8;
        const phase = phases[i];
        return (
          <g key={`flame-${i}`} transform={`rotate(${angle} 50 50)`}>
            {/* Halo glow circle behind flame — pulses with flame */}
            <circle cx="50" cy="0" r="3.4" fill={`url(#${uid}-halo)`} className={`cc-halo cc-halo-${phase}`} />
            {/* Candle base with 3-stop cream gradient + ink outline */}
            <rect x="48.2" y="2" width="3.6" height="5" fill={`url(#${uid}-wax)`} stroke="#1a1410" strokeWidth="0.3" rx="0.3" />
            {/* Wax drip on the side */}
            <path d="M48.2 4 Q 47.2 5 47.8 6.8 Q 48.6 5.6 48.2 4 Z" fill="#fff8d8" stroke="#1a1410" strokeWidth="0.25" opacity="0.9" />
            {/* Wax top rim shadow */}
            <rect x="48.2" y="2" width="3.6" height="0.6" fill="#a08458" opacity="0.55" />
            {/* Flame group — preserves .flame-A..E stagger */}
            <g className={`flame flame-${phase}`}>
              {/* Outer wider flame at 0.55 opacity */}
              <path d="M50 -3 C 46 1, 46 5, 50 7 C 54 5, 54 1, 50 -3 Z" fill="#ff7820" opacity="0.55" />
              {/* Main flame */}
              <path d="M50 -2 C 47 1, 47 5, 50 6 C 53 5, 53 1, 50 -2 Z" fill="#ffaa3d" stroke="#ff7020" strokeWidth="0.4" />
              {/* Inner bright flame core */}
              <path d="M50 0 C 48.6 2, 48.6 4, 50 5 C 51.4 4, 51.4 2, 50 0 Z" fill="#fff4c2" opacity="0.85" />
            </g>
          </g>
        );
      });
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-wax`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="#a08458" />
                <stop offset="50%"  stopColor="#f5d68a" />
                <stop offset="100%" stopColor="#a08458" />
              </linearGradient>
              <radialGradient id={`${uid}-halo`} cx="50%" cy="50%" r="50%">
                <stop offset="0%"   stopColor="#ffd27a" stopOpacity="0.85" />
                <stop offset="60%"  stopColor="#ffaa3d" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#ffaa3d" stopOpacity="0" />
              </radialGradient>
            </defs>
            {flames}
          </>
        ),
        className: 'border-candle-circle',
      };
    },
  },

  // 4. gemstone-studded — gold ring with 4 inset gem stones.
  //    Rarity 3. lantern-shop 900.
  //
  // Phase C Tier 3 — layered upgrade: 4-stop gold band gradient,
  // per-gem layering (outer + inner bright diamond + white glint),
  // mounting prongs at each gem, 4 small filigree curls at the
  // mid-cardinals. ONE subtle pulse on the gems via .gs-gem-A/B.
  {
    id: 'gemstone-studded',
    render: ({ accentHue, uid = 'gs' }) => {
      const gemColor = `hsl(${accentHue} 80% 60%)`;
      const gemHi = `hsl(${accentHue} 95% 85%)`;
      const gemEdge = `hsl(${accentHue} 75% 25%)`;
      const gems = [0, 90, 180, 270].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad) * 47;
        const cy = 50 + Math.sin(rad) * 47;
        const phase = i % 2 === 0 ? 'A' : 'B';
        return (
          <g key={`gem-${angle}`} className={`gs-gem gs-gem-${phase}`}>
            {/* Mounting prongs (4 small triangles around the gem) */}
            <path
              d={`M${cx - 3.4} ${cy} L ${cx - 4} ${cy - 1.6} L ${cx - 3} ${cy - 1.4} Z M ${cx + 3.4} ${cy} L ${cx + 4} ${cy + 1.6} L ${cx + 3} ${cy + 1.4} Z M ${cx} ${cy - 4} L ${cx - 1.4} ${cy - 4.8} L ${cx + 1.4} ${cy - 4.8} Z M ${cx} ${cy + 4} L ${cx - 1.4} ${cy + 4.8} L ${cx + 1.4} ${cy + 4.8} Z`}
              fill="#a08438" stroke="#5a4218" strokeWidth="0.3"
            />
            {/* Outer gem diamond */}
            <path
              d={`M${cx} ${cy - 4} L ${cx + 3} ${cy} L ${cx} ${cy + 4} L ${cx - 3} ${cy} Z`}
              fill={gemColor}
              stroke={gemEdge}
              strokeWidth="0.5"
            />
            {/* Inner brighter diamond */}
            <path
              d={`M${cx} ${cy - 2.4} L ${cx + 1.8} ${cy} L ${cx} ${cy + 2.4} L ${cx - 1.8} ${cy} Z`}
              fill={gemHi}
              opacity="0.85"
            />
            {/* White glint top */}
            <circle cx={cx - 0.8} cy={cy - 1.6} r="0.55" fill="#fff" opacity="0.95" />
          </g>
        );
      });
      // 4 small filigree curls at midpoints (45°, 135°, 225°, 315°)
      const filigrees = [45, 135, 225, 315].map((angle) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad) * 47;
        const cy = 50 + Math.sin(rad) * 47;
        return (
          <g key={`fil-${angle}`}>
            <path d={`M${cx - 2} ${cy - 1} Q ${cx} ${cy - 2.4} ${cx + 2} ${cy - 1} Q ${cx} ${cy + 1.2} ${cx - 2} ${cy - 1} Z`}
                  fill="none" stroke="#a08438" strokeWidth="0.55" opacity="0.85" />
            <circle cx={cx} cy={cy} r="0.5" fill="#f5d680" opacity="0.85" />
          </g>
        );
      });
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-gold`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#fff8d8" />
                <stop offset="30%"  stopColor="#f5d680" />
                <stop offset="65%"  stopColor="#b8902a" />
                <stop offset="100%" stopColor="#6c5012" />
              </linearGradient>
            </defs>
            {/* Outer band */}
            <circle cx="50" cy="50" r="47" fill="none" stroke={`url(#${uid}-gold)`} strokeWidth="3.5" />
            {/* Inner polish band */}
            <circle cx="50" cy="50" r="44.6" fill="none" stroke="#f5d680" strokeWidth="0.5" opacity="0.7" />
            {/* Outer dark trim */}
            <circle cx="50" cy="50" r="49.4" fill="none" stroke="#5a4218" strokeWidth="0.4" opacity="0.7" />
            {filigrees}
            {gems}
          </>
        ),
        className: 'border-gemstone-studded',
      };
    },
  },

  // 5. runic-glyph — engraved arcane runes around the ring,
  //    rotating slowly via CSS @keyframes. Rarity 4. lantern-shop 1500.
  //
  // Phase D pass-2 Tier 4 — 5-stop stone gradient on the band, weathered
  // fractalNoise stone-texture overlay, double-stacked arcane glow
  // halos, 12 glyph stones with bevel highlights, drop-shadow filter,
  // counter-rotating rune ring + radial sparks rising on staggered
  // phases. Replaces the flat single-stroke band with layered relief.
  {
    id: 'runic-glyph',
    render: ({ primaryHue, uid = 'rg' }) => {
      const stoneHi = `hsl(${primaryHue} 14% 36%)`;
      const stoneMid = `hsl(${primaryHue} 12% 22%)`;
      const stoneLo = `hsl(${primaryHue} 18% 10%)`;
      const runeColor = `hsl(${(primaryHue + 30) % 360} 80% 72%)`;
      const runeGlow = `hsl(${(primaryHue + 30) % 360} 90% 60%)`;
      const runes = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        const variant = i % 3;
        const path =
          variant === 0
            ? 'M-1.5 -3 L 1.5 -3 M 0 -3 L 0 3 M -1.5 3 L 1.5 3'
            : variant === 1
              ? 'M-2 -3 L 0 0 L -2 3 M 0 -3 L 2 0 L 0 3'
              : 'M-2 -3 L 2 -3 L 0 0 L 2 3 L -2 3';
        return (
          <g key={`rune-${i}`} transform={`rotate(${angle} 50 50) translate(50 4)`}>
            {/* small inset stone behind each rune */}
            <rect x="-2.6" y="-3.4" width="5.2" height="6.8" rx="0.6" fill={stoneLo} opacity="0.6" />
            {/* glow halo behind glyph */}
            <path d={path} fill="none" stroke={runeGlow} strokeWidth="1.6" strokeLinecap="round" opacity="0.55" />
            {/* the rune itself */}
            <path d={path} fill="none" stroke={runeColor} strokeWidth="0.75" strokeLinecap="round" />
          </g>
        );
      });
      // 6 radial sparks rising from the stones (between runes).
      const sparks = [30, 90, 150, 210, 270, 330].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad - Math.PI / 2) * 47;
        const cy = 50 + Math.sin(rad - Math.PI / 2) * 47;
        const phase = ['A', 'B', 'C'][i % 3];
        return <circle key={`rgs-${i}`} cx={cx} cy={cy} r="0.7" fill={runeGlow} className={`rg-spark rg-spark-${phase}`} />;
      });
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-stone`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor={stoneHi} />
                <stop offset="35%" stopColor={stoneMid} />
                <stop offset="70%" stopColor={stoneMid} />
                <stop offset="100%" stopColor={stoneLo} />
              </linearGradient>
              <radialGradient id={`${uid}-halo`} cx="50%" cy="50%" r="55%">
                <stop offset="80%" stopColor={runeGlow} stopOpacity="0" />
                <stop offset="92%" stopColor={runeGlow} stopOpacity="0.4" />
                <stop offset="100%" stopColor={runeGlow} stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-tex`} x="0%" y="0%" width="100%" height="100%">
                <feTurbulence type="fractalNoise" baseFrequency="0.08" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.35 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
              <filter id={`${uid}-drop`} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.7" />
                <feOffset dx="0" dy="0.8" result="o" />
                <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {/* outer arcane halo (soft pulse) */}
            <circle cx="50" cy="50" r="49" fill={`url(#${uid}-halo)`} className="rg-outer-halo" />
            {/* main stone band with gradient + drop-shadow */}
            <g filter={`url(#${uid}-drop)`}>
              <circle cx="50" cy="50" r="47" fill="none" stroke={`url(#${uid}-stone)`} strokeWidth="6" />
              {/* weathered stone texture overlay */}
              <circle cx="50" cy="50" r="47" fill="none" stroke="#000" strokeWidth="6" filter={`url(#${uid}-tex)`} opacity="0.45" />
              {/* inner bevel highlight */}
              <circle cx="50" cy="50" r="45" fill="none" stroke={stoneHi} strokeWidth="0.6" opacity="0.55" />
              {/* outer bevel shadow */}
              <circle cx="50" cy="50" r="49.4" fill="none" stroke={stoneLo} strokeWidth="0.6" opacity="0.7" />
            </g>
            {/* rotating rune ring */}
            <g className="runes">{runes}</g>
            {/* rising sparks */}
            <g className="rg-sparks">{sparks}</g>
          </>
        ),
        className: 'border-runic-glyph',
      };
    },
  },

  // 6. ember-ring — CRIMSON SOLSTICE TIER 30, premium-only.
  //
  // Phase D pass-2 Tier 4 — 5-stop fire gradient on flame tongues,
  // double-stacked halos (deep + bright), feTurbulence ember-texture
  // overlay on flames, drop-shadow filter, gradient-tinted inner
  // tongues, plus the existing dual counter-rotating rings and 6 seam
  // sparks. All palette-locked (Crimson Solstice).
  {
    id: 'ember-ring',
    render: ({ uid = 'er' }) => {
      const outerFlames = Array.from({ length: 16 }).map((_, i) => {
        const angle = (i * 360) / 16;
        return (
          <g key={`o-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M50 1 C 46 6, 46 12, 50 18 C 54 12, 54 6, 50 1 Z"
              fill={`url(#${uid}-flame)`}
              stroke="#280404"
              strokeWidth="0.4"
            />
            {/* per-tongue ember-texture overlay */}
            <path
              d="M50 1 C 46 6, 46 12, 50 18 C 54 12, 54 6, 50 1 Z"
              filter={`url(#${uid}-tex)`}
              opacity="0.5"
            />
          </g>
        );
      });
      const innerFlames = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        return (
          <g key={`i-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M50 8 C 48 12, 48 16, 50 19 C 52 16, 52 12, 50 8 Z"
              fill={`url(#${uid}-inner)`}
              opacity="0.92"
            />
          </g>
        );
      });
      const sparks = [0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad - Math.PI / 2) * 42;
        const cy = 50 + Math.sin(rad - Math.PI / 2) * 42;
        const phase = ['A', 'B', 'C'][i % 3];
        return <circle key={`sp-${i}`} cx={cx} cy={cy} r="1.2" fill="#ffe6a8" className={`spark spark-${phase}`} />;
      });
      return {
        children: (
          <>
            <defs>
              {/* 5-stop fire gradient — bright tip → mid-orange → deep
                  crimson → blackened base, alternating per-tongue via
                  the SVG paint angle. */}
              <linearGradient id={`${uid}-flame`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#ffe6a8" />
                <stop offset="25%" stopColor="#ffaa3d" />
                <stop offset="55%" stopColor="#ff4040" />
                <stop offset="85%" stopColor="#a02020" />
                <stop offset="100%" stopColor="#280404" />
              </linearGradient>
              <linearGradient id={`${uid}-inner`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#fff6d8" />
                <stop offset="40%" stopColor="#ffaa3d" />
                <stop offset="100%" stopColor="#ff7820" />
              </linearGradient>
              <radialGradient id={`${uid}-deepHalo`} cx="50%" cy="50%" r="55%">
                <stop offset="70%" stopColor="#ff4040" stopOpacity="0" />
                <stop offset="92%" stopColor="#ff7820" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#ff7820" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-brightHalo`} cx="50%" cy="50%" r="50%">
                <stop offset="80%" stopColor="#ffe6a8" stopOpacity="0" />
                <stop offset="95%" stopColor="#ffe6a8" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#ffe6a8" stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-drop`} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" />
                <feOffset dx="0" dy="1" result="o" />
                <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id={`${uid}-tex`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.18" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 1  0 0 0 0 0.5  0 0 0 0 0.1  0 0 0 0.35 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
            </defs>
            {/* Double-stacked halos — deep crimson aura then bright rim */}
            <circle cx="50" cy="50" r="48" fill={`url(#${uid}-deepHalo)`} className="glow-halo" />
            <circle cx="50" cy="50" r="46" fill={`url(#${uid}-brightHalo)`} className="glow-halo" />
            {/* Outermost trim — deep blood */}
            <circle cx="50" cy="50" r="49" fill="none" stroke="#4a0a0a" strokeWidth="1.5" />
            {/* Outer flame ring — gradient + texture + drop-shadow */}
            <g className="outer-flame" filter={`url(#${uid}-drop)`}>{outerFlames}</g>
            {/* Inner flame ring — counter-rotates + pulses */}
            <g className="inner-flame">{innerFlames}</g>
            {/* Seam-spark ember dots */}
            <g className="sparks">{sparks}</g>
          </>
        ),
        className: 'border-ember-ring',
      };
    },
  },

  // ───────── Phase F6 additions — 6 new avatar borders ─────────
  // Bug-2 fix — F5 only added new SEAT borders; the avatar BORDERS
  // registry was stuck at 6. Players had nothing new to equip in the
  // avatar Border slot and the docs hint at obsidian / void-ring /
  // tide-foam / phoenix lines. These six fill that gap.

  // 7. obsidian-band — black band with subtle red veins. Rarity 3.
  //
  // Phase C Tier 3 — layered upgrade: 4-stop obsidian gradient, 6 vein
  // arcs (doubled from 3, varied angles), inner thin magma-glow stroke,
  // top-left gloss highlight arc. ONE subtle vein-pulse animation on
  // staggered phases via .ob-vein-A/B/C.
  {
    id: 'obsidian-band',
    render: ({ uid = 'ob' }) => {
      // 6 vein angles around the ring
      const veinPaths = [
        { d: 'M 6 52 Q 12 56 14 50', phase: 'A' },
        { d: 'M 92 46 Q 86 42 84 48', phase: 'B' },
        { d: 'M 50 4  Q 54 8 50 12', phase: 'C' },
        { d: 'M 24 14 Q 30 20 28 24', phase: 'A' },
        { d: 'M 76 86 Q 70 80 72 76', phase: 'B' },
        { d: 'M 46 96 Q 50 90 54 96', phase: 'C' },
      ];
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-glass`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#2a1a24" />
                <stop offset="35%"  stopColor="#1a0e14" />
                <stop offset="70%"  stopColor="#08040a" />
                <stop offset="100%" stopColor="#1a0e14" />
              </linearGradient>
              <filter id={`${uid}-tex`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.05" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.4  0 0 0 0 0.05  0 0 0 0 0.05  0 0 0 0.18 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
            </defs>
            {/* Main obsidian band */}
            <circle cx="50" cy="50" r="47" fill="none" stroke={`url(#${uid}-glass)`} strokeWidth="5" />
            {/* Glassy surface texture overlay (subtle) */}
            <circle cx="50" cy="50" r="47" fill="none" stroke="#000" strokeWidth="5" filter={`url(#${uid}-tex)`} opacity="0.55" />
            {/* Inner magma-glow stroke */}
            <circle cx="50" cy="50" r="44.6" fill="none" stroke="#a02418" strokeWidth="0.6" opacity="0.55" />
            {/* Outer dark trim */}
            <circle cx="50" cy="50" r="49.4" fill="none" stroke="#08040a" strokeWidth="0.5" />
            {/* Gloss highlight arc top-left */}
            <path d="M 14 28 Q 24 14 40 8" fill="none" stroke="#a8a0a8" strokeWidth="0.7" opacity="0.4" />
            {/* 6 veins on staggered pulses */}
            {veinPaths.map((v, i) => (
              <path
                key={`vein-${i}`}
                d={v.d}
                fill="none"
                stroke="#a02418"
                strokeWidth="0.7"
                strokeLinecap="round"
                className={`ob-vein ob-vein-${v.phase}`}
              />
            ))}
            {/* Vein highlight glints (small bright dots at vein peaks) */}
            <circle cx="11" cy="54" r="0.5" fill="#ff5a28" opacity="0.6" />
            <circle cx="87" cy="44" r="0.5" fill="#ff5a28" opacity="0.6" />
            <circle cx="52" cy="6" r="0.5" fill="#ff5a28" opacity="0.6" />
          </>
        ),
        className: 'border-obsidian-band',
      };
    },
  },

  // 8. crystal-shard — refracting crystal facets, prismatic.
  //
  // Phase D pass-2 Tier 4 — 5-stop crystal gradient (icy white → pale
  // cyan → deep blue → midnight) per shard, fractalNoise refraction
  // texture, double-stacked prismatic halos, drop-shadow filter, plus
  // the existing dual counter-rotating parallax rings AND 6 prism-light
  // glints that rise on staggered phases.
  {
    id: 'crystal-shard',
    render: ({ uid = 'cs' }) => {
      const outer = Array.from({ length: 10 }).map((_, i) => {
        const angle = (i * 360) / 10;
        const gradRef = i % 2 === 0 ? `${uid}-cyan` : `${uid}-violet`;
        return (
          <g key={`os-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M50 0 L 45 12 L 55 12 Z"
              fill={`url(#${gradRef})`}
              stroke="#3a6090"
              strokeWidth="0.5"
              opacity="0.95"
            />
            {/* facet bevel line (specular highlight) */}
            <path d="M50 1 L 49 11" stroke="#ffffff" strokeWidth="0.4" opacity="0.85" />
            {/* refraction texture */}
            <path d="M50 0 L 45 12 L 55 12 Z" filter={`url(#${uid}-tex)`} opacity="0.4" />
          </g>
        );
      });
      const inner = Array.from({ length: 8 }).map((_, i) => {
        const angle = (i * 360) / 8 + 22;
        const gradRef = i % 2 === 0 ? `${uid}-pearl` : `${uid}-violet`;
        return (
          <g key={`is-${i}`} transform={`rotate(${angle} 50 50)`}>
            <path
              d="M50 6 L 47 14 L 53 14 Z"
              fill={`url(#${gradRef})`}
              stroke="#5078a0"
              strokeWidth="0.3"
              opacity="0.92"
            />
            <path d="M50 7 L 49.5 13.5" stroke="#ffffff" strokeWidth="0.3" opacity="0.7" />
          </g>
        );
      });
      // 6 prism-light glints rising at the seams between outer shards.
      const glints = [18, 90, 162, 234, 306, 0].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad - Math.PI / 2) * 14;
        const cy = 50 + Math.sin(rad - Math.PI / 2) * 14;
        const phase = ['A', 'B', 'C'][i % 3];
        return <circle key={`gl-${i}`} cx={cx} cy={cy} r="0.9" fill="#e0f4ff" className={`cs-glint cs-glint-${phase}`} />;
      });
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-cyan`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#ffffff" />
                <stop offset="22%" stopColor="#cff0fa" />
                <stop offset="55%" stopColor="#7ac4e6" />
                <stop offset="80%" stopColor="#3a6090" />
                <stop offset="100%" stopColor="#1a304a" />
              </linearGradient>
              <linearGradient id={`${uid}-violet`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#ffffff" />
                <stop offset="22%" stopColor="#e0d8ff" />
                <stop offset="55%" stopColor="#a8b0e8" />
                <stop offset="80%" stopColor="#5078a0" />
                <stop offset="100%" stopColor="#1a2050" />
              </linearGradient>
              <linearGradient id={`${uid}-pearl`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#ffffff" />
                <stop offset="35%" stopColor="#f4f4fc" />
                <stop offset="70%" stopColor="#c0d4ee" />
                <stop offset="100%" stopColor="#5a7898" />
              </linearGradient>
              <radialGradient id={`${uid}-outerHalo`} cx="50%" cy="50%" r="50%">
                <stop offset="78%" stopColor="#9be0f0" stopOpacity="0" />
                <stop offset="92%" stopColor="#c0e0ff" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#c0e0ff" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-innerHalo`} cx="50%" cy="50%" r="40%">
                <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-drop`} x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.7" />
                <feOffset dx="0" dy="0.8" />
                <feComponentTransfer><feFuncA type="linear" slope="0.5" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id={`${uid}-tex`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.12" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.7  0 0 0 0 0.9  0 0 0 0 1  0 0 0 0.25 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
            </defs>
            {/* Inner crystalline halo (breathing) */}
            <circle cx="50" cy="50" r="36" fill={`url(#${uid}-innerHalo)`} className="refract-inner" />
            {/* Outermost prismatic glow ring */}
            <circle cx="50" cy="50" r="49" fill={`url(#${uid}-outerHalo)`} className="refract-outer" />
            <circle cx="50" cy="50" r="48" fill="none" stroke="#3a6090" strokeWidth="1.5" />
            {/* Outer shards — gradient + bevel + texture + drop-shadow */}
            <g className="shards-outer" filter={`url(#${uid}-drop)`}>{outer}</g>
            {/* Inner parallax shards */}
            <g className="shards-inner">{inner}</g>
            {/* Refracted thin ring (the original glow ring) */}
            <circle cx="50" cy="50" r="46" fill="none" stroke="#c0e0ff" strokeWidth="0.8" className="refract-glow" />
            {/* Prism-light glints rising */}
            <g className="cs-glints">{glints}</g>
          </>
        ),
        className: 'border-crystal-shard',
      };
    },
  },

  // 9. tide-foam-ring — wave / foam ring with drift. Rarity 3.
  //
  // Phase C Tier 3 — layered upgrade: 4-stop water band gradient,
  // each crest gets a darker shadow underneath + brighter top + tiny
  // drip teardrop, inner wet-ring stroke, gradient on the band. Keeps
  // .foam drift animation; adds per-crest scaleY pulse via tf-crest-A/B/C.
  {
    id: 'tide-foam-ring',
    render: ({ uid = 'tf' }) => {
      const phases = ['A', 'B', 'C'];
      const crests = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        const phase = phases[i % 3];
        return (
          <g key={`crest-${i}`} transform={`rotate(${angle} 50 50)`} className={`tf-crest tf-crest-${phase}`}>
            {/* Foam-base shadow (darker) */}
            <path d="M46 6 Q 50 2 54 6 Q 50 8 46 6 Z" fill="#3a6c8a" opacity="0.7" />
            {/* Crest top bright */}
            <path d="M47 5 Q 50 1 53 5 Q 50 7 47 5 Z" fill={`url(#${uid}-foam)`} stroke="#5a8ab0" strokeWidth="0.4" />
            {/* Drip teardrop under crest */}
            <path d="M50 7 Q 49.4 9 50 10.2 Q 50.6 9 50 7 Z" fill="#9bc8e0" opacity="0.7" />
            {/* Tiny bright glint on crest top */}
            <circle cx="49.6" cy="3" r="0.4" fill="#fff" opacity="0.85" />
          </g>
        );
      });
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-band`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#6aa0c4" />
                <stop offset="35%"  stopColor="#3a6c8a" />
                <stop offset="70%"  stopColor="#1a3a58" />
                <stop offset="100%" stopColor="#0a2030" />
              </linearGradient>
              <radialGradient id={`${uid}-foam`} cx="50%" cy="35%" r="60%">
                <stop offset="0%"   stopColor="#ffffff" />
                <stop offset="50%"  stopColor="#dceefc" />
                <stop offset="100%" stopColor="#9bc8e0" />
              </radialGradient>
            </defs>
            {/* Outer band */}
            <circle cx="50" cy="50" r="46" fill="none" stroke={`url(#${uid}-band)`} strokeWidth="3" />
            {/* Inner wet-ring gradient stroke */}
            <circle cx="50" cy="50" r="43" fill="none" stroke="#3a8a9e" strokeWidth="0.7" opacity="0.6" />
            {/* Outer wave-curl shadow */}
            <circle cx="50" cy="50" r="48" fill="none" stroke="#0a2030" strokeWidth="0.5" opacity="0.5" />
            <g className="foam">{crests}</g>
          </>
        ),
        className: 'border-tide-foam-ring',
      };
    },
  },

  // 10. void-eclipse — event horizon. Mythic rarity 5.
  //
  // Phase D pass-2 Tier 4 — 5-stop deep-space gradient core, double-
  // stacked chromatic halos (violet inner + magenta outer), fractalNoise
  // void-static texture overlay, drop-shadow filter, gradient-tinted
  // accretion disc ticks, plus the existing rotating disc, breathing
  // event-glow ring, and 12 twinkling micro-stars.
  {
    id: 'void-eclipse',
    render: ({ uid = 've' }) => {
      const stars: Array<{ cx: number; cy: number; r: number; phase: 'A' | 'B' | 'C' }> = [
        { cx: 50, cy: 3,  r: 0.9, phase: 'A' },
        { cx: 96, cy: 50, r: 1.1, phase: 'B' },
        { cx: 50, cy: 97, r: 0.9, phase: 'C' },
        { cx: 4,  cy: 50, r: 1.1, phase: 'A' },
        { cx: 80, cy: 18, r: 0.7, phase: 'B' },
        { cx: 82, cy: 82, r: 0.7, phase: 'C' },
        { cx: 18, cy: 82, r: 0.7, phase: 'A' },
        { cx: 18, cy: 18, r: 0.7, phase: 'B' },
        { cx: 92, cy: 32, r: 0.5, phase: 'C' },
        { cx: 8,  cy: 32, r: 0.5, phase: 'A' },
        { cx: 92, cy: 68, r: 0.5, phase: 'B' },
        { cx: 8,  cy: 68, r: 0.5, phase: 'C' },
      ];
      const disc = Array.from({ length: 24 }).map((_, i) => {
        const angle = (i * 360) / 24;
        const fill = i % 3 === 0 ? `url(#${uid}-tickA)` : i % 3 === 1 ? `url(#${uid}-tickB)` : `url(#${uid}-tickC)`;
        return (
          <g key={`ad-${i}`} transform={`rotate(${angle} 50 50)`}>
            <rect x="49.4" y="2" width="1.2" height="3.4" fill={fill} opacity="0.92" />
          </g>
        );
      });
      // 3 secondary slow-drifting bright disc ticks (parallax layer)
      const discInner = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12 + 15;
        return (
          <g key={`adi-${i}`} transform={`rotate(${angle} 50 50)`}>
            <rect x="49.7" y="6" width="0.6" height="2" fill="#dcc8ff" opacity="0.55" />
          </g>
        );
      });
      return {
        children: (
          <>
            <defs>
              <radialGradient id={`${uid}-core`} cx="50%" cy="50%" r="50%">
                <stop offset="0%"  stopColor="#000000" />
                <stop offset="55%" stopColor="#04020a" />
                <stop offset="85%" stopColor="#1a1028" />
                <stop offset="95%" stopColor="#2a1450" />
                <stop offset="100%" stopColor="#04020a" />
              </radialGradient>
              <radialGradient id={`${uid}-violetHalo`} cx="50%" cy="50%" r="55%">
                <stop offset="70%" stopColor="#7a4ec8" stopOpacity="0" />
                <stop offset="88%" stopColor="#7a4ec8" stopOpacity="0.65" />
                <stop offset="100%" stopColor="#7a4ec8" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-magentaHalo`} cx="50%" cy="50%" r="55%">
                <stop offset="78%" stopColor="#d050b8" stopOpacity="0" />
                <stop offset="94%" stopColor="#d050b8" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#d050b8" stopOpacity="0" />
              </radialGradient>
              <linearGradient id={`${uid}-tickA`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffffff" /><stop offset="100%" stopColor="#a8a0ff" />
              </linearGradient>
              <linearGradient id={`${uid}-tickB`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#dcc8ff" /><stop offset="100%" stopColor="#5a3a98" />
              </linearGradient>
              <linearGradient id={`${uid}-tickC`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffeed8" /><stop offset="100%" stopColor="#a05878" />
              </linearGradient>
              <filter id={`${uid}-static`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.6" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.7  0 0 0 0 0.6  0 0 0 0 0.9  0 0 0 0.18 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
              <filter id={`${uid}-drop`} x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.5" />
                <feOffset dx="0" dy="0.6" />
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            {/* Magenta outer halo (slow breathe) */}
            <circle cx="50" cy="50" r="50" fill={`url(#${uid}-magentaHalo)`} className="ve-halo-outer" />
            {/* Violet near halo (faster breathe) */}
            <circle cx="50" cy="50" r="48" fill={`url(#${uid}-violetHalo)`} className="ve-halo-inner" />
            {/* Event-horizon edge */}
            <circle cx="50" cy="50" r="49" fill="none" stroke="#000000" strokeWidth="2" />
            {/* Deep void core — gradient instead of flat fill */}
            <circle cx="50" cy="50" r="46" fill={`url(#${uid}-core)`} stroke="#1a1028" strokeWidth="1.2" />
            {/* Void-static texture overlay (very subtle) */}
            <circle cx="50" cy="50" r="46" filter={`url(#${uid}-static)`} opacity="0.5" />
            {/* Inner parallax disc — counter to outer */}
            <g className="accretion-inner">{discInner}</g>
            {/* Main accretion disc — bright, chromatic, rotates */}
            <g className="accretion" filter={`url(#${uid}-drop)`}>{disc}</g>
            {/* Soft glow ring (event horizon edge breath) */}
            <circle cx="50" cy="50" r="44" fill="none" stroke="#6a4ab8" strokeWidth="0.6" className="event-glow" />
            {/* Micro-stars — 3 stagger phases */}
            <g className="starlight">
              {stars.map((s, i) => (
                <circle
                  key={`vs-${i}`}
                  cx={s.cx}
                  cy={s.cy}
                  r={s.r}
                  fill="#ffffff"
                  className={`vstar vstar-${s.phase}`}
                />
              ))}
            </g>
          </>
        ),
        className: 'border-void-eclipse',
      };
    },
  },

  // 11. phoenix-feather — animated red/gold feather ring. Rarity 4.
  //
  // Phase D pass-2 Tier 4 — 5-stop fire-gold gradient on plumes, each
  // plume gets a quill spine + barb detail + feather-noise texture
  // overlay, double-stacked plume halos (deep crimson + bright gold),
  // drop-shadow filter, plus the existing undulating wave phases AND
  // rising ember sparks at 6 positions.
  {
    id: 'phoenix-feather',
    render: ({ uid = 'pf' }) => {
      const plumes = Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        const gradRef = i % 2 === 0 ? `${uid}-flame` : `${uid}-gold`;
        const phase = ['A', 'B', 'C'][i % 3];
        return (
          <g key={`pl-${i}`} transform={`rotate(${angle} 50 50)`} className={`plume plume-${phase}`}>
            {/* feather body */}
            <path
              d="M50 1 C 46 6, 46 14, 50 18 C 54 14, 54 6, 50 1 Z"
              fill={`url(#${gradRef})`}
              stroke="#5a1a08"
              strokeWidth="0.4"
            />
            {/* feather quill spine */}
            <path d="M50 1.5 L 50 17.5" stroke="#3a0e04" strokeWidth="0.35" opacity="0.8" />
            {/* feather barbs */}
            <path d="M50 6  L 47.5 7.5  M 50 6  L 52.5 7.5" stroke="#3a0e04" strokeWidth="0.25" opacity="0.6" />
            <path d="M50 10 L 47   12   M 50 10 L 53   12"   stroke="#3a0e04" strokeWidth="0.25" opacity="0.6" />
            <path d="M50 14 L 47.5 15.5 M 50 14 L 52.5 15.5" stroke="#3a0e04" strokeWidth="0.25" opacity="0.6" />
            {/* feather noise texture */}
            <path
              d="M50 1 C 46 6, 46 14, 50 18 C 54 14, 54 6, 50 1 Z"
              filter={`url(#${uid}-tex)`}
              opacity="0.45"
            />
          </g>
        );
      });
      const embers = [0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad - Math.PI / 2) * 24;
        const cy = 50 + Math.sin(rad - Math.PI / 2) * 24;
        const phase = ['A', 'B', 'C'][i % 3];
        return <circle key={`pe-${i}`} cx={cx} cy={cy} r="1" fill="#ffe6a8" className={`pember pember-${phase}`} />;
      });
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-flame`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#fff0c4" />
                <stop offset="25%" stopColor="#ffaa3d" />
                <stop offset="55%" stopColor="#ff5a28" />
                <stop offset="85%" stopColor="#a02418" />
                <stop offset="100%" stopColor="#3a0e04" />
              </linearGradient>
              <linearGradient id={`${uid}-gold`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#fff6d8" />
                <stop offset="30%" stopColor="#f5c050" />
                <stop offset="60%" stopColor="#c8852a" />
                <stop offset="85%" stopColor="#7a4818" />
                <stop offset="100%" stopColor="#3a1808" />
              </linearGradient>
              <radialGradient id={`${uid}-crimsonHalo`} cx="50%" cy="50%" r="50%">
                <stop offset="75%" stopColor="#a04020" stopOpacity="0" />
                <stop offset="92%" stopColor="#ff5a28" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#ff5a28" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-goldHalo`} cx="50%" cy="50%" r="48%">
                <stop offset="78%" stopColor="#f5c050" stopOpacity="0" />
                <stop offset="94%" stopColor="#ffe6a8" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#ffe6a8" stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-drop`} x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.6" />
                <feOffset dx="0" dy="0.8" />
                <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id={`${uid}-tex`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.4" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.6  0 0 0 0 0.3  0 0 0 0 0.05  0 0 0 0.25 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
            </defs>
            {/* Crimson outer halo */}
            <circle cx="50" cy="50" r="49" fill={`url(#${uid}-crimsonHalo)`} className="pf-halo-outer" />
            {/* Gold inner halo */}
            <circle cx="50" cy="50" r="47" fill={`url(#${uid}-goldHalo)`} className="pf-halo-inner" />
            {/* Sturdy crimson trim */}
            <circle cx="50" cy="50" r="48" fill="none" stroke="#a04020" strokeWidth="1.2" />
            {/* Plumes — drop-shadowed, hue-rotated globally */}
            <g className="plumes" filter={`url(#${uid}-drop)`}>{plumes}</g>
            {/* Rising ember sparks */}
            <g className="pembers">{embers}</g>
          </>
        ),
        className: 'border-phoenix-feather',
      };
    },
  },

  // 12. gilded-rose-ring — gold filigree with rose-gold accents.
  //
  // Phase D pass-2 Tier 4 — 5-stop gold-filigree gradient, fractalNoise
  // metallic texture overlay, 6 detailed rose buds with layered petals
  // + center stamen + leaf accents, gold filigree connector arcs
  // between buds, double-stacked glow halos (gold + rose), drop-shadow
  // filter, plus subtle bud-pulse animation on staggered phases.
  {
    id: 'gilded-rose-ring',
    render: ({ uid = 'gr' }) => {
      const roses = [0, 60, 120, 180, 240, 300].map((angle, i) => {
        const rad = (angle * Math.PI) / 180;
        const cx = 50 + Math.cos(rad - Math.PI / 2) * 47;
        const cy = 50 + Math.sin(rad - Math.PI / 2) * 47;
        const phase = ['A', 'B', 'C'][i % 3];
        return (
          <g key={`rose-${angle}`} className={`gr-bud gr-bud-${phase}`} transform={`rotate(${angle} 50 50) translate(${cx - 50} ${cy - 50}) rotate(${-angle})`}>
            {/* outer petal layer */}
            <circle cx="0" cy="0" r="2.6" fill={`url(#${uid}-petalOuter)`} stroke="#7a3e58" strokeWidth="0.4" />
            {/* mid petal layer */}
            <circle cx="0" cy="0" r="1.8" fill={`url(#${uid}-petalMid)`} />
            {/* center stamen */}
            <circle cx="0" cy="0" r="0.8" fill="#f5d6c0" />
            {/* glint highlight */}
            <circle cx="-0.6" cy="-0.6" r="0.4" fill="#fff8e8" opacity="0.9" />
            {/* leaf accents flanking the bud */}
            <path d="M-3.4 0.6 Q -4.8 1.6 -3.6 2.4" stroke="#7a8a3a" strokeWidth="0.5" fill="none" strokeLinecap="round" />
            <path d="M3.4 0.6 Q 4.8 1.6 3.6 2.4" stroke="#7a8a3a" strokeWidth="0.5" fill="none" strokeLinecap="round" />
          </g>
        );
      });
      // 6 filigree connecting arcs between buds.
      const arcs = [0, 60, 120, 180, 240, 300].map((a) => {
        const a1 = (a * Math.PI) / 180;
        const a2 = ((a + 60) * Math.PI) / 180;
        const p1x = 50 + Math.cos(a1 - Math.PI / 2) * 45;
        const p1y = 50 + Math.sin(a1 - Math.PI / 2) * 45;
        const p2x = 50 + Math.cos(a2 - Math.PI / 2) * 45;
        const p2y = 50 + Math.sin(a2 - Math.PI / 2) * 45;
        const ctrlX = 50 + Math.cos(((a + 30) * Math.PI) / 180 - Math.PI / 2) * 49;
        const ctrlY = 50 + Math.sin(((a + 30) * Math.PI) / 180 - Math.PI / 2) * 49;
        return (
          <path
            key={`arc-${a}`}
            d={`M ${p1x} ${p1y} Q ${ctrlX} ${ctrlY} ${p2x} ${p2y}`}
            fill="none"
            stroke={`url(#${uid}-filigree)`}
            strokeWidth="0.7"
            opacity="0.85"
          />
        );
      });
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-band`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#fff8d8" />
                <stop offset="25%" stopColor="#f5d680" />
                <stop offset="55%" stopColor="#e8c870" />
                <stop offset="80%" stopColor="#a08438" />
                <stop offset="100%" stopColor="#5a4418" />
              </linearGradient>
              <linearGradient id={`${uid}-filigree`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"  stopColor="#a08438" />
                <stop offset="50%" stopColor="#fff8d8" />
                <stop offset="100%" stopColor="#a08438" />
              </linearGradient>
              <radialGradient id={`${uid}-petalOuter`} cx="35%" cy="35%" r="65%">
                <stop offset="0%"  stopColor="#ffd8c8" />
                <stop offset="55%" stopColor="#f5b09c" />
                <stop offset="100%" stopColor="#a05878" />
              </radialGradient>
              <radialGradient id={`${uid}-petalMid`} cx="40%" cy="40%" r="55%">
                <stop offset="0%"  stopColor="#fff0e8" />
                <stop offset="60%" stopColor="#f5d0c0" />
                <stop offset="100%" stopColor="#d8a098" />
              </radialGradient>
              <radialGradient id={`${uid}-goldHalo`} cx="50%" cy="50%" r="55%">
                <stop offset="80%" stopColor="#f5d680" stopOpacity="0" />
                <stop offset="94%" stopColor="#f5d680" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#f5d680" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-roseHalo`} cx="50%" cy="50%" r="50%">
                <stop offset="78%" stopColor="#d8a888" stopOpacity="0" />
                <stop offset="94%" stopColor="#f5b09c" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#f5b09c" stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-drop`} x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.7" />
                <feOffset dx="0" dy="0.8" />
                <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id={`${uid}-tex`} x="0%" y="0%" width="100%" height="100%">
                <feTurbulence type="fractalNoise" baseFrequency="0.22" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.5  0 0 0 0 0.4  0 0 0 0 0.15  0 0 0 0.32 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
            </defs>
            {/* Outer gold halo */}
            <circle cx="50" cy="50" r="49" fill={`url(#${uid}-goldHalo)`} className="gr-halo-outer" />
            {/* Inner rose halo */}
            <circle cx="50" cy="50" r="46" fill={`url(#${uid}-roseHalo)`} className="gr-halo-inner" />
            <g filter={`url(#${uid}-drop)`}>
              {/* Outer gold filigree band — gradient + texture */}
              <circle cx="50" cy="50" r="47" fill="none" stroke={`url(#${uid}-band)`} strokeWidth="2.5" />
              <circle cx="50" cy="50" r="47" fill="none" stroke="#000" strokeWidth="2.5" filter={`url(#${uid}-tex)`} opacity="0.4" />
              {/* Inner rose-gold trim */}
              <circle cx="50" cy="50" r="43" fill="none" stroke="#d8a888" strokeWidth="0.8" />
              {/* Filigree connector arcs */}
              <g className="gr-filigree">{arcs}</g>
              {/* Roses with layered petals */}
              {roses}
            </g>
          </>
        ),
        className: 'border-gilded-rose-ring',
      };
    },
  },
];

/** Background plates. 3 lantern-shop entries + forges-maw (Crimson
 *  Solstice tier 50 premium). */
export const BACKGROUNDS: BackgroundDef[] = [
  // 1. tavern-hearth — warm radial gradient (orange center → dark edge).
  //    Static. Rarity 2. lantern-shop 500.
  //
  // Phase B Tier 2 — 3-stop radial gradient + accent layer: outer
  // vignette dark ring + 3 static flame tongues silhouetted at the
  // bottom + 3 static ember dots. No animation.
  {
    id: 'tavern-hearth',
    render: ({ uid = 'th' }) => {
      return {
        children: (
          <>
            <defs>
              <radialGradient id={`${uid}-grad`} cx="50%" cy="58%" r="55%">
                <stop offset="0%"  stopColor="#ffd27a" />
                <stop offset="30%" stopColor="#ffaa3d" />
                <stop offset="60%" stopColor="#a04020" />
                <stop offset="100%" stopColor="#1a0808" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${uid}-grad)`} />
            {/* Outer dark vignette ring */}
            <circle cx="50" cy="50" r="45" fill="none" stroke="#1a0808" strokeWidth="2" opacity="0.55" />
            {/* Static flame tongues at the bottom */}
            <path d="M22 96 C 20 88, 26 84, 24 78 C 28 84, 28 92, 26 96 Z" fill="#ff7820" opacity="0.65" />
            <path d="M48 96 C 46 86, 52 80, 50 72 C 54 80, 54 92, 52 96 Z" fill="#ffaa3d" opacity="0.7" />
            <path d="M74 96 C 72 88, 78 84, 76 78 C 80 84, 80 92, 78 96 Z" fill="#ff7820" opacity="0.65" />
            {/* Static ember dots above the flames */}
            <circle cx="32" cy="76" r="0.9" fill="#ffe6a8" opacity="0.8" />
            <circle cx="62" cy="68" r="0.7" fill="#ffe6a8" opacity="0.8" />
            <circle cx="84" cy="78" r="0.8" fill="#ffd27a" opacity="0.75" />
          </>
        ),
        className: 'background-tavern-hearth',
      };
    },
  },

  // 2. forest-grove — green linearGradient with 3 floating leaves.
  //    Leaves drift translateY. Rarity 3. lantern-shop 800.
  //
  // Phase C Tier 3 — layered upgrade: 4-stop forest gradient with
  // sunbeam shaft, 7 leaves (drifting on A-G phases — leaves recycle
  // existing leaf-A/B/C classes), branch silhouettes at top edges,
  // 3 firefly dots opacity-pulsing on staggered phases via
  // .fg-firefly-A/B/C.
  {
    id: 'forest-grove',
    render: ({ uid = 'fg' }) => {
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-grad`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#5a8a3a" />
                <stop offset="35%"  stopColor="#3a6028" />
                <stop offset="70%"  stopColor="#1a3818" />
                <stop offset="100%" stopColor="#0e2008" />
              </linearGradient>
              <linearGradient id={`${uid}-sunbeam`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"   stopColor="#f4e8a8" stopOpacity="0" />
                <stop offset="45%"  stopColor="#f4e8a8" stopOpacity="0.22" />
                <stop offset="55%"  stopColor="#fff8d8" stopOpacity="0.26" />
                <stop offset="100%" stopColor="#f4e8a8" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Base gradient */}
            <rect x="0" y="0" width="100" height="100" fill={`url(#${uid}-grad)`} />
            {/* Sunbeam shaft — diagonal warm light */}
            <rect x="0" y="0" width="100" height="100" fill={`url(#${uid}-sunbeam)`} className="fg-sunbeam" />
            {/* Branch silhouettes at top edges */}
            <path d="M0 10 Q 14 14 22 8 Q 30 4 36 12" stroke="#0e2008" strokeWidth="1.6" fill="none" opacity="0.65" strokeLinecap="round" />
            <path d="M100 18 Q 86 22 78 14 Q 70 8 64 16" stroke="#0e2008" strokeWidth="1.4" fill="none" opacity="0.65" strokeLinecap="round" />
            <path d="M0 92 Q 12 86 18 90" stroke="#0e2008" strokeWidth="1" fill="none" opacity="0.5" strokeLinecap="round" />
            {/* Twigs */}
            <path d="M22 8 L 18 4" stroke="#0e2008" strokeWidth="0.5" opacity="0.6" />
            <path d="M78 14 L 82 10" stroke="#0e2008" strokeWidth="0.5" opacity="0.6" />
            {/* Leaves — 7 total, recycle leaf-A/B/C drift phases */}
            <g className="leaf leaf-A">
              <path d="M22 28 C 18 32, 18 36, 22 38 C 26 36, 26 32, 22 28 Z" fill="#5a8a3a" opacity="0.78" stroke="#3a5a20" strokeWidth="0.3" />
            </g>
            <g className="leaf leaf-B">
              <path d="M78 50 C 74 54, 74 58, 78 60 C 82 58, 82 54, 78 50 Z" fill="#6ea042" opacity="0.78" stroke="#3a5a20" strokeWidth="0.3" />
            </g>
            <g className="leaf leaf-C">
              <path d="M40 76 C 36 80, 36 84, 40 86 C 44 84, 44 80, 40 76 Z" fill="#4a7830" opacity="0.78" stroke="#3a5a20" strokeWidth="0.3" />
            </g>
            <g className="leaf leaf-A">
              <path d="M56 32 C 53 35, 53 39, 56 41 C 59 39, 59 35, 56 32 Z" fill="#5a8a3a" opacity="0.72" stroke="#3a5a20" strokeWidth="0.3" />
            </g>
            <g className="leaf leaf-B">
              <path d="M14 56 C 11 59, 11 62, 14 64 C 17 62, 17 59, 14 56 Z" fill="#6ea042" opacity="0.72" stroke="#3a5a20" strokeWidth="0.3" />
            </g>
            <g className="leaf leaf-C">
              <path d="M68 78 C 65 81, 65 85, 68 86 C 72 85, 72 81, 68 78 Z" fill="#4a7830" opacity="0.72" stroke="#3a5a20" strokeWidth="0.3" />
            </g>
            <g className="leaf leaf-A">
              <path d="M86 32 C 84 34, 84 37, 86 38 C 88 37, 88 34, 86 32 Z" fill="#5a8a3a" opacity="0.65" stroke="#3a5a20" strokeWidth="0.3" />
            </g>
            {/* 3 firefly dots — opacity-pulsing on staggered phases */}
            <circle cx="32" cy="58" r="0.7" fill="#fff8d8" className="fg-firefly fg-firefly-A" />
            <circle cx="62" cy="42" r="0.6" fill="#fff8d8" className="fg-firefly fg-firefly-B" />
            <circle cx="48" cy="86" r="0.7" fill="#fff8d8" className="fg-firefly fg-firefly-C" />
          </>
        ),
        className: 'background-forest-grove',
      };
    },
  },

  // 3. starfield — deep cosmos with 3-depth parallax stars + nebula
  //    drift + shooting-star streaks. Rarity 4 rework. lantern-shop 1500.
  {
    id: 'starfield',
    render: ({ uid = 'sf' }) => {
      const gradId = `${uid}-grad`;
      const nebId  = `${uid}-neb`;
      const nebBId = `${uid}-nebB`;
      const milkyId = `${uid}-milky`;
      const dustId = `${uid}-dust`;
      // FAR small stars (twinkle fast, stagger 5 phases)
      const farStars: Array<{ cx: number; cy: number; r: number; phase: string }> = [
        { cx: 12, cy: 22, r: 0.6, phase: 'A' },
        { cx: 24, cy: 14, r: 0.5, phase: 'B' },
        { cx: 38, cy: 30, r: 0.6, phase: 'C' },
        { cx: 52, cy: 12, r: 0.5, phase: 'D' },
        { cx: 64, cy: 26, r: 0.7, phase: 'E' },
        { cx: 78, cy: 16, r: 0.5, phase: 'A' },
        { cx: 88, cy: 34, r: 0.6, phase: 'C' },
        { cx: 14, cy: 64, r: 0.5, phase: 'B' },
        { cx: 30, cy: 76, r: 0.6, phase: 'D' },
        { cx: 50, cy: 70, r: 0.5, phase: 'E' },
        { cx: 72, cy: 74, r: 0.6, phase: 'A' },
        { cx: 86, cy: 86, r: 0.5, phase: 'C' },
      ];
      // MID stars (slower twinkle)
      const midStars: Array<{ cx: number; cy: number; r: number; phase: string }> = [
        { cx: 22, cy: 44, r: 1.0, phase: 'A' },
        { cx: 60, cy: 50, r: 1.1, phase: 'C' },
        { cx: 82, cy: 58, r: 0.9, phase: 'E' },
        { cx: 36, cy: 84, r: 1.0, phase: 'B' },
      ];
      // NEAR stars (large, slow pulse)
      const nearStars: Array<{ cx: number; cy: number; r: number; phase: string }> = [
        { cx: 70, cy: 38, r: 1.7, phase: 'A' },
        { cx: 28, cy: 60, r: 1.6, phase: 'C' },
      ];
      return {
        children: (
          <>
            <defs>
              {/* 5-stop deep-space radial — center indigo → midnight */}
              <radialGradient id={gradId} cx="50%" cy="50%" r="65%">
                <stop offset="0%"   stopColor="#2a3478" />
                <stop offset="30%"  stopColor="#1a2454" />
                <stop offset="60%"  stopColor="#0a1230" />
                <stop offset="85%"  stopColor="#04081a" />
                <stop offset="100%" stopColor="#01020a" />
              </radialGradient>
              {/* primary magenta nebula */}
              <radialGradient id={nebId} cx="40%" cy="60%" r="55%">
                <stop offset="0%"   stopColor="#5a3a98" stopOpacity="0.55" />
                <stop offset="55%"  stopColor="#7a3aa8" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#5a3a98" stopOpacity="0" />
              </radialGradient>
              {/* secondary cyan nebula */}
              <radialGradient id={nebBId} cx="70%" cy="35%" r="40%">
                <stop offset="0%"   stopColor="#3a78b8" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#3a78b8" stopOpacity="0" />
              </radialGradient>
              {/* milky-way diagonal band */}
              <linearGradient id={milkyId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"   stopColor="#a8a0e8" stopOpacity="0" />
                <stop offset="48%"  stopColor="#a8a0e8" stopOpacity="0.18" />
                <stop offset="52%"  stopColor="#dcc8ff" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#a8a0e8" stopOpacity="0" />
              </linearGradient>
              {/* fractalNoise cosmic-dust layer */}
              <filter id={dustId}>
                <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.85  0 0 0 0 0.8  0 0 0 0 1  0 0 0 0.22 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            {/* milky-way diagonal sweep */}
            <g className="sf-milky">
              <rect x="0" y="0" width="100" height="100" fill={`url(#${milkyId})`} />
            </g>
            {/* primary nebula — slow rotate + hue-rotate */}
            <g className="sf-neb">
              <ellipse cx="50" cy="50" rx="45" ry="32" fill={`url(#${nebId})`} />
            </g>
            {/* secondary nebula — counter rotate + opposite hue */}
            <g className="sf-neb-B">
              <ellipse cx="50" cy="50" rx="30" ry="22" fill={`url(#${nebBId})`} />
            </g>
            {/* cosmic dust layer — fractalNoise overlay */}
            <rect x="0" y="0" width="100" height="100" filter={`url(#${dustId})`} opacity="0.45" />
            {/* FAR stars layer — fast twinkle */}
            <g className="sf-far">
              {farStars.map((s, i) => (
                <circle key={`fs-${i}`} cx={s.cx} cy={s.cy} r={s.r} fill="#fff8d8" className={`star star-${s.phase}`} />
              ))}
            </g>
            {/* MID stars layer — medium twinkle */}
            <g className="sf-mid">
              {midStars.map((s, i) => (
                <circle key={`ms-${i}`} cx={s.cx} cy={s.cy} r={s.r} fill="#dcc8ff" className={`star star-${s.phase}`} />
              ))}
            </g>
            {/* NEAR stars layer — slow large pulse, soft glow */}
            <g className="sf-near">
              {nearStars.map((s, i) => (
                <g key={`ns-${i}`}>
                  <circle cx={s.cx} cy={s.cy} r={s.r * 2.6} fill="#ffffff" opacity="0.18" />
                  <circle cx={s.cx} cy={s.cy} r={s.r} fill="#ffffff" className={`star star-${s.phase}`} />
                </g>
              ))}
            </g>
            {/* Shooting star — long thin streak with bright tail */}
            <g className="sf-shoot">
              <path d="M -10 30 L 14 22" stroke="#ffffff" strokeWidth="0.8" strokeLinecap="round" opacity="0.9" />
              <circle cx="14" cy="22" r="0.9" fill="#ffffff" />
            </g>
          </>
        ),
        className: 'background-starfield',
      };
    },
  },

  // 4. forges-maw — CRIMSON SOLSTICE TIER 50, premium-only.
  //    Mythic rarity-5 rework. Layered: throbbing crimson core, drifting
  //    smoke wisps, rising embers (5 with stagger phases), pulsing
  //    hot-spot coals.
  {
    id: 'forges-maw',
    render: ({ uid = 'fm' }) => {
      const gradId = `${uid}-grad`;
      const coreId = `${uid}-core`;
      const smokeId = `${uid}-smoke`;
      const coalId = `${uid}-coal`;
      const heatId = `${uid}-heat`;
      const ashId = `${uid}-ash`;
      const embers: Array<{ cx: number; cy: number; r: number; phase: string }> = [
        { cx: 22, cy: 70, r: 0.9, phase: 'A' },
        { cx: 38, cy: 82, r: 0.7, phase: 'B' },
        { cx: 56, cy: 74, r: 0.8, phase: 'C' },
        { cx: 70, cy: 86, r: 0.7, phase: 'D' },
        { cx: 84, cy: 76, r: 0.9, phase: 'E' },
      ];
      return {
        children: (
          <>
            <defs>
              {/* 5-stop deep-forge gradient — char black → blood → crimson */}
              <radialGradient id={gradId} cx="50%" cy="58%" r="62%">
                <stop offset="0%"   stopColor="#7a1a10" />
                <stop offset="22%"  stopColor="#4a0a0a" />
                <stop offset="55%"  stopColor="#280404" />
                <stop offset="85%"  stopColor="#0a0202" />
                <stop offset="100%" stopColor="#000000" />
              </radialGradient>
              {/* throbbing maw core — bright orange center */}
              <radialGradient id={coreId} cx="50%" cy="60%" r="42%">
                <stop offset="0%"  stopColor="#ffe6a8" stopOpacity="0.85" />
                <stop offset="22%" stopColor="#ff7820" stopOpacity="0.85" />
                <stop offset="55%" stopColor="#a02410" stopOpacity="0.55" />
                <stop offset="85%" stopColor="#4a0a0a" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#4a0a0a" stopOpacity="0" />
              </radialGradient>
              {/* heat-haze ring around the maw */}
              <radialGradient id={heatId} cx="50%" cy="60%" r="55%">
                <stop offset="45%" stopColor="#ff7820" stopOpacity="0" />
                <stop offset="65%" stopColor="#ff7820" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#ff7820" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={smokeId} cx="50%" cy="50%" r="50%">
                <stop offset="0%"   stopColor="#5a3828" stopOpacity="0.65" />
                <stop offset="60%"  stopColor="#3a2018" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#3a2018" stopOpacity="0" />
              </radialGradient>
              {/* coal-bed surface gradient for each coal */}
              <radialGradient id={coalId} cx="40%" cy="40%" r="65%">
                <stop offset="0%"   stopColor="#ffe6a8" />
                <stop offset="40%"  stopColor="#ffaa3d" />
                <stop offset="80%"  stopColor="#a02410" />
                <stop offset="100%" stopColor="#280404" />
              </radialGradient>
              {/* ash / soot fractalNoise overlay */}
              <filter id={ashId}>
                <feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="3" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.15  0 0 0 0 0.08  0 0 0 0 0.04  0 0 0 0.4 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
              <filter id={`${uid}-coalDrop`} x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" />
                <feOffset dx="0" dy="0.8" />
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            {/* ash / soot fractalNoise overlay */}
            <rect x="0" y="0" width="100" height="100" filter={`url(#${ashId})`} opacity="0.55" />
            {/* heat-haze halo around the maw */}
            <ellipse cx="50" cy="60" rx="48" ry="38" fill={`url(#${heatId})`} className="forge-heat" />
            {/* throbbing crimson maw core */}
            <g className="forge-core">
              <ellipse cx="50" cy="60" rx="34" ry="26" fill={`url(#${coreId})`} />
            </g>
            {/* inner bright eye — pulses faster than the core */}
            <g className="forge-eye">
              <ellipse cx="50" cy="62" rx="14" ry="10" fill="#ffaa3d" opacity="0.6" />
              <ellipse cx="50" cy="62" rx="8"  ry="6"  fill="#ffe6a8" opacity="0.7" />
            </g>
            {/* drifting smoke wisp #1 */}
            <g className="forge-smoke-A">
              <ellipse cx="30" cy="32" rx="22" ry="10" fill={`url(#${smokeId})`} />
            </g>
            {/* drifting smoke wisp #2 — opposite phase */}
            <g className="forge-smoke-B">
              <ellipse cx="70" cy="22" rx="20" ry="8" fill={`url(#${smokeId})`} />
            </g>
            {/* hot coals — gradient + drop-shadow + pulse on stagger phases */}
            <g filter={`url(#${uid}-coalDrop)`}>
              <circle cx="28" cy="64" r="3.4" fill={`url(#${coalId})`} className="coal coal-A" />
              <circle cx="60" cy="76" r="2.8" fill={`url(#${coalId})`} className="coal coal-B" />
              <circle cx="76" cy="50" r="3.0" fill={`url(#${coalId})`} className="coal coal-C" />
              {/* bright cinder glints on each coal */}
              <circle cx="27.2" cy="63.2" r="0.7" fill="#fff6d8" opacity="0.95" />
              <circle cx="59.2" cy="75.4" r="0.6" fill="#fff6d8" opacity="0.9" />
              <circle cx="75.2" cy="49.2" r="0.6" fill="#fff6d8" opacity="0.95" />
            </g>
            {/* rising embers */}
            <g className="forge-embers">
              {embers.map((e, i) => (
                <circle key={`fe-${i}`} cx={e.cx} cy={e.cy} r={e.r} fill="#ffe6a8" className={`fember fember-${e.phase}`} />
              ))}
            </g>
          </>
        ),
        className: 'background-forges-maw',
      };
    },
  },

  // ───────── Phase F6 additions — 4 new avatar backgrounds ─────────
  // Bug-2 companion fix — F5 only added new SEAT backgrounds; the
  // avatar BACKGROUNDS registry was stuck at 4. These four widen the
  // selection so players who buy a non-Solstice background actually
  // have variety in the locker.

  // 5. parchment-scroll — warm cream parchment with faint ink lines.
  //    Static. Rarity 2.
  //
  // Phase B Tier 2 — 3-stop parchment gradient + accent layer: edge
  // curl shadow bands + calligraphic flourish + small red wax seal in a
  // corner. Static (no animation).
  {
    id: 'parchment-scroll',
    render: ({ uid = 'ps' }) => {
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-grad`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#f5e6c4" />
                <stop offset="55%"  stopColor="#dec59a" />
                <stop offset="100%" stopColor="#c8a878" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${uid}-grad)`} />
            {/* Edge curl shadow bands (top + bottom) */}
            <rect x="0" y="0" width="100" height="3" fill="#8a6a3a" opacity="0.4" />
            <rect x="0" y="97" width="100" height="3" fill="#8a6a3a" opacity="0.4" />
            {/* Faint ink horizontal lines */}
            <path d="M 8 30 L 92 30" stroke="#8a6a3a" strokeWidth="0.3" opacity="0.4" />
            <path d="M 8 42 L 78 42" stroke="#8a6a3a" strokeWidth="0.3" opacity="0.35" />
            <path d="M 8 54 L 92 54" stroke="#8a6a3a" strokeWidth="0.3" opacity="0.4" />
            <path d="M 8 66 L 84 66" stroke="#8a6a3a" strokeWidth="0.3" opacity="0.35" />
            <path d="M 8 78 L 92 78" stroke="#8a6a3a" strokeWidth="0.3" opacity="0.4" />
            {/* Calligraphic flourish on one side */}
            <path d="M 12 18 Q 22 14 30 20 Q 38 26 30 22 Q 22 18 12 18 Z" fill="none" stroke="#5a3a18" strokeWidth="0.5" opacity="0.55" />
            <path d="M 70 86 Q 78 84 84 88" fill="none" stroke="#5a3a18" strokeWidth="0.5" opacity="0.5" strokeLinecap="round" />
            {/* Wax seal in the bottom-right corner */}
            <circle cx="82" cy="84" r="3.4" fill="#a02020" stroke="#5a1010" strokeWidth="0.5" />
            <circle cx="81.4" cy="83.4" r="1" fill="#ff4040" opacity="0.7" />
            {/* Seal emblem */}
            <path d="M80.6 84 L 82 85.6 L 83.4 84 L 82 82.4 Z" stroke="#5a1010" strokeWidth="0.3" fill="none" />
          </>
        ),
        className: 'background-parchment-scroll',
      };
    },
  },

  // 6. velvet-drape — deep purple curtain with vertical highlight.
  //    Static. Rarity 3.
  //
  // Phase C Tier 3 — layered upgrade: 4-stop velvet gradient, 5
  // additional fold stripes at varied widths, dark top valance with
  // scalloped bottom edge, gold tassel cluster at bottom-center, ONE
  // diagonal sheen sweep animation via .vd-sheen.
  {
    id: 'velvet-drape',
    render: ({ uid = 'vd' }) => {
      return {
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-velvet`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="#1a0628" />
                <stop offset="30%"  stopColor="#2a0e44" />
                <stop offset="55%"  stopColor="#5a2e88" />
                <stop offset="100%" stopColor="#2a0e44" />
              </linearGradient>
              <linearGradient id={`${uid}-sheen`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"   stopColor="#ffffff" stopOpacity="0" />
                <stop offset="48%"  stopColor="#ffffff" stopOpacity="0" />
                <stop offset="52%"  stopColor="#ffffff" stopOpacity="0.18" />
                <stop offset="56%"  stopColor="#ffffff" stopOpacity="0" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Base velvet */}
            <rect x="0" y="0" width="100" height="100" fill={`url(#${uid}-velvet)`} />
            {/* Fold stripes — vertical with varied widths/opacities */}
            <rect x="14" y="0" width="2" height="100" fill="#1a0628" opacity="0.4" />
            <rect x="28" y="0" width="3" height="100" fill="#7a4ea0" opacity="0.35" />
            <rect x="42" y="0" width="2.4" height="100" fill="#1a0628" opacity="0.35" />
            <rect x="55" y="0" width="3" height="100" fill="#7a4ea0" opacity="0.32" />
            <rect x="68" y="0" width="3" height="100" fill="#7a4ea0" opacity="0.35" />
            <rect x="82" y="0" width="2" height="100" fill="#1a0628" opacity="0.4" />
            {/* Soft highlight bands (right of fold creases) */}
            <rect x="32" y="0" width="1.4" height="100" fill="#a06ed0" opacity="0.2" />
            <rect x="72" y="0" width="1.4" height="100" fill="#a06ed0" opacity="0.2" />
            {/* Top valance — dark band with scalloped bottom edge */}
            <rect x="0" y="0" width="100" height="9" fill="#0a0218" opacity="0.92" />
            <path d="M0 9 Q 5 12 10 9 Q 15 12 20 9 Q 25 12 30 9 Q 35 12 40 9 Q 45 12 50 9 Q 55 12 60 9 Q 65 12 70 9 Q 75 12 80 9 Q 85 12 90 9 Q 95 12 100 9"
                  fill="#0a0218" stroke="none" />
            {/* Valance gold trim */}
            <path d="M0 9 Q 5 12 10 9 Q 15 12 20 9 Q 25 12 30 9 Q 35 12 40 9 Q 45 12 50 9 Q 55 12 60 9 Q 65 12 70 9 Q 75 12 80 9 Q 85 12 90 9 Q 95 12 100 9"
                  fill="none" stroke="#e8c870" strokeWidth="0.4" opacity="0.85" />
            {/* Sheen sweep — diagonal bright highlight (animates slowly) */}
            <g className="vd-sheen">
              <rect x="-40" y="-20" width="80" height="180" fill={`url(#${uid}-sheen)`} transform="rotate(-20 50 50)" />
            </g>
            {/* Gold tassel cluster at bottom-center */}
            <rect x="46" y="92" width="8" height="2" fill="#e8c870" />
            <path d="M48 94 L 47 100" stroke="#e8c870" strokeWidth="0.7" />
            <path d="M50 94 L 50 100" stroke="#e8c870" strokeWidth="0.7" />
            <path d="M52 94 L 53 100" stroke="#e8c870" strokeWidth="0.7" />
            <circle cx="50" cy="93" r="1.2" fill="#f5d680" stroke="#a08438" strokeWidth="0.3" />
          </>
        ),
        className: 'background-velvet-drape',
      };
    },
  },

  // 7. tide-pool-wash — soft aqua / teal with rippling highlights.
  //    Animated rarity 3.
  //
  // Phase C Tier 3 — layered upgrade: 4-stop water gradient, 6 ripples
  // (varied sizes), sand bottom hint band, caustic light criss-cross
  // strokes, 3 rising bubbles via .tp-bubble-A/B/C animations. Keeps
  // existing .ripple-A/B/C classes.
  {
    id: 'tide-pool-wash',
    render: ({ uid = 'tp' }) => {
      return {
        children: (
          <>
            <defs>
              <radialGradient id={`${uid}-water`} cx="50%" cy="40%" r="75%">
                <stop offset="0%"   stopColor="#cfeef0" />
                <stop offset="30%"  stopColor="#9be0d8" />
                <stop offset="65%"  stopColor="#3a8a9e" />
                <stop offset="100%" stopColor="#0e2c3c" />
              </radialGradient>
              <linearGradient id={`${uid}-sand`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#9aa050" stopOpacity="0" />
                <stop offset="100%" stopColor="#7a8a3c" stopOpacity="0.6" />
              </linearGradient>
            </defs>
            {/* Base water */}
            <rect x="0" y="0" width="100" height="100" fill={`url(#${uid}-water)`} />
            {/* Sand bottom hint */}
            <rect x="0" y="80" width="100" height="20" fill={`url(#${uid}-sand)`} />
            {/* Sand-grain dots */}
            <circle cx="14" cy="92" r="0.4" fill="#cdb674" opacity="0.7" />
            <circle cx="34" cy="96" r="0.5" fill="#cdb674" opacity="0.65" />
            <circle cx="56" cy="93" r="0.4" fill="#cdb674" opacity="0.7" />
            <circle cx="76" cy="96" r="0.5" fill="#cdb674" opacity="0.65" />
            <circle cx="92" cy="92" r="0.4" fill="#cdb674" opacity="0.7" />
            {/* Caustic light criss-cross strokes (opacity-pulse via existing ripple classes) */}
            <path d="M14 30 Q 30 36 46 32" stroke="#fff" strokeWidth="0.4" fill="none" opacity="0.25" className="ripple ripple-A" />
            <path d="M50 22 Q 64 30 80 26" stroke="#fff" strokeWidth="0.4" fill="none" opacity="0.25" className="ripple ripple-B" />
            <path d="M10 50 Q 26 46 42 52" stroke="#fff" strokeWidth="0.35" fill="none" opacity="0.22" className="ripple ripple-C" />
            {/* 6 ripple ovals — varied sizes + phases */}
            <ellipse cx="36" cy="44" rx="8" ry="2" fill="none" stroke="#cfeef0" strokeWidth="0.5" opacity="0.6" className="ripple ripple-A" />
            <ellipse cx="64" cy="60" rx="10" ry="2.5" fill="none" stroke="#cfeef0" strokeWidth="0.5" opacity="0.6" className="ripple ripple-B" />
            <ellipse cx="50" cy="76" rx="9" ry="2" fill="none" stroke="#cfeef0" strokeWidth="0.5" opacity="0.6" className="ripple ripple-C" />
            <ellipse cx="20" cy="64" rx="6" ry="1.6" fill="none" stroke="#cfeef0" strokeWidth="0.4" opacity="0.5" className="ripple ripple-A" />
            <ellipse cx="78" cy="38" rx="7" ry="1.8" fill="none" stroke="#cfeef0" strokeWidth="0.4" opacity="0.5" className="ripple ripple-B" />
            <ellipse cx="86" cy="74" rx="5" ry="1.4" fill="none" stroke="#cfeef0" strokeWidth="0.4" opacity="0.5" className="ripple ripple-C" />
            {/* 3 rising bubbles */}
            <circle cx="24" cy="80" r="0.7" fill="#cfeef0" opacity="0.7" className="tp-bubble tp-bubble-A" />
            <circle cx="48" cy="85" r="0.85" fill="#cfeef0" opacity="0.75" className="tp-bubble tp-bubble-B" />
            <circle cx="74" cy="82" r="0.7" fill="#cfeef0" opacity="0.7" className="tp-bubble tp-bubble-C" />
          </>
        ),
        className: 'background-tide-pool-wash',
      };
    },
  },

  // 8. cosmos-veil — galaxy. Rarity 4 rework. Layered: rotating
  //    pink/blue nebula, scattered stars at 3 depths twinkling on
  //    stagger phases, slow breathing radial glow.
  {
    id: 'cosmos-veil',
    render: ({ uid = 'cv' }) => {
      const gradId = `${uid}-grad`;
      const nebId  = `${uid}-neb`;
      const nebBId = `${uid}-nebB`;
      const nebCId = `${uid}-nebC`;
      const glowId = `${uid}-glow`;
      const galaxyId = `${uid}-galaxy`;
      const dustId = `${uid}-dust`;
      const haloId = `${uid}-halo`;
      return {
        children: (
          <>
            <defs>
              {/* 5-stop deep cosmos background — violet → indigo → black */}
              <radialGradient id={gradId} cx="50%" cy="50%" r="75%">
                <stop offset="0%"   stopColor="#3a1f7a" />
                <stop offset="28%"  stopColor="#2a1858" />
                <stop offset="60%"  stopColor="#10082a" />
                <stop offset="85%"  stopColor="#04081a" />
                <stop offset="100%" stopColor="#010108" />
              </radialGradient>
              {/* primary magenta nebula — multi-stop */}
              <radialGradient id={nebId} cx="40%" cy="45%" r="55%">
                <stop offset="0%"   stopColor="#ff8ad8" stopOpacity="0.55" />
                <stop offset="35%"  stopColor="#d050b8" stopOpacity="0.55" />
                <stop offset="70%"  stopColor="#7028a0" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#d050b8" stopOpacity="0" />
              </radialGradient>
              {/* secondary cyan-blue nebula */}
              <radialGradient id={nebBId} cx="65%" cy="60%" r="45%">
                <stop offset="0%"   stopColor="#80b0ff" stopOpacity="0.55" />
                <stop offset="40%"  stopColor="#5078ff" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#5078ff" stopOpacity="0" />
              </radialGradient>
              {/* third teal-green nebula wisp */}
              <radialGradient id={nebCId} cx="50%" cy="80%" r="35%">
                <stop offset="0%"   stopColor="#3aa098" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#3aa098" stopOpacity="0" />
              </radialGradient>
              {/* breathing radial glow */}
              <radialGradient id={glowId} cx="50%" cy="50%" r="55%">
                <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </radialGradient>
              {/* spiral galaxy hint — soft elliptical band */}
              <radialGradient id={galaxyId} cx="50%" cy="50%" r="60%">
                <stop offset="0%"   stopColor="#fff0d8" stopOpacity="0.28" />
                <stop offset="35%"  stopColor="#dcc8ff" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#dcc8ff" stopOpacity="0" />
              </radialGradient>
              {/* outer chromatic halo */}
              <radialGradient id={haloId} cx="50%" cy="50%" r="55%">
                <stop offset="78%" stopColor="#d050b8" stopOpacity="0" />
                <stop offset="92%" stopColor="#7a4ec8" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#7a4ec8" stopOpacity="0" />
              </radialGradient>
              {/* cosmic dust fractalNoise */}
              <filter id={dustId}>
                <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="3" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.85  0 0 0 0 0.7  0 0 0 0 1  0 0 0 0.18 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill={`url(#${gradId})`} />
            {/* outer chromatic halo */}
            <rect x="0" y="0" width="100" height="100" fill={`url(#${haloId})`} className="cv-halo-outer" />
            {/* breathing center glow */}
            <g className="cv-breath">
              <ellipse cx="50" cy="50" rx="48" ry="38" fill={`url(#${glowId})`} />
            </g>
            {/* spiral galaxy ellipse — slow rotate */}
            <g className="cv-galaxy">
              <ellipse cx="50" cy="50" rx="42" ry="14" fill={`url(#${galaxyId})`} transform="rotate(28 50 50)" />
            </g>
            {/* primary nebula — CW rotation + hue-rotate */}
            <g className="cv-neb-A">
              <ellipse cx="50" cy="50" rx="40" ry="30" fill={`url(#${nebId})`} />
            </g>
            {/* secondary nebula — CCW rotation, opposite hue */}
            <g className="cv-neb-B">
              <ellipse cx="50" cy="50" rx="34" ry="26" fill={`url(#${nebBId})`} />
            </g>
            {/* third nebula wisp at bottom */}
            <g className="cv-neb-C">
              <ellipse cx="50" cy="78" rx="34" ry="14" fill={`url(#${nebCId})`} />
            </g>
            {/* cosmic dust scattered overlay */}
            <rect x="0" y="0" width="100" height="100" filter={`url(#${dustId})`} opacity="0.4" />
            {/* near (large) stars — soft glow + bright core */}
            <g className="cv-near">
              <g>
                <circle cx="24" cy="22" r="3" fill="#fff8d8" opacity="0.2" />
                <circle cx="24" cy="22" r="1.2" fill="#fff8d8" className="star star-A" />
              </g>
              <g>
                <circle cx="78" cy="80" r="3" fill="#dcc8ff" opacity="0.2" />
                <circle cx="78" cy="80" r="1.3" fill="#dcc8ff" className="star star-C" />
              </g>
            </g>
            {/* mid stars */}
            <g className="cv-mid">
              <circle cx="86" cy="30" r="0.9" fill="#dcc8ff" className="star star-B" />
              <circle cx="14" cy="74" r="0.8" fill="#fff8d8" className="star star-D" />
              <circle cx="50" cy="86" r="0.9" fill="#dcc8ff" className="star star-E" />
              <circle cx="68" cy="42" r="0.8" fill="#fff8d8" className="star star-A" />
              <circle cx="32" cy="32" r="0.7" fill="#dcc8ff" className="star star-C" />
            </g>
            {/* far stars (lots of small ones) */}
            <g className="cv-far">
              <circle cx="14" cy="22" r="0.5" fill="#fff8d8" className="star star-A" />
              <circle cx="36" cy="60" r="0.5" fill="#dcc8ff" className="star star-C" />
              <circle cx="68" cy="14" r="0.5" fill="#fff8d8" className="star star-E" />
              <circle cx="92" cy="56" r="0.5" fill="#dcc8ff" className="star star-B" />
              <circle cx="50" cy="40" r="0.4" fill="#fff8d8" className="star star-D" />
              <circle cx="8"  cy="44" r="0.4" fill="#dcc8ff" className="star star-A" />
              <circle cx="58" cy="68" r="0.4" fill="#fff8d8" className="star star-C" />
              <circle cx="22" cy="48" r="0.4" fill="#fff8d8" className="star star-B" />
              <circle cx="78" cy="58" r="0.4" fill="#dcc8ff" className="star star-D" />
            </g>
          </>
        ),
        className: 'background-cosmos-veil',
      };
    },
  },
];

// ─────────────────────────────────────────────────────────────────────
// Phase F5 — Masks (face-overlay cosmetic family)
// ─────────────────────────────────────────────────────────────────────
//
// Masks render ON TOP of face features but UNDER the hat (z-order:
// background → body → face → MASK → hat → border). All paths sized to
// the same 100×100 SVG canvas the body / face / hat use; the eye region
// for FACE_SHAPES sits around y=50, so masks frame that area. Each mask
// reads as a half-mask covering the upper face — leaves the mouth
// readable. Animations apply via CSS classes in the cosmetic-animations
// section of `web/src/styles/global.css`. Most masks are static; mythic
// ones get a subtle shimmer/pulse to read as rare without spamming
// motion across a 4-seat lobby.
export const MASKS: MaskDef[] = [
  // 1. domino — simple black eye-mask. Static. Rarity 2 / lantern-shop.
  //
  // Phase B Tier 2 — 3-stop dark gradient (slight curvature shading) +
  // ink outline + accent: highlight glint along the upper edge + small
  // ribbon-tie hint at one edge. Static (no animation).
  {
    id: 'domino',
    label: 'Domino mask',
    render: ({ accentHue, uid = 'dm' }) => {
      const maskPath = "M22 44 C 22 38, 32 36, 42 38 L 50 40 L 58 38 C 68 36, 78 38, 78 44 C 78 56, 64 60, 54 56 L 50 54 L 46 56 C 36 60, 22 56, 22 44 Z";
      const ribbonFill = `hsl(${accentHue} 65% 45%)`;
      const ribbonEdge = `hsl(${accentHue} 70% 25%)`;
      return ({
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-grad`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#1c1428" />
                <stop offset="55%"  stopColor="#0a0710" />
                <stop offset="100%" stopColor="#04020a" />
              </linearGradient>
            </defs>
            {/* Drop shadow under mask */}
            <ellipse cx="50" cy="58" rx="26" ry="2" fill="#000" opacity="0.32" />
            {/* Base mask body with 3-stop gradient + ink outline */}
            <path d={maskPath} fill={`url(#${uid}-grad)`} stroke="#1a1018" strokeWidth="0.6" />
            {/* Upper-edge highlight glint */}
            <path d="M24 42 Q 36 38 50 40 Q 64 38 76 42" stroke="#3a3340" strokeWidth="0.6" fill="none" opacity="0.85" />
            {/* Eye-hole inner trim hints */}
            <path d="M34 46 Q 36 42 42 44" stroke="#3a3340" strokeWidth="0.35" fill="none" opacity="0.7" />
            <path d="M66 46 Q 64 42 58 44" stroke="#3a3340" strokeWidth="0.35" fill="none" opacity="0.7" />
            {/* Ribbon tie on the right edge */}
            <path d="M78 44 L 86 40 L 88 46 L 80 48 Z" fill={ribbonFill} stroke={ribbonEdge} strokeWidth="0.4" />
            <path d="M78 44 L 86 46 L 84 50 L 78 48 Z" fill={ribbonFill} stroke={ribbonEdge} strokeWidth="0.4" opacity="0.85" />
            {/* Ribbon knot accent */}
            <circle cx="79" cy="46" r="1" fill={ribbonEdge} />
          </>
        ),
      });
    },
  },

  // 2. feline — cat-eye mask with whisker hints. Static. Rarity 3.
  //
  // Phase C Tier 3 — layered upgrade: 3-stop gradient on mask body,
  // ear-tip triangles at top corners with inner pink, eye-hole color
  // trim, 3 curving whisker strokes per cheek replacing the dots,
  // ONE subtle eye-glow via .mask-feline-eye-glow (opacity pulse).
  {
    id: 'feline',
    label: 'Feline mask',
    render: ({ accentHue, uid = 'fl' }) => {
      const accentSoft = `hsl(${accentHue} 70% 55%)`;
      return ({
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-mask`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#2a1828" />
                <stop offset="55%"  stopColor="#1a0e16" />
                <stop offset="100%" stopColor="#0a0410" />
              </linearGradient>
            </defs>
            {/* Drop shadow under mask */}
            <ellipse cx="50" cy="58" rx="28" ry="1.8" fill="#000" opacity="0.32" />
            {/* Ear-tip triangles at top corners */}
            <path d="M24 38 L 22 28 L 30 34 Z" fill="#1a0e16" stroke="#3a1830" strokeWidth="0.4" />
            <path d="M76 38 L 78 28 L 70 34 Z" fill="#1a0e16" stroke="#3a1830" strokeWidth="0.4" />
            {/* Inner-ear pink triangle */}
            <path d="M25 36 L 24 32 L 28 34 Z" fill={accentSoft} opacity="0.7" />
            <path d="M75 36 L 76 32 L 72 34 Z" fill={accentSoft} opacity="0.7" />
            {/* Mask body with 3-stop gradient + ink outline */}
            <path
              d="M22 44 C 22 36, 32 32, 42 36 L 48 40 L 52 40 L 58 36 C 68 32, 78 36, 78 44 C 78 50, 70 58, 60 56 C 56 54, 52 50, 50 50 C 48 50, 44 54, 40 56 C 30 58, 22 50, 22 44 Z"
              fill={`url(#${uid}-mask)`}
              stroke="#3a1830"
              strokeWidth="0.6"
            />
            {/* Upper-edge highlight glint */}
            <path d="M26 40 Q 38 36 50 40 Q 62 36 74 40" stroke="#5a3848" strokeWidth="0.45" fill="none" opacity="0.85" />
            {/* Eye-hole inner trim */}
            <path d="M34 46 Q 36 42 42 44" stroke={accentSoft} strokeWidth="0.45" fill="none" opacity="0.7" />
            <path d="M66 46 Q 64 42 58 44" stroke={accentSoft} strokeWidth="0.45" fill="none" opacity="0.7" />
            {/* Eye-glow inner highlights — pulse */}
            <g className="mask-feline-eye-glow">
              <ellipse cx="38" cy="44" rx="1.8" ry="0.7" fill={accentSoft} opacity="0.65" />
              <ellipse cx="62" cy="44" rx="1.8" ry="0.7" fill={accentSoft} opacity="0.65" />
            </g>
            {/* Whisker strokes — 3 thin curves per cheek (replacing dots) */}
            <path d="M30 58 Q 26 60 22 60" stroke="#3a1830" strokeWidth="0.5" fill="none" opacity="0.8" strokeLinecap="round" />
            <path d="M30 60 Q 26 62 22 62.6" stroke="#3a1830" strokeWidth="0.5" fill="none" opacity="0.75" strokeLinecap="round" />
            <path d="M30 62 Q 26 64 22 65" stroke="#3a1830" strokeWidth="0.45" fill="none" opacity="0.7" strokeLinecap="round" />
            <path d="M70 58 Q 74 60 78 60" stroke="#3a1830" strokeWidth="0.5" fill="none" opacity="0.8" strokeLinecap="round" />
            <path d="M70 60 Q 74 62 78 62.6" stroke="#3a1830" strokeWidth="0.5" fill="none" opacity="0.75" strokeLinecap="round" />
            <path d="M70 62 Q 74 64 78 65" stroke="#3a1830" strokeWidth="0.45" fill="none" opacity="0.7" strokeLinecap="round" />
            {/* Tiny gem on the forehead/center */}
            <circle cx="50" cy="40" r="0.65" fill={accentSoft} stroke="#1a1410" strokeWidth="0.2" />
          </>
        ),
      });
    },
  },

  // 3. harlequin — diamond-pattern half mask. Static. Rarity 3.
  //
  // Phase C Tier 3 — layered upgrade: 3-stop crimson gradient on mask,
  // each diamond gets an inner smaller bright diamond, 4 more diamonds
  // added (8 total) at the bottom row, gold tassel/bell tips at corners,
  // dashed stitch border along mask edge. Static.
  {
    id: 'harlequin',
    label: 'Harlequin mask',
    render: ({ uid = 'hq' }) => {
      const diamondPositions = [
        { cx: 34, cy: 44 },
        { cx: 46, cy: 48 },
        { cx: 58, cy: 44 },
        { cx: 70, cy: 48 },
        { cx: 40, cy: 54 },
        { cx: 52, cy: 50 },
        { cx: 64, cy: 54 },
        { cx: 28, cy: 50 },
      ];
      const maskPath = "M22 44 C 22 36, 32 36, 42 38 L 50 40 L 58 38 C 68 36, 78 36, 78 44 C 78 56, 66 60, 54 56 L 50 54 L 46 56 C 34 60, 22 56, 22 44 Z";
      return ({
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-mask`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#d04a64" />
                <stop offset="55%"  stopColor="#a83a52" />
                <stop offset="100%" stopColor="#5a1a28" />
              </linearGradient>
              <radialGradient id={`${uid}-gem`} cx="35%" cy="35%" r="65%">
                <stop offset="0%"   stopColor="#fff8d8" />
                <stop offset="55%"  stopColor="#f1d780" />
                <stop offset="100%" stopColor="#a08438" />
              </radialGradient>
            </defs>
            {/* Drop shadow */}
            <ellipse cx="50" cy="58" rx="28" ry="1.6" fill="#000" opacity="0.32" />
            {/* Base mask with 3-stop gradient + ink outline */}
            <path d={maskPath} fill={`url(#${uid}-mask)`} stroke="#5a1a28" strokeWidth="0.6" />
            {/* Upper-edge highlight */}
            <path d="M26 40 Q 38 36 50 40 Q 62 36 74 40" stroke="#f1d780" strokeWidth="0.3" fill="none" opacity="0.7" />
            {/* Dashed stitch border along mask edge */}
            <path d={maskPath} fill="none" stroke="#5a1a28" strokeWidth="0.35" strokeDasharray="1.2 1" opacity="0.85" />
            {/* Diamond pattern overlay — outer + inner bright */}
            {diamondPositions.map((d, i) => (
              <g key={`dia-${i}`}>
                <path
                  d={`M${d.cx} ${d.cy - 4} L ${d.cx + 4} ${d.cy} L ${d.cx} ${d.cy + 4} L ${d.cx - 4} ${d.cy} Z`}
                  fill={`url(#${uid}-gem)`}
                  stroke="#5a4218"
                  strokeWidth="0.3"
                />
                <path
                  d={`M${d.cx} ${d.cy - 2.2} L ${d.cx + 2.2} ${d.cy} L ${d.cx} ${d.cy + 2.2} L ${d.cx - 2.2} ${d.cy} Z`}
                  fill="#fff8d8"
                  opacity="0.85"
                />
                <circle cx={d.cx - 0.7} cy={d.cy - 0.9} r="0.35" fill="#fff" opacity="0.95" />
              </g>
            ))}
            {/* Bell/tassel tips at corners */}
            <circle cx="20" cy="46" r="1.6" fill="#f1d780" stroke="#5a4218" strokeWidth="0.35" />
            <circle cx="19.6" cy="45.6" r="0.5" fill="#fff" opacity="0.85" />
            <path d="M19 47.4 L 18 50" stroke="#a08438" strokeWidth="0.5" strokeLinecap="round" />
            <circle cx="80" cy="46" r="1.6" fill="#f1d780" stroke="#5a4218" strokeWidth="0.35" />
            <circle cx="79.6" cy="45.6" r="0.5" fill="#fff" opacity="0.85" />
            <path d="M81 47.4 L 82 50" stroke="#a08438" strokeWidth="0.5" strokeLinecap="round" />
          </>
        ),
      });
    },
  },

  // 4. phantom — half mask, white with subtle glow. Animated rarity 4.
  //
  // Phase D pass-2 Tier 4 — 4-stop pearl gradient, fractalNoise porcelain
  // texture, double-stacked spectral halos (cool blue + warm gold),
  // drop-shadow filter, eye-hole socket with depth, painted-crack detail,
  // hairline gold trim, ghostly mote drifting on rising phase.
  {
    id: 'phantom',
    label: 'Phantom mask',
    render: ({ uid = 'ph' }) => {
      const maskPath = "M22 38 C 22 32, 32 30, 42 34 L 50 38 L 50 62 L 46 60 C 36 64, 22 56, 22 38 Z";
      return ({
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-pearl`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#ffffff" />
                <stop offset="35%" stopColor="#f4ecdc" />
                <stop offset="75%" stopColor="#d8c8a8" />
                <stop offset="100%" stopColor="#a89878" />
              </linearGradient>
              <radialGradient id={`${uid}-bloom`} cx="50%" cy="50%" r="60%">
                <stop offset="0%"   stopColor="#cfe4ff" stopOpacity="0.6" />
                <stop offset="60%"  stopColor="#a8c0d8" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#a8c0d8" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-warm`} cx="50%" cy="50%" r="60%">
                <stop offset="0%"   stopColor="#ffe6a8" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#ffe6a8" stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-drop`} x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.7" />
                <feOffset dx="0" dy="1" />
                <feComponentTransfer><feFuncA type="linear" slope="0.5" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id={`${uid}-porcelain`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.18" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.35  0 0 0 0 0.3  0 0 0 0 0.18  0 0 0 0.2 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
              <clipPath id={`${uid}-clip`}>
                <path d={maskPath} />
              </clipPath>
            </defs>
            {/* Spectral cool halo behind mask */}
            <ellipse cx="36" cy="48" rx="22" ry="18" fill={`url(#${uid}-bloom)`} className="phantom-bloom-cool" />
            {/* Warm halo (slower phase) */}
            <ellipse cx="36" cy="48" rx="20" ry="16" fill={`url(#${uid}-warm)`} className="phantom-bloom-warm" />
            {/* Base mask with gradient + drop-shadow */}
            <g filter={`url(#${uid}-drop)`}>
              <path d={maskPath} fill={`url(#${uid}-pearl)`} stroke="#a89878" strokeWidth="0.5" />
              {/* Porcelain noise overlay */}
              <path d={maskPath} filter={`url(#${uid}-porcelain)`} opacity="0.55" />
            </g>
            {/* Hairline gold inner trim */}
            <path d={maskPath} fill="none" stroke="#d8b878" strokeWidth="0.3" opacity="0.85" />
            {/* Eye socket — dark hollow with bright glint */}
            <g clipPath={`url(#${uid}-clip)`}>
              <ellipse cx="34" cy="46" rx="4.6" ry="3.4" fill="#1a1410" opacity="0.85" />
              <ellipse cx="33" cy="44.6" rx="1.2" ry="0.8" fill="#fff6e0" opacity="0.7" className="phantom-eye-glint" />
              {/* Hairline crack — paint detail */}
              <path d="M30 36 L 33 42 L 31 50" stroke="#5a4a30" strokeWidth="0.25" fill="none" opacity="0.45" />
            </g>
            {/* Drifting ghost mote rising from mask edge */}
            <circle cx="40" cy="62" r="0.7" fill="#f4ecdc" className="phantom-mote" />
          </>
        ),
        className: 'mask-phantom',
      });
    },
  },

  // 5. oni — Japanese demon mask, red with horns.
  //
  // Phase D pass-2 Tier 4 — 5-stop demon-crimson gradient on the face
  // plate, 4-stop bone gradient on horns, fractalNoise lacquered-wood
  // texture, double-stacked rage halos (crimson + flame), drop-shadow
  // filter, gold filigree brow accents, glowing eye slots that pulse,
  // ember-glow inside fangs. All palette-locked.
  {
    id: 'oni',
    label: 'Oni mask',
    render: ({ uid = 'on' }) => {
      const facePath = "M22 42 C 22 32, 32 30, 42 34 L 50 38 L 58 34 C 68 30, 78 32, 78 42 C 78 56, 66 62, 54 58 L 50 56 L 46 58 C 34 62, 22 56, 22 42 Z";
      return ({
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-face`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#ff5a4a" />
                <stop offset="22%" stopColor="#d83020" />
                <stop offset="55%" stopColor="#a82018" />
                <stop offset="85%" stopColor="#6a0e08" />
                <stop offset="100%" stopColor="#2a0404" />
              </linearGradient>
              <linearGradient id={`${uid}-horn`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#fff0c0" />
                <stop offset="35%" stopColor="#e8d090" />
                <stop offset="75%" stopColor="#a07840" />
                <stop offset="100%" stopColor="#3a2a10" />
              </linearGradient>
              <radialGradient id={`${uid}-rage`} cx="50%" cy="55%" r="55%">
                <stop offset="60%" stopColor="#ff4040" stopOpacity="0" />
                <stop offset="85%" stopColor="#ff4040" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#ff4040" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-flame`} cx="50%" cy="55%" r="50%">
                <stop offset="70%" stopColor="#ffaa3d" stopOpacity="0" />
                <stop offset="92%" stopColor="#ffaa3d" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#ffaa3d" stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-drop`} x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" />
                <feOffset dx="0" dy="1" />
                <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id={`${uid}-lacquer`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.15" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.3  0 0 0 0 0.05  0 0 0 0 0.02  0 0 0 0.3 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
            </defs>
            {/* Crimson rage halo (slow pulse) */}
            <ellipse cx="50" cy="46" rx="34" ry="22" fill={`url(#${uid}-rage)`} className="oni-rage" />
            {/* Flame halo (inner) */}
            <ellipse cx="50" cy="46" rx="30" ry="20" fill={`url(#${uid}-flame)`} className="oni-flame" />
            <g filter={`url(#${uid}-drop)`}>
              {/* horns — gradient bone */}
              <path d="M30 28 L 28 18 L 34 24 Z" fill={`url(#${uid}-horn)`} stroke="#5a3a18" strokeWidth="0.5" />
              <path d="M70 28 L 72 18 L 66 24 Z" fill={`url(#${uid}-horn)`} stroke="#5a3a18" strokeWidth="0.5" />
              {/* gold filigree wraps at horn bases */}
              <path d="M28 26 Q 31 24 34 26" stroke="#e8c870" strokeWidth="0.5" fill="none" />
              <path d="M66 26 Q 69 24 72 26" stroke="#e8c870" strokeWidth="0.5" fill="none" />
              {/* face plate with gradient + lacquer texture */}
              <path d={facePath} fill={`url(#${uid}-face)`} stroke="#4a0808" strokeWidth="0.7" />
              <path d={facePath} filter={`url(#${uid}-lacquer)`} opacity="0.45" />
              {/* gold brow streaks */}
              <path d="M30 40 L 38 38" stroke="#e8c870" strokeWidth="0.5" />
              <path d="M70 40 L 62 38" stroke="#e8c870" strokeWidth="0.5" />
              {/* glowing eye slots */}
              <g className="oni-eyes">
                <ellipse cx="38" cy="44" rx="3" ry="1.4" fill="#ffaa3d" />
                <ellipse cx="38" cy="44" rx="1.4" ry="0.7" fill="#fff8d8" />
                <ellipse cx="62" cy="44" rx="3" ry="1.4" fill="#ffaa3d" />
                <ellipse cx="62" cy="44" rx="1.4" ry="0.7" fill="#fff8d8" />
              </g>
              {/* fang highlights with inner ember glow */}
              <path d="M44 56 L 46 62 L 48 56 Z" fill="#f4ecdc" />
              <path d="M52 56 L 54 62 L 56 56 Z" fill="#f4ecdc" />
              <path d="M45.4 57.6 L 46 60 L 46.6 57.6 Z" fill="#ffaa3d" opacity="0.7" />
              <path d="M53.4 57.6 L 54 60 L 54.6 57.6 Z" fill="#ffaa3d" opacity="0.7" />
            </g>
            {/* Rising sparks from horn tips */}
            <circle cx="28" cy="16" r="0.6" fill="#ffe6a8" className="oni-spark oni-spark-A" />
            <circle cx="72" cy="16" r="0.6" fill="#ffe6a8" className="oni-spark oni-spark-B" />
            <circle cx="50" cy="30" r="0.5" fill="#ffe6a8" className="oni-spark oni-spark-C" />
          </>
        ),
        className: 'mask-oni',
      });
    },
  },

  // 6. sunburst — radiating sun mask, gold.
  //
  // Phase D pass-2 Tier 4 — 5-stop solar gradient on long rays + short
  // rays, fractalNoise solar-flare texture, double-stacked corona halos
  // (white-hot + bright gold), drop-shadow filter, layered face plate
  // (outer brass + inner gold + bright core), glowing eye slots with
  // ember cores, rising sun-spark flares on staggered phases.
  {
    id: 'sunburst',
    label: 'Sunburst mask',
    render: ({ uid = 'sb' }) => ({
      children: (
        <>
          <defs>
            <linearGradient id={`${uid}-long`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#fff8d8" />
              <stop offset="25%" stopColor="#ffe6a8" />
              <stop offset="55%" stopColor="#ffd770" />
              <stop offset="85%" stopColor="#c08838" />
              <stop offset="100%" stopColor="#7a4818" />
            </linearGradient>
            <linearGradient id={`${uid}-short`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor="#fff6e8" />
              <stop offset="40%" stopColor="#ffe6a0" />
              <stop offset="85%" stopColor="#d8a040" />
              <stop offset="100%" stopColor="#6a3a10" />
            </linearGradient>
            <radialGradient id={`${uid}-face`} cx="35%" cy="35%" r="70%">
              <stop offset="0%"  stopColor="#ffe6a8" />
              <stop offset="40%" stopColor="#ffaa3d" />
              <stop offset="80%" stopColor="#c8702a" />
              <stop offset="100%" stopColor="#5a2810" />
            </radialGradient>
            <radialGradient id={`${uid}-corona`} cx="50%" cy="50%" r="55%">
              <stop offset="50%" stopColor="#ffe6a8" stopOpacity="0" />
              <stop offset="78%" stopColor="#ffaa3d" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#ffaa3d" stopOpacity="0" />
            </radialGradient>
            <radialGradient id={`${uid}-coronaHot`} cx="50%" cy="50%" r="35%">
              <stop offset="40%" stopColor="#fff8d8" stopOpacity="0" />
              <stop offset="80%" stopColor="#fff8d8" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#fff8d8" stopOpacity="0" />
            </radialGradient>
            <filter id={`${uid}-drop`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="0.8" />
              <feOffset dx="0" dy="1" />
              <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
              <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id={`${uid}-flare`}>
              <feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="2" result="n" />
              <feColorMatrix in="n" type="matrix" values="0 0 0 0 1  0 0 0 0 0.7  0 0 0 0 0.15  0 0 0 0.25 0" />
              <feComposite in2="SourceGraphic" operator="in" />
            </filter>
          </defs>
          {/* Outer corona halo */}
          <circle cx="50" cy="50" r="40" fill={`url(#${uid}-corona)`} className="mask-sunburst-corona" />
          {/* Inner white-hot corona */}
          <circle cx="50" cy="50" r="22" fill={`url(#${uid}-coronaHot)`} className="mask-sunburst-corona-hot" />
          {/* Short rays — pulse counter to long rays */}
          <g className="mask-sunburst-rays-short" filter={`url(#${uid}-drop)`}>
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i * 360) / 12 + 15;
              return (
                <path
                  key={`rs-${i}`}
                  d="M50 30 L 49 22 L 51 22 Z"
                  fill={`url(#${uid}-short)`}
                  stroke="#7a4818"
                  strokeWidth="0.2"
                  transform={`rotate(${angle} 50 50)`}
                />
              );
            })}
          </g>
          {/* Long rays — rotate slowly + pulse */}
          <g className="mask-sunburst-rays" filter={`url(#${uid}-drop)`}>
            {Array.from({ length: 12 }).map((_, i) => {
              const angle = (i * 360) / 12;
              return (
                <g key={`ray-${i}`} transform={`rotate(${angle} 50 50)`}>
                  <path d="M50 22 L 48 10 L 52 10 Z" fill={`url(#${uid}-long)`} stroke="#7a4818" strokeWidth="0.25" />
                  {/* flare texture per ray */}
                  <path d="M50 22 L 48 10 L 52 10 Z" filter={`url(#${uid}-flare)`} opacity="0.55" />
                </g>
              );
            })}
          </g>
          {/* Inner pulsing glow ring */}
          <circle cx="50" cy="50" r="16" fill="none" stroke="#ffe6a8" strokeWidth="0.8" className="mask-sunburst-glow" />
          {/* Face plate — radial brass-gold + bright core */}
          <circle cx="50" cy="50" r="14" fill={`url(#${uid}-face)`} stroke="#a06020" strokeWidth="0.6" />
          <circle cx="50" cy="48" r="6" fill="#ffe6a8" opacity="0.4" />
          {/* Glowing eye slots — outer ring + ember core */}
          <g className="mask-sunburst-eyes">
            <circle cx="42" cy="48" r="2.2" fill="#5a2810" />
            <circle cx="42" cy="48" r="1.2" fill="#ffaa3d" />
            <circle cx="42" cy="47.6" r="0.5" fill="#fff8d8" />
            <circle cx="58" cy="48" r="2.2" fill="#5a2810" />
            <circle cx="58" cy="48" r="1.2" fill="#ffaa3d" />
            <circle cx="58" cy="47.6" r="0.5" fill="#fff8d8" />
          </g>
          {/* Rising sun-spark flares at 3 tick positions */}
          <circle cx="50" cy="6"  r="0.8" fill="#fff8d8" className="mask-sunburst-spark mask-sunburst-spark-A" />
          <circle cx="14" cy="50" r="0.8" fill="#fff8d8" className="mask-sunburst-spark mask-sunburst-spark-B" />
          <circle cx="86" cy="50" r="0.8" fill="#fff8d8" className="mask-sunburst-spark mask-sunburst-spark-C" />
        </>
      ),
      className: 'mask-sunburst',
    }),
  },

  // 7. void — black with deep starfield interior. Mythic rarity 5.
  //
  // Phase D pass-2 Tier 4 — 5-stop void-depth gradient (violet edge →
  // pure black core), fractalNoise void-static texture overlay, double-
  // stacked chromatic rim halos (violet + magenta), drop-shadow filter,
  // clipped magenta nebula wisp interior + drifting starfield, accretion
  // arc tick-marks at the rim, plus the existing twinkling stars.
  {
    id: 'void',
    label: 'Void mask',
    render: ({ uid = 'vm' }) => {
      const gradId = `${uid}-grad`;
      const clipId = `${uid}-clip`;
      const nebId = `${uid}-neb`;
      const maskPath = "M22 42 C 22 34, 32 32, 42 36 L 50 40 L 58 36 C 68 32, 78 34, 78 42 C 78 56, 66 60, 54 56 L 50 54 L 46 56 C 34 60, 22 56, 22 42 Z";
      return {
        children: (
          <>
            <defs>
              <radialGradient id={gradId} cx="50%" cy="50%" r="62%">
                <stop offset="0%"   stopColor="#3a1058" />
                <stop offset="22%"  stopColor="#1a0828" />
                <stop offset="55%"  stopColor="#08041a" />
                <stop offset="85%"  stopColor="#04020a" />
                <stop offset="100%" stopColor="#000000" />
              </radialGradient>
              <radialGradient id={nebId} cx="60%" cy="40%" r="55%">
                <stop offset="0%"   stopColor="#d050b8" stopOpacity="0.4" />
                <stop offset="60%"  stopColor="#5078ff" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#5078ff" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-violetHalo`} cx="50%" cy="50%" r="55%">
                <stop offset="65%" stopColor="#7a4ec8" stopOpacity="0" />
                <stop offset="88%" stopColor="#7a4ec8" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#7a4ec8" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-magentaHalo`} cx="50%" cy="50%" r="55%">
                <stop offset="78%" stopColor="#d050b8" stopOpacity="0" />
                <stop offset="94%" stopColor="#d050b8" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#d050b8" stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-drop`} x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.6" />
                <feOffset dx="0" dy="0.7" />
                <feComponentTransfer><feFuncA type="linear" slope="0.5" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id={`${uid}-static`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.7  0 0 0 0 0.6  0 0 0 0 1  0 0 0 0.18 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
              <clipPath id={clipId}>
                <path d={maskPath} />
              </clipPath>
            </defs>
            {/* Magenta outer halo */}
            <ellipse cx="50" cy="46" rx="32" ry="18" fill={`url(#${uid}-magentaHalo)`} className="mask-void-halo-outer" />
            {/* Violet inner halo */}
            <ellipse cx="50" cy="46" rx="30" ry="16" fill={`url(#${uid}-violetHalo)`} className="mask-void-halo-inner" />
            {/* Base mask body with gradient + drop-shadow */}
            <g filter={`url(#${uid}-drop)`}>
              <path d={maskPath} fill={`url(#${gradId})`} stroke="#0a0414" strokeWidth="0.6" />
            </g>
            <g clipPath={`url(#${clipId})`}>
              {/* Nebula wisp interior */}
              <ellipse cx="58" cy="42" rx="22" ry="14" fill={`url(#${nebId})`} className="mask-void-neb" />
              {/* void-static fractalNoise overlay */}
              <path d={maskPath} filter={`url(#${uid}-static)`} opacity="0.5" />
              {/* Drifting starfield */}
              <g className="mask-void-stars">
                <circle cx="28" cy="44" r="0.6" fill="#dcc8ff" className="vmstar vmstar-A" />
                <circle cx="36" cy="48" r="0.5" fill="#f4ecdc" className="vmstar vmstar-B" />
                <circle cx="44" cy="42" r="0.7" fill="#ffffff" className="vmstar vmstar-C" />
                <circle cx="52" cy="50" r="0.4" fill="#dcc8ff" className="vmstar vmstar-A" />
                <circle cx="58" cy="44" r="0.6" fill="#f4ecdc" className="vmstar vmstar-B" />
                <circle cx="64" cy="48" r="0.5" fill="#dcc8ff" className="vmstar vmstar-C" />
                <circle cx="72" cy="44" r="0.6" fill="#ffffff" className="vmstar vmstar-A" />
                <circle cx="32" cy="52" r="0.4" fill="#dcc8ff" className="vmstar vmstar-B" />
                <circle cx="48" cy="38" r="0.5" fill="#f4ecdc" className="vmstar vmstar-C" />
                <circle cx="68" cy="52" r="0.5" fill="#ffffff" className="vmstar vmstar-A" />
              </g>
              {/* Accretion arc tick-marks along the rim */}
              <g className="mask-void-accretion">
                <rect x="24" y="38" width="0.6" height="1.5" fill="#dcc8ff" opacity="0.7" />
                <rect x="34" y="34" width="0.6" height="1.5" fill="#dcc8ff" opacity="0.7" />
                <rect x="50" y="38" width="0.6" height="1.5" fill="#dcc8ff" opacity="0.7" />
                <rect x="66" y="34" width="0.6" height="1.5" fill="#dcc8ff" opacity="0.7" />
                <rect x="76" y="38" width="0.6" height="1.5" fill="#dcc8ff" opacity="0.7" />
              </g>
            </g>
            {/* chromatic rim glow */}
            <path d={maskPath} fill="none" stroke="#7a4ec8" strokeWidth="0.4" className="mask-void-rim" />
          </>
        ),
        className: 'mask-void',
      };
    },
  },

  // 8. crimson-veil — Crimson Solstice premium mythic rarity 5.
  //
  // Phase D pass-2 Tier 4 — 5-stop fire gradient base, fractalNoise coal
  // texture overlay, double-stacked solstice halos (crimson edge + flame
  // bloom), drop-shadow filter, gold filigree trim, glowing eye slots
  // with bright cores, plus existing ember field, sparks, and smoke
  // wisp. Palette-locked Crimson Solstice.
  {
    id: 'crimson-veil',
    label: 'Crimson Veil mask',
    render: ({ uid = 'cv' }) => {
      const gradId = `${uid}-grad`;
      const smokeId = `${uid}-smoke`;
      const maskPath = "M22 36 C 22 30, 32 28, 42 32 L 50 36 L 58 32 C 68 28, 78 30, 78 36 L 76 56 L 50 60 L 24 56 Z";
      return {
        children: (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#280404" />
                <stop offset="25%"  stopColor="#4a0a0a" />
                <stop offset="55%"  stopColor="#ff4040" />
                <stop offset="80%"  stopColor="#ff7820" />
                <stop offset="100%" stopColor="#ffaa3d" />
              </linearGradient>
              <radialGradient id={smokeId} cx="50%" cy="50%" r="50%">
                <stop offset="0%"   stopColor="#5a1a10" stopOpacity="0.6" />
                <stop offset="55%"  stopColor="#3a0a0a" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#3a0a0a" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-crimsonHalo`} cx="50%" cy="55%" r="55%">
                <stop offset="65%" stopColor="#ff4040" stopOpacity="0" />
                <stop offset="88%" stopColor="#ff4040" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#ff4040" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-flameBloom`} cx="50%" cy="55%" r="45%">
                <stop offset="40%" stopColor="#ffaa3d" stopOpacity="0" />
                <stop offset="80%" stopColor="#ffaa3d" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#ffaa3d" stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-drop`} x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.7" />
                <feOffset dx="0" dy="1" />
                <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id={`${uid}-coal`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.22" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.6  0 0 0 0 0.15  0 0 0 0 0.05  0 0 0 0.35 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
            </defs>
            {/* Crimson outer halo */}
            <ellipse cx="50" cy="46" rx="34" ry="20" fill={`url(#${uid}-crimsonHalo)`} className="mask-crimson-veil-halo-outer" />
            {/* Flame bloom inner */}
            <ellipse cx="50" cy="48" rx="28" ry="16" fill={`url(#${uid}-flameBloom)`} className="mask-crimson-veil-halo-inner" />
            {/* smoke wisp under mask */}
            <g className="mask-crimson-veil-smoke">
              <ellipse cx="50" cy="60" rx="30" ry="6" fill={`url(#${smokeId})`} />
            </g>
            {/* Base mask body — gradient + drop-shadow + coal texture */}
            <g filter={`url(#${uid}-drop)`}>
              <path d={maskPath} fill={`url(#${gradId})`} stroke="#280404" strokeWidth="0.7" />
              <path d={maskPath} filter={`url(#${uid}-coal)`} opacity="0.5" />
            </g>
            {/* Gold filigree inner trim */}
            <path d={maskPath} fill="none" stroke="#e8c870" strokeWidth="0.3" opacity="0.7" />
            {/* embers — staggered phases */}
            <g className="mask-crimson-veil-embers">
              <circle cx="30" cy="48" r="1.2" fill="#ffaa3d" className="cv-ember cv-ember-A" />
              <circle cx="40" cy="46" r="1.0" fill="#ffaa3d" className="cv-ember cv-ember-B" />
              <circle cx="50" cy="48" r="1.1" fill="#ffe6a8" className="cv-ember cv-ember-C" />
              <circle cx="60" cy="46" r="1.0" fill="#ffaa3d" className="cv-ember cv-ember-A" />
              <circle cx="70" cy="48" r="1.2" fill="#ffaa3d" className="cv-ember cv-ember-B" />
            </g>
            {/* glowing eye slots — outer dark + ember + bright core */}
            <g className="mask-crimson-veil-eyes">
              <ellipse cx="40" cy="42" rx="2.6" ry="1.4" fill="#280404" />
              <ellipse cx="40" cy="42" rx="2.0" ry="1.0" fill="#ffaa3d" />
              <ellipse cx="40" cy="42" rx="1.0" ry="0.55" fill="#ffe6a8" />
              <ellipse cx="60" cy="42" rx="2.6" ry="1.4" fill="#280404" />
              <ellipse cx="60" cy="42" rx="2.0" ry="1.0" fill="#ffaa3d" />
              <ellipse cx="60" cy="42" rx="1.0" ry="0.55" fill="#ffe6a8" />
            </g>
            {/* rising sparks from mask edges */}
            <g className="mask-crimson-veil-sparks">
              <circle cx="26" cy="34" r="0.6" fill="#ffe6a8" className="cv-spark cv-spark-A" />
              <circle cx="50" cy="30" r="0.5" fill="#ffe6a8" className="cv-spark cv-spark-B" />
              <circle cx="74" cy="34" r="0.6" fill="#ffe6a8" className="cv-spark cv-spark-C" />
            </g>
          </>
        ),
        className: 'mask-crimson-veil',
      };
    },
  },

  // 9. mardi-gras — purple/gold elaborate decoration. Static rarity 3.
  //
  // Phase C Tier 3 — layered upgrade: 3-stop purple mask gradient,
  // each feather plume becomes a fan of 3 feather strokes, gold
  // filigree curling pattern across the mask, 2 small gem ovals
  // (purple + green) inset with highlights, more filigree dots.
  {
    id: 'mardi-gras',
    label: 'Mardi Gras mask',
    render: ({ uid = 'mg' }) => {
      const maskPath = "M22 44 C 22 36, 30 34, 40 36 L 48 40 L 52 40 L 60 36 C 70 34, 78 36, 78 44 C 78 56, 66 60, 54 56 L 50 54 L 46 56 C 34 60, 22 56, 22 44 Z";
      return ({
        children: (
          <>
            <defs>
              <linearGradient id={`${uid}-mask`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#7a4ea8" />
                <stop offset="55%"  stopColor="#5a2e88" />
                <stop offset="100%" stopColor="#2a0e44" />
              </linearGradient>
            </defs>
            {/* Drop shadow */}
            <ellipse cx="50" cy="58" rx="28" ry="1.6" fill="#000" opacity="0.32" />
            {/* Left feather plume fan (3 strokes) */}
            <path d="M24 38 L 16 22 L 22 34 Z" fill="#a06ed0" stroke="#5a2e88" strokeWidth="0.4" />
            <path d="M22 38 L 12 26 L 20 34 Z" fill="#7a4ea8" stroke="#5a2e88" strokeWidth="0.4" opacity="0.92" />
            <path d="M26 38 L 22 18 L 26 32 Z" fill="#c8a0e0" stroke="#5a2e88" strokeWidth="0.4" opacity="0.85" />
            {/* Left plume vein */}
            <path d="M16 22 L 22 38" stroke="#3a1858" strokeWidth="0.3" opacity="0.7" />
            {/* Right feather plume fan */}
            <path d="M76 38 L 84 22 L 78 34 Z" fill="#a06ed0" stroke="#5a2e88" strokeWidth="0.4" />
            <path d="M78 38 L 88 26 L 80 34 Z" fill="#7a4ea8" stroke="#5a2e88" strokeWidth="0.4" opacity="0.92" />
            <path d="M74 38 L 78 18 L 74 32 Z" fill="#c8a0e0" stroke="#5a2e88" strokeWidth="0.4" opacity="0.85" />
            <path d="M84 22 L 78 38" stroke="#3a1858" strokeWidth="0.3" opacity="0.7" />
            {/* Mask body with 3-stop gradient + ink outline */}
            <path d={maskPath} fill={`url(#${uid}-mask)`} stroke="#2a0e44" strokeWidth="0.6" />
            {/* Upper-edge highlight */}
            <path d="M26 40 Q 38 36 50 40 Q 62 36 74 40" stroke="#c8a0e0" strokeWidth="0.4" fill="none" opacity="0.85" />
            {/* Gold filigree curl pattern across mask */}
            <path d="M30 46 Q 36 50 42 48" stroke="#f1d780" strokeWidth="0.5" fill="none" opacity="0.85" strokeLinecap="round" />
            <path d="M58 48 Q 64 50 70 46" stroke="#f1d780" strokeWidth="0.5" fill="none" opacity="0.85" strokeLinecap="round" />
            <path d="M40 52 Q 50 54 60 52" stroke="#f1d780" strokeWidth="0.4" fill="none" opacity="0.7" strokeLinecap="round" />
            {/* Gold filigree dots — more than the original */}
            <circle cx="28" cy="40" r="1.2" fill="#f1d780" stroke="#a08438" strokeWidth="0.3" />
            <circle cx="34" cy="38" r="1" fill="#f1d780" stroke="#a08438" strokeWidth="0.3" />
            <circle cx="66" cy="38" r="1" fill="#f1d780" stroke="#a08438" strokeWidth="0.3" />
            <circle cx="72" cy="40" r="1.2" fill="#f1d780" stroke="#a08438" strokeWidth="0.3" />
            <circle cx="44" cy="42" r="0.55" fill="#f1d780" opacity="0.85" />
            <circle cx="56" cy="42" r="0.55" fill="#f1d780" opacity="0.85" />
            {/* Filigree dot highlights */}
            <circle cx="27.6" cy="39.6" r="0.35" fill="#fff" opacity="0.85" />
            <circle cx="71.6" cy="39.6" r="0.35" fill="#fff" opacity="0.85" />
            {/* 2 gem ovals (purple + green) inset with highlights */}
            <ellipse cx="40" cy="46" rx="1.6" ry="1" fill="#3a1858" stroke="#1a0828" strokeWidth="0.3" />
            <ellipse cx="40" cy="45.8" rx="0.9" ry="0.6" fill="#a06ed0" />
            <circle cx="39.6" cy="45.6" r="0.3" fill="#fff" opacity="0.95" />
            <ellipse cx="60" cy="46" rx="1.6" ry="1" fill="#1a4828" stroke="#0a280f" strokeWidth="0.3" />
            <ellipse cx="60" cy="45.8" rx="0.9" ry="0.6" fill="#5a9858" />
            <circle cx="59.6" cy="45.6" r="0.3" fill="#fff" opacity="0.95" />
            {/* Center forehead gem */}
            <path d="M50 40 L 51.5 42 L 50 44 L 48.5 42 Z" fill="#f1d780" stroke="#5a4218" strokeWidth="0.3" />
            <path d="M50 41.2 L 50.7 42 L 50 42.8 L 49.3 42 Z" fill="#fff" opacity="0.7" />
          </>
        ),
      });
    },
  },

  // 10. mirror — silver reflective mask. Mythic rarity 5.
  //
  // Phase D pass-2 Tier 4 — 5-stop polished silver gradient, fractalNoise
  // brushed-metal texture, double-stacked chromatic halos (cool + warm),
  // drop-shadow filter, etched filigree pattern on mask surface, beveled
  // gold rim, two clipped sweeping glare bands (primary + secondary), and
  // tiny reflection sparkles on staggered phases.
  {
    id: 'mirror',
    label: 'Mirror mask',
    render: ({ uid = 'mr' }) => {
      const gradId = `${uid}-grad`;
      const sweepId = `${uid}-sweep`;
      const sweepBId = `${uid}-sweepB`;
      const clipId = `${uid}-clip`;
      const maskPath = "M22 42 C 22 34, 32 32, 42 36 L 50 40 L 58 36 C 68 32, 78 34, 78 42 C 78 56, 66 60, 54 56 L 50 54 L 46 56 C 34 60, 22 56, 22 42 Z";
      return {
        children: (
          <>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"  stopColor="#ffffff" />
                <stop offset="25%" stopColor="#f4f4f8" />
                <stop offset="55%" stopColor="#a8a8b8" />
                <stop offset="80%" stopColor="#7a7a8c" />
                <stop offset="100%" stopColor="#dcdce4" />
              </linearGradient>
              {/* primary diagonal sweep highlight */}
              <linearGradient id={sweepId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"  stopColor="#ffffff" stopOpacity="0" />
                <stop offset="45%" stopColor="#ffffff" stopOpacity="0" />
                <stop offset="50%" stopColor="#ffffff" stopOpacity="0.9" />
                <stop offset="55%" stopColor="#ffffff" stopOpacity="0" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
              {/* secondary softer sweep */}
              <linearGradient id={sweepBId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"  stopColor="#a0d0ff" stopOpacity="0" />
                <stop offset="48%" stopColor="#a0d0ff" stopOpacity="0" />
                <stop offset="52%" stopColor="#a0d0ff" stopOpacity="0.5" />
                <stop offset="56%" stopColor="#a0d0ff" stopOpacity="0" />
                <stop offset="100%" stopColor="#a0d0ff" stopOpacity="0" />
              </linearGradient>
              <radialGradient id={`${uid}-coolHalo`} cx="50%" cy="50%" r="55%">
                <stop offset="65%" stopColor="#a0d0ff" stopOpacity="0" />
                <stop offset="88%" stopColor="#a0d0ff" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#a0d0ff" stopOpacity="0" />
              </radialGradient>
              <radialGradient id={`${uid}-warmHalo`} cx="50%" cy="50%" r="55%">
                <stop offset="78%" stopColor="#ffe6a8" stopOpacity="0" />
                <stop offset="94%" stopColor="#ffe6a8" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#ffe6a8" stopOpacity="0" />
              </radialGradient>
              <filter id={`${uid}-drop`} x="-15%" y="-15%" width="130%" height="130%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="0.7" />
                <feOffset dx="0" dy="1" />
                <feComponentTransfer><feFuncA type="linear" slope="0.55" /></feComponentTransfer>
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id={`${uid}-brushed`}>
                <feTurbulence type="fractalNoise" baseFrequency="0.6 0.04" numOctaves="2" result="n" />
                <feColorMatrix in="n" type="matrix" values="0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0 0.6  0 0 0 0.2 0" />
                <feComposite in2="SourceGraphic" operator="in" />
              </filter>
              <clipPath id={clipId}>
                <path d={maskPath} />
              </clipPath>
            </defs>
            {/* Cool outer halo */}
            <ellipse cx="50" cy="46" rx="32" ry="18" fill={`url(#${uid}-coolHalo)`} className="mask-mirror-halo-cool" />
            {/* Warm inner halo */}
            <ellipse cx="50" cy="46" rx="28" ry="16" fill={`url(#${uid}-warmHalo)`} className="mask-mirror-halo-warm" />
            {/* Base mirrored surface — gradient + drop-shadow + brushed texture */}
            <g filter={`url(#${uid}-drop)`}>
              <path d={maskPath} fill={`url(#${gradId})`} stroke="#5a5a68" strokeWidth="0.6" />
              <path d={maskPath} filter={`url(#${uid}-brushed)`} opacity="0.55" />
            </g>
            {/* Etched filigree on mask surface */}
            <g clipPath={`url(#${clipId})`}>
              <path d="M28 44 Q 38 46 50 44 Q 62 42 72 44" stroke="#7a7a8c" strokeWidth="0.25" fill="none" opacity="0.55" />
              <path d="M28 52 Q 38 50 50 52 Q 62 54 72 52" stroke="#7a7a8c" strokeWidth="0.25" fill="none" opacity="0.55" />
              <path d="M40 40 L 40 56" stroke="#7a7a8c" strokeWidth="0.18" opacity="0.4" />
              <path d="M60 40 L 60 56" stroke="#7a7a8c" strokeWidth="0.18" opacity="0.4" />
            </g>
            {/* Bevel gold rim */}
            <path d={maskPath} fill="none" stroke="#e8c870" strokeWidth="0.25" opacity="0.55" />
            {/* Primary sweeping glare */}
            <g clipPath={`url(#${clipId})`}>
              <g className="mask-mirror-sweep">
                <rect x="-30" y="20" width="40" height="60" fill={`url(#${sweepId})`} transform="rotate(-20 50 50)" />
              </g>
            </g>
            {/* Secondary cool sweep (offset phase) */}
            <g clipPath={`url(#${clipId})`}>
              <g className="mask-mirror-sweep-B">
                <rect x="-30" y="20" width="40" height="60" fill={`url(#${sweepBId})`} transform="rotate(-20 50 50)" />
              </g>
            </g>
            {/* Reflection sparkles */}
            <g className="mask-mirror-sparkles">
              <circle cx="36" cy="42" r="0.5" fill="#ffffff" className="mr-sparkle mr-sparkle-A" />
              <circle cx="60" cy="44" r="0.4" fill="#ffffff" className="mr-sparkle mr-sparkle-B" />
              <circle cx="50" cy="52" r="0.5" fill="#ffffff" className="mr-sparkle mr-sparkle-C" />
            </g>
          </>
        ),
        className: 'mask-mirror',
      };
    },
  },
];
